/**
 * DecomposeCodeView — Decompose Code Stage
 *
 * HITL service-definition workspace.
 * Tabs: Workbench (default) | Graph | Report | Automation Flow
 * Three-pane Workbench: Inventory Tree | Service Builder | Signals
 *
 * Object states: Proposed → Modified → Approved
 */

import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import {
  ArrowLeft, ArrowRight, ChevronRight, ChevronDown,
  Settings, HelpCircle, User, Home, Workflow, Plug,
  FileBarChart, BarChart2, LayoutGrid,
  FileText, Layers, GitBranch, Network,
  Search, Download, RefreshCw, CheckCircle2,
  AlertTriangle, AlertCircle, Circle,
  Box, Zap, Share2, Lock,
  MoveRight, Info, Loader2,
  Shuffle, SplitSquareHorizontal,
  FolderOpen, Code2, Package, Plus, FileCode,
  X, Sparkles, ClipboardList, Cpu, Trash2, Eye, Database, BookOpen,
} from 'lucide-react'
import { getAPIBaseUrl, isQA } from '../../environment'
import { InventoryAIModal, BulkInventoryAIModal } from '../guided/GuidedAnalyzeView'
import { computeProposalSets, proposalSetToManualServices, filterNewProposalServices } from '../../utils/proposalEngine'
import './DecomposeCodeView.css'
import '../runs/RunsView.css'
// Step 4 — view-switcher tab bar (Domains / Services / Graph / List).
// Step 5 — Real Domains View; Graph/List remain placeholders.
import DefineDecompositionTabs from './DefineDecompositionTabs'
import {
  DECOMPOSITION_VIEW_MODES,
  DEFAULT_DECOMPOSITION_VIEW_MODE,
} from './decompositionViewModes'
import DomainsBoardView from './DomainsBoardView'
import DomainsGraphView from './DomainsGraphView'
import DecompositionListView from './DecompositionListView'
// Step 10: Cmd+K command palette
import DecomposeCommandPalette from './DecomposeCommandPalette'
// Step 11: Pipeline Overview inset (Programs → Domains → Services)
import DecomposeFlowInset from './DecomposeFlowInset'
// Step 12: domain contract loading for per-domain service scoping
import {
  findDefineDomainsJobId,
  getDomainContract,
  getDomainProposals,
} from '../../services/domainsService.js'
import { ReactFlow, useNodesState, useEdgesState, Background, Controls, Handle, Position, MarkerType } from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import dagre from '@dagrejs/dagre'

// ── (mock data removed — all data comes from real backends now) ────────────────

// NOTE: MOCK_SERVICES, MOCK_COMMON, MOCK_DISCONNECTED, MOCK_GRAPH_NODES,
// MOCK_GRAPH_EDGES were removed 2026-04-05.  All runtime data comes from
// ingest/CA/MI/DM artifacts or manual HITL creation.

// Kept for reference only — original mock transaction service shape:
// { id, name, domain, classes, loc, cohesion, couplingOut, businessHint,
//   status, cobolOrigins, members: [{name, cobol, loc, extDeps}],
//   whySignals: {dataShared, internalDensity, circular, aiConfidence}, deps }

// ── Pipeline nodes ─────────────────────────────────────────────────────────────

const PIPELINE_NODES = [
  { id: 'refactor',  label: 'Code Refactor',       contrib: 'LCOM4-split Java classes',     done: true,  active: false },
  { id: 'monolith',  label: 'Monolith Identifier', contrib: 'Service grouping proposals',   done: true,  active: false },
  { id: 'depmap',    label: 'Dependency Mapper',   contrib: 'Coupling metrics + call graph', done: true, active: false },
  { id: 'decompose', label: 'Define Services',      contrib: 'Approved service boundaries',  done: false, active: true  },
  { id: 'svcgen',    label: 'Service Generation',  contrib: 'Spring Boot microservices',    done: false, active: false },
]

// ── Helpers ───────────────────────────────────────────────────────────────────

const COHESION_LABEL = { high: 'High', medium: 'Med', low: 'Low' }
const COHESION_SCORE = { high: 82, medium: 58, low: 31 }

// Display labels for service status — keeps CSS class names stable
const STATUS_LABEL = { proposed: 'Draft', modified: 'Modified', approved: 'Confirmed' }
const statusLabel  = (s) => STATUS_LABEL[s] || (s ? s.charAt(0).toUpperCase() + s.slice(1) : '')

// Per-service confirm button label — respects Draft / Modified / Confirmed lifecycle
// Draft     → "Confirm Target Service"
// Modified  → "Re-confirm Target Service"  (was confirmed, then edited)
// Confirmed → "Service Confirmed ✓"        (locked, button disabled)
const svcConfirmLabel = (effStatus) =>
  effStatus === 'approved'  ? 'Service Confirmed ✓'
  : effStatus === 'modified' ? 'Re-confirm Service'
  :                            'Confirm Service'

// ── Decomposition Readiness Model ─────────────────────────────────────────────
//
// Pure function — takes current decomposition state, returns structured readiness.
//
// BLOCKING (prevent finalization):
//   • No Target Services defined
//   • No Confirmed Target Services (at least one required)
//   • Same COBOL program assigned to multiple services (exclusive ownership)
//
// WARNINGS (do not block, but surface to user):
//   • Unassigned COBOL programs
//   • Unassigned JCL jobs
//   • Target Services still in Draft state
//   • Target Services in Modified state (edited since last confirmation)
//   • God objects / MI-disconnected items still unresolved
//
// ALLOWED by design (no warning needed):
//   • Copybooks unassigned (shared structures, not service-owned)
//   • Data assets (VSAM/SQL) unassigned (shared by nature)
//   • All services still Draft (blocks via "no confirmed" rule)
//
function computeReadiness(services, serviceStatuses, sourceInventory, disconnected, discResolutions, hasMiProposals = false) {
  const blockingReasons = []
  const warningReasons  = []

  const effStatus = (svc) => serviceStatuses[svc.id] || svc.status
  const confirmedCount = services.filter(s => effStatus(s) === 'approved').length
  const draftCount     = services.filter(s => effStatus(s) === 'proposed').length
  const modifiedCount  = services.filter(s => effStatus(s) === 'modified').length

  // ── Blocking: no services at all ──────────────────────────────────────────
  if (services.length === 0) {
    blockingReasons.push('No services added yet — add at least one')
  }

  // ── Blocking: no confirmed services ───────────────────────────────────────
  if (services.length > 0 && confirmedCount === 0) {
    blockingReasons.push('Confirm at least one service before locking the plan')
  }

  // ── Shared-program detection ──────────────────────────────────────────────
  // A COBOL program whose paragraphs span multiple domains is intentional —
  // it will be generated once as a shared class with facade services per domain.
  // These are NOT conflicts; only true duplicates (unintentional) should block.
  const cobolAssignments = {}
  services.forEach(svc => {
    (svc.cobolOrigins || []).forEach(prog => {
      if (!cobolAssignments[prog]) cobolAssignments[prog] = []
      cobolAssignments[prog].push(svc.name)
    })
  })
  const _sharedProgSet = new Set(
    Object.entries(cobolAssignments)
      .filter(([, svcs]) => svcs.length > 1)
      .map(([prog]) => prog)
  )
  // Only block on true conflicts — programs that are shared are intentional
  const duplicateCobols = Object.entries(cobolAssignments)
    .filter(([prog, svcs]) => svcs.length > 1 && !_sharedProgSet.has(prog))
    .map(([prog]) => prog)
  if (duplicateCobols.length > 0) {
    const shown = duplicateCobols.slice(0, 3).join(', ') + (duplicateCobols.length > 3 ? '…' : '')
    blockingReasons.push(
      `${duplicateCobols.length} COBOL program${duplicateCobols.length > 1 ? 's' : ''} assigned to multiple services (${shown})`
    )
  }
  // Advisory: shared programs are intentional — info only, not a blocker
  if (_sharedProgSet.size > 0) {
    const shownShared = [..._sharedProgSet].slice(0, 3).join(', ') + (_sharedProgSet.size > 3 ? '…' : '')
    warningReasons.push(
      `${_sharedProgSet.size} program${_sharedProgSet.size > 1 ? 's' : ''} span multiple domains (${shownShared}) — will be generated as shared class${_sharedProgSet.size > 1 ? 'es' : ''} with facade services`
    )
  }

  // ── Blocking: confirmed service with no COBOL programs assigned ──────────
  // A confirmed service with empty cobolOrigins produces zero (program, service)
  // pairs in the service-generation fan-out — it is silently skipped and never
  // materialised in the generated output. Block finalization so counts stay honest.
  const confirmedEmpty = services.filter(s => effStatus(s) === 'approved' && !(s.cobolOrigins?.length))
  if (confirmedEmpty.length > 0) {
    const shown = confirmedEmpty.slice(0, 2).map(s => `"${s.name}"`).join(', ') + (confirmedEmpty.length > 2 ? '…' : '')
    blockingReasons.push(
      `${confirmedEmpty.length} confirmed service${confirmedEmpty.length > 1 ? 's' : ''} ${confirmedEmpty.length > 1 ? 'have' : 'has'} no programs assigned — add programs or remove before locking (${shown})`
    )
  }

  // ── Warning: Draft services ────────────────────────────────────────────────
  if (draftCount > 0) {
    warningReasons.push(
      `${draftCount} service${draftCount > 1 ? 's' : ''} not yet confirmed`
    )
  }

  // ── Warning: Modified services ─────────────────────────────────────────────
  if (modifiedCount > 0) {
    warningReasons.push(
      `${modifiedCount} service${modifiedCount > 1 ? 's' : ''} edited since last confirmation`
    )
  }

  // ── Warning: unassigned COBOL programs ────────────────────────────────────
  const assignedCobols = new Set(services.flatMap(s => s.cobolOrigins || []))
  const unassignedCobols = (sourceInventory?.programs || []).filter(p => !assignedCobols.has(p.name))
  if (unassignedCobols.length > 0) {
    const n = unassignedCobols.length
    // When MI ran, unassigned programs are often boundary/batch/support programs that
    // genuinely belong outside any service — frame as informational, not alarming.
    warningReasons.push(
      hasMiProposals
        ? `${n} source program${n > 1 ? 's' : ''} not included in any service — likely support or batch; OK to leave out`
        : `${n} source program${n > 1 ? 's' : ''} not assigned to any service — drag from the tree to assign`
    )
  }

  // ── Warning: unassigned JCL jobs ──────────────────────────────────────────
  const assignedMembers = new Set(services.flatMap(s => (s.members || []).map(m => m.name)))
  const unassignedJcl = (sourceInventory?.jcl || []).filter(j => !assignedMembers.has(j.name))
  if (unassignedJcl.length > 0) {
    warningReasons.push(
      `${unassignedJcl.length} JCL job${unassignedJcl.length > 1 ? 's' : ''} unassigned (optional)`
    )
  }

  // ── Warning: unresolved MI god objects / disconnected items ───────────────
  const unresolvedCount = (disconnected || []).filter(d => !(discResolutions || {})[d.id]).length
  if (unresolvedCount > 0) {
    warningReasons.push(
      `${unresolvedCount} unassigned item${unresolvedCount > 1 ? 's' : ''} from AI analysis`
    )
  }

  return {
    isReadyToFinalize: blockingReasons.length === 0,
    blockingReasons,
    warningReasons,
    confirmedCount,
    draftCount,
    modifiedCount,
    unassignedCobolCount: unassignedCobols.length,
    unassignedJclCount: unassignedJcl.length,
    duplicateCobols,
  }
}

// ── Real-data helpers ─────────────────────────────────────────────────────────

// Map MI modularity classification (HIGH/MEDIUM/LOW) → badge level
function computeServiceCohesion(programs, modMetrics) {
  if (!modMetrics?.by_program || !programs?.length) return 'medium'
  const lookup = {}
  modMetrics.by_program.forEach(p => {
    lookup[p.program] = p
    // also try without .java suffix
    lookup[p.program.replace(/\.java$/i, '')] = p
  })
  const classes = programs.map(prog => {
    const key = prog.replace(/\.java$/i, '')
    return lookup[prog] || lookup[key] || null
  }).filter(Boolean)
  if (!classes.length) return 'medium'
  const scores = classes.map(c => (c.classification || 'MEDIUM').toUpperCase())
  if (scores.some(s => s === 'LOW'))  return 'low'
  if (scores.every(s => s === 'HIGH')) return 'high'
  return 'medium'
}

// Sum fan_out across all programs in a service from DM coupling_metrics
function computeServiceCouplingOut(programs, couplingMetrics) {
  if (!couplingMetrics?.classes || !programs?.length) return 0
  return programs.reduce((sum, prog) => {
    const key  = prog.replace(/\.java$/i, '')
    const data = couplingMetrics.classes[prog] || couplingMetrics.classes[key] || {}
    return sum + (data.fan_out || 0)
  }, 0)
}

// Get LOC for a program from modularity_metrics.by_program
function getProgramLoc(prog, modMetrics) {
  if (!modMetrics?.by_program) return 0
  const key = prog.replace(/\.java$/i, '')
  const entry = modMetrics.by_program.find(p => p.program === prog || p.program === key)
  return entry?.loc || 0
}

// Build real service objects from MI decomposition_strategy.json + metrics.
//
// crClassMapping: the `class_mapping` object from transformed_java_catalog.json.
//   Keys are COBOL-derived source class names (e.g. "CUSTUPD").
//   Values are arrays of OOP class filenames (e.g. ["CustupdService.java"]).
//   Used to reverse-map Java class names in MI's programs[] back to their
//   canonical COBOL source names so cobolOrigins aligns with the ingest catalog.
function buildServicesFromMI(decomp, modMetrics, couplingMetrics, crClassMapping = {}, knownCobolPrograms = []) {
  // Reverse map: OOP Java class name → COBOL-derived source key
  // e.g. "CustupdService" → "CUSTUPD"
  const javaToCobol = {}
  Object.entries(crClassMapping).forEach(([cobolKey, javaFiles]) => {
    ;(Array.isArray(javaFiles) ? javaFiles : []).forEach(jf => {
      const cls = String(jf).replace(/\.java$/i, '')
      javaToCobol[cls] = cobolKey
    })
  })

  // Build a set of known COBOL program names (uppercase) for validation.
  // When the ingest catalog is available this lets us reject invented service
  // labels (e.g. "FindService") that MI sometimes places in programs[] when
  // it ran against CR-produced Java files instead of raw COBOL source.
  const cobolNameSet = new Set(knownCobolPrograms.map(p => (p?.name || p || '').toUpperCase()).filter(Boolean))

  return (decomp?.recommended_services || []).map((svc, i) => {
    const programs   = svc.programs || []
    const cohesion   = computeServiceCohesion(programs, modMetrics)
    const couplingOut = computeServiceCouplingOut(programs, couplingMetrics)
    const sharedItems = svc.shared_data || []
    const members = programs.map(prog => {
      const fanOut = (() => {
        if (!couplingMetrics?.classes) return 0
        const k = prog.replace(/\.java$/i, '')
        return (couplingMetrics.classes[prog] || couplingMetrics.classes[k] || {}).fan_out || 0
      })()
      return {
        name: prog,
        cobol: javaToCobol[prog] || javaToCobol[prog.replace(/\.java$/i, '')] || prog,
        loc: getProgramLoc(prog, modMetrics),
        extDeps: fanOut,
      }
    })

    // Derive canonical COBOL source names for duplicate-assignment checks and
    // inventory alignment.  MI's programs[] contains Java class names when
    // source_type="java"; map each back through the CR catalog reverse map so
    // cobolOrigins matches what the ingest catalog holds (e.g. "CUSTUPD" not
    // "CustupdService").  Falls back to the original value when no mapping exists.
    //
    // Validation pass: if the ingest catalog provided known COBOL program names
    // (knownCobolPrograms), only keep entries that resolve to a real COBOL name.
    // This prevents invented service labels like "FindService" — produced when MI
    // analysed CR Java output files — from propagating as program_name into SvcGen
    // and generating hollow Java shells.
    const cobolOrigins = [...new Set(programs.map(prog => {
      const key = prog.replace(/\.java$/i, '')
      return javaToCobol[key] || javaToCobol[prog] || prog
    }).filter(resolved => {
      // If we have a known inventory, reject names that are not real COBOL programs.
      if (cobolNameSet.size === 0) return true   // no inventory → accept all (safe fallback)
      return cobolNameSet.has(resolved.toUpperCase())
    }))]

    return {
      id: `svc-real-${i}`,
      name: svc.service_name,
      domain: svc.business_capability,
      classes: programs.length,
      loc: svc.total_loc || 0,
      cohesion,
      couplingOut,
      businessHint: svc.business_capability,
      status: 'proposed',
      cobolOrigins,
      members,
      whySignals: {
        dataShared: sharedItems.length
          ? `Shared structures: ${sharedItems.join(', ')}`
          : 'No shared data structures detected',
        internalDensity: `Extraction complexity: ${svc.extraction_complexity || 'unknown'}`,
        circular: svc.god_object_risk ? 'Contains God Object patterns' : '0 circular dependencies detected',
        aiConfidence: 0.8,
      },
      deps: svc.dependencies || [],
    }
  })
}

// Build disconnected bucket from MI god_object_decomposition
function buildDisconnectedFromMI(decomp) {
  return (decomp?.god_object_decomposition || []).map((plan, i) => ({
    id: `disc-real-${i}`,
    name: plan.program,
    loc: 0,
    cobol: plan.program,
    tag: plan.program.substring(0, 8).toUpperCase(),
    reason: `God Object — ${(plan.current_capabilities || []).length} capabilities detected, must be decomposed first`,
  }))
}

// ── Domain → Service candidate conversion ─────────────────────────────────────
//
// Each saved domain becomes a draft service candidate in the Services tab.
// The shape is compatible with the manual-service shape so all existing card
// renderers, readiness checks and approve handlers work without modification.
// isDomainDerived=true lets the render layer show a distinct "Domain" badge.
function buildServiceFromDomain(domain) {
  // Collect unique real program names (suppress UNKNOWN sentinels)
  const uniquePrograms = [...new Set(
    (domain.members || [])
      .map(m => m.source_program)
      .filter(p => p && p !== 'UNKNOWN')
  )]

  // members = COBOL paragraph-level assignments (name = paragraph name, cobol = parent program).
  // This is critical: the facade executor reads members[].name as paragraph names to decide
  // which methods each facade service exposes. Using program names here (old behaviour)
  // caused domain_paragraphs=['CUSTUPD'] → no procedure model paragraph matched → empty facades.
  const members = (domain.members || []).map(m => ({
    name: m.name,                        // COBOL paragraph: FIND-CUSTOMER, MAIN, …
    cobol: m.source_program || m.name,   // parent program: CUSTUPD
    loc: m.loc || 0,
    extDeps: 0,
  }))

  const memberCount = domain.members?.length || 0
  const sourceLabel = domain.source === 'user' ? 'User-defined domain' : 'AI-proposed domain'

  return {
    id: `svc-domain-${domain.domain_id}`,
    name: domain.name,
    domain: domain.name,
    classes: memberCount,
    loc: 0,
    cohesion: 'medium',
    couplingOut: 0,
    businessHint: domain.description || domain.name,
    status: 'proposed',
    cobolOrigins: uniquePrograms,
    members,
    isDomainDerived: true,
    isManual: false,
    domainId: domain.domain_id,
    pinned: !!domain.user_pinned,
    whySignals: {
      dataShared: `Domain: ${domain.name}`,
      internalDensity: `${memberCount} member${memberCount !== 1 ? 's' : ''} · ${sourceLabel}`,
      circular: 'N/A',
      aiConfidence: typeof domain.confidence === 'number' ? domain.confidence : null,
    },
    deps: [],
  }
}

// ── Graph-powered proposals → Service candidate conversion ────────────────────
//
// Converts proposals from the Define Services graph clustering endpoint
// into the standard service card shape used by all existing renderers,
// readiness checks, and approve handlers.
function buildServicesFromGraphProposals(proposals) {
  return (proposals || []).map((p, i) => {
    const members = (p.paragraphs || []).map(para => ({
      name: para,
      cobol: (p.programs || [])[0] || para,
      loc: 0,
      extDeps: 0,
    }))

    // Map coupling_strength (0-N domain fields) → cohesion label
    const cs = p.coupling_strength || 0
    const cohesion = cs >= 3 ? 'high' : cs >= 2 ? 'medium' : 'low'

    // Pipeline enrichment — method/entity/repo counts from service_generation
    const pt = p.pipeline_totals || {}
    const pipelineSummary = pt.method_count
      ? `${pt.method_count} methods · ${pt.entity_count} entities · ${pt.repository_count} repos`
      : null

    return {
      id: `svc-graph-${p.cluster_id}`,
      name: p.domain_name || `ServiceGroup${p.cluster_id}`,
      domain: p.domain_name || 'Unclassified',
      classes: (p.paragraphs || []).length,
      loc: 0,
      cohesion,
      couplingOut: 0,
      businessHint: p.rationale || p.domain_name || '',
      status: 'proposed',
      cobolOrigins: p.programs || [],
      members,
      isManual: false,
      isGraphProposal: true,
      clusterId: p.cluster_id,
      pipelineTotals: pt,
      pipelinePrograms: p.pipeline || {},
      whySignals: {
        dataShared: (p.shared_fields || []).length
          ? `Shared fields: ${p.shared_fields.join(', ')}`
          : 'No shared data fields',
        internalDensity: pipelineSummary
          ? `${p.paragraph_count || 0} paragraphs · ${pipelineSummary}`
          : `${p.paragraph_count || 0} paragraphs · coupling ${cs}`,
        circular: (p.cross_cutting_concerns || []).length
          ? p.cross_cutting_concerns.join(', ')
          : 'No cross-cutting concerns',
        aiConfidence: 0.85,
      },
      datasets: p.datasets || [],
      deps: [],
    }
  })
}

// ── Boundary Intelligence — Phase 1 deterministic scoring ─────────────────────
//
// Computes four boundary-level signals from already-available Analyze data.
// Pure frontend. No ML. No backend changes. Fail-safe (returns null fields if
// any input is missing). Called from renderSignals() when a service is selected.
//
// Inputs:
//   svc               — the selected service object {cobolOrigins[], members[]}
//   intelligenceMetrics — CA intelligence_metrics.json (programs keyed by COBOL stem)
//   depGraphData      — DM dependency_graph.json ({edges: [{from, to, type}]})
//
// All four signals are display-only. None feed the contract or downstream pipeline.
//
function computeBoundaryScore(svc, intelligenceMetrics, depGraphData) {
  const cobolOrigins = svc?.cobolOrigins || []
  const members      = svc?.members      || []
  const programs     = intelligenceMetrics?.programs || null

  // ── 1. Boundary Cohesion Score ─────────────────────────────────────────────
  // Source: CA intelligence_metrics.programs[cobolOrigin].lcom4
  // Formula: mean(1 / lcom4) across matched programs × 100  → 0–100
  //   lcom4=1 (cohesive) → 1.0  |  lcom4=5 (5 independent groups) → 0.2
  // Higher = more cohesive. Null if lcom4 not available for any member.
  let cohesionScore  = null
  let cohesionMatched = 0
  if (programs && cobolOrigins.length > 0) {
    const vals = []
    for (const stem of cobolOrigins) {
      const p = programs[stem]
      if (p?.lcom4 != null) { vals.push(1.0 / Math.max(1, p.lcom4)); cohesionMatched++ }
    }
    if (vals.length > 0)
      cohesionScore = Math.round((vals.reduce((a, b) => a + b, 0) / vals.length) * 100)
  }

  // ── 2. Boundary Instability Score ─────────────────────────────────────────
  // Source: CA intelligence_metrics.programs[cobolOrigin].instability + betweenness_centrality
  // Formula: weighted mean(instability, weight = 1 + BC×10) across matched programs × 100
  //   Betweenness centrality amplifies the weight of structurally important programs.
  //   Result: 0 = highly stable, 100 = highly unstable.
  let instabilityScore  = null
  let instabilityMatched = 0
  if (programs && cobolOrigins.length > 0) {
    let wSum = 0, wTot = 0
    for (const stem of cobolOrigins) {
      const p = programs[stem]
      if (p == null || p.instability == null) continue
      const w = 1 + (p.betweenness_centrality || 0) * 10
      wSum += p.instability * w
      wTot += w
      instabilityMatched++
    }
    if (wTot > 0) instabilityScore = Math.round((wSum / wTot) * 100)
  }

  // ── 3. Boundary Coupling Leak ──────────────────────────────────────────────
  // Source: DM dependency_graph.json edges [{from, to, type: "DEPENDS_ON"}]
  //   Nodes and edges are Java class names (post-CR OOP split).
  //   Boundary membership = svc.members[].name (Java class names).
  // Formula:
  //   internal = edges where from ∈ boundary AND to ∈ boundary
  //   external = edges where from ∈ boundary AND to ∉ boundary
  //   leak% = round(external / (internal + external) × 100)
  // Scope: class-level DEPENDS_ON edges only. Copybook/data edges not included.
  let couplingLeakPct = null
  let internalEdges   = 0
  let externalEdges   = 0
  if (depGraphData?.edges && members.length > 0) {
    const memberSet = new Set(members.map(m => m.name).filter(Boolean))
    for (const edge of depGraphData.edges) {
      const src = edge.from
      const tgt = edge.to
      if (!src || !tgt || !memberSet.has(src)) continue
      if (memberSet.has(tgt)) internalEdges++
      else                    externalEdges++
    }
    const total = internalEdges + externalEdges
    couplingLeakPct = total > 0 ? Math.round((externalEdges / total) * 100) : 0
  }

  // ── 4. Split Candidate Count ──────────────────────────────────────────────
  // Source: CA intelligence_metrics.programs[cobolOrigin].split_signal
  // Counts member programs already flagged by Analyze as split candidate or god program risk.
  // "Cohesive" and "Cohesive core…" are OK — anything else is a split signal.
  let splitCount = 0
  const splitNames = []
  if (programs && cobolOrigins.length > 0) {
    for (const stem of cobolOrigins) {
      const p = programs[stem]
      if (!p?.split_signal) continue
      if (!p.split_signal.startsWith('Cohesive')) { splitCount++; splitNames.push(stem) }
    }
  }

  // ── 5. Overall boundary status (transparent, non-ML) ──────────────────────
  // Risky:  instability > 70  OR  couplingLeak > 50  OR  splitCount ≥ 2
  // Review: instability > 45  OR  couplingLeak > 25  OR  splitCount ≥ 1
  // Strong: otherwise
  // Null when there is not enough data to make any determination.
  const hasData = cohesionScore !== null || instabilityScore !== null || couplingLeakPct !== null
  let overallStatus = null
  if (hasData) {
    const isRisky =
      (instabilityScore !== null && instabilityScore > 70) ||
      (couplingLeakPct  !== null && couplingLeakPct  > 50) ||
      splitCount >= 2
    const isReview =
      (instabilityScore !== null && instabilityScore > 45) ||
      (couplingLeakPct  !== null && couplingLeakPct  > 25) ||
      splitCount >= 1
    overallStatus = isRisky ? 'risky' : isReview ? 'review' : 'strong'
  }

  return {
    cohesionScore,    cohesionMatched,
    instabilityScore, instabilityMatched,
    couplingLeakPct,  internalEdges, externalEdges,
    splitCount,       splitNames,
    overallStatus,
    hasData,
    totalOrigins: cobolOrigins.length,
    totalMembers: members.length,
  }
}

// ── Service rationale generator ───────────────────────────────────────────────
//
// Turns MI-derived service fields into human-readable decision-support sentences.
// Returns an array of plain text strings — empty means "nothing useful to say".
//
function generateServiceRationale(svc) {
  const lines = []
  const memberCount = svc.members?.length || svc.classes || 0
  const cobolCount  = svc.cobolOrigins?.length || 0

  // Primary scope sentence
  if (svc.businessHint) {
    const cobolClause = cobolCount > 0 && cobolCount !== memberCount
      ? ` from ${cobolCount} COBOL source program${cobolCount !== 1 ? 's' : ''}`
      : ''
    lines.push(
      `Grouped around ${svc.businessHint} — ${memberCount} generated Java class${memberCount !== 1 ? 'es' : ''}${cobolClause}.`
    )
  } else if (memberCount > 0) {
    lines.push(`Contains ${memberCount} generated Java class${memberCount !== 1 ? 'es' : ''} identified as a natural service boundary.`)
  }

  // Cohesion — only surface non-default states; medium is expected and adds no signal
  if (svc.cohesion === 'high') {
    lines.push('Strong internal cohesion — these classes are closely related and form a natural boundary.')
  } else if (svc.cohesion === 'low') {
    lines.push('Low cohesion detected — classes in this group have weaker relationships. Review before finalizing.')
  }

  // Extraction complexity
  const density = (svc.whySignals?.internalDensity || '').toLowerCase()
  if (density.includes('low'))  lines.push('Extraction complexity is low — this service should be straightforward to generate.')
  else if (density.includes('high')) lines.push('High extraction complexity — review dependencies before finalizing this boundary.')

  // God object risk
  if (svc.whySignals?.circular?.includes('God Object')) {
    lines.push('Contains God Object patterns — this boundary may benefit from further decomposition.')
  }

  // Shared structures
  const dataShared = svc.whySignals?.dataShared || ''
  if (dataShared.startsWith('Shared structures:')) {
    lines.push(`Shares data structures with other components: ${dataShared.replace('Shared structures: ', '')}.`)
  }

  return lines
}

// ── ReactFlow custom node for the dependency graph ─────────────────────────
const DEP_TYPE_COLORS = {
  COBOL_PROGRAM: '#60a5fa', COPYBOOK: '#a78bfa',
  DB2_TABLE: '#f59e0b', DB2_DCLGEN: '#f59e0b',
  VSAM_DATASET: '#34d399', DATA_FILE: '#34d399', LOGICAL_FILE: '#34d399',
  JCL_JOB: '#8b92a5', BMS_MAP: '#8b92a5',
}
const DEP_TYPE_LABELS = {
  COBOL_PROGRAM: 'Program', COPYBOOK: 'Copybook',
  DB2_TABLE: 'DB2', DB2_DCLGEN: 'DB2',
  VSAM_DATASET: 'VSAM', DATA_FILE: 'File', LOGICAL_FILE: 'File',
  JCL_JOB: 'JCL', BMS_MAP: 'BMS',
}
const DEP_EDGE_COLORS = {
  CALLS: '#60a5fa', COPIES: '#a78bfa', READS: '#34d399', WRITES: '#f59e0b',
  FILE_IO: '#34d399', SQL_SELECT: '#f59e0b', SQL_INSERT: '#f59e0b',
  SQL_UPDATE: '#f59e0b', SQL_DELETE: '#f59e0b', SQL_ACCESS: '#f59e0b',
  DD_REFERENCES: '#8b92a5', EXECUTES: '#60a5fa', USES_PROC: '#8b92a5',
}

function DepGraphRFNode({ data }) {
  const color = DEP_TYPE_COLORS[data.assetType] || '#8b92a5'
  const typeLabel = DEP_TYPE_LABELS[data.assetType] || data.assetType
  return (
    <div className={[
      'dc-rf-node',
      data.isSelected  ? 'dc-rf-selected'  : '',
      data.highlighted ? 'dc-rf-connected' : '',
      data.assignedTo  ? 'dc-rf-assigned'  : '',
    ].filter(Boolean).join(' ')}>
      <Handle type="target" position={Position.Left}  style={{ opacity: 0 }} />
      <span className="dc-rf-stripe" style={{ background: color }} />
      <div className="dc-rf-body">
        <span className="dc-rf-name">{data.label}</span>
        <span className="dc-rf-type" style={{ color }}>{typeLabel}</span>
      </div>
      {data.assignedTo && <span className="dc-rf-dot-assigned" title={`In: ${data.assignedTo}`} />}
      <Handle type="source" position={Position.Right} style={{ opacity: 0 }} />
    </div>
  )
}
const DEP_NODE_TYPES = { depAsset: DepGraphRFNode }

// ── ReactFlow custom node for the Neo4j knowledge graph ───────────────────
const KG_TYPE_COLORS = {
  Program: '#60a5fa', Paragraph: '#34d399', Copybook: '#a78bfa',
  Dataset: '#f59e0b', JclJob: '#8b92a5',
}
const KG_TYPE_LABELS = {
  Program: 'Program', Paragraph: 'Paragraph', Copybook: 'Copybook',
  Dataset: 'Dataset', JclJob: 'JCL Job',
}
const KG_EDGE_COLORS = {
  CALLS: '#60a5fa', COPIES: '#a78bfa', PERFORMS: '#34d399',
  USES_FIELD: '#f59e0b', READS: '#34d399', WRITES: '#f59e0b',
  FILE_IO: '#34d399', HAS_FIELD: '#8b92a5',
}
const KG_EDGE_LABELS = {
  CALLS: 'Calls', COPIES: 'Copies', PERFORMS: 'Performs',
  USES_FIELD: 'Uses Field', READS: 'Reads', WRITES: 'Writes',
  FILE_IO: 'File I/O', HAS_FIELD: 'Has Field',
}

function KGRFNode({ data }) {
  const color = KG_TYPE_COLORS[data.nodeType] || '#8b92a5'
  const typeLabel = KG_TYPE_LABELS[data.nodeType] || data.nodeType
  return (
    <div className={[
      'dc-rf-node dc-kg-node',
      data.isSelected  ? 'dc-rf-selected'  : '',
      data.highlighted ? 'dc-rf-connected' : '',
    ].filter(Boolean).join(' ')}>
      <Handle type="target" position={Position.Left}  style={{ opacity: 0 }} />
      <span className="dc-rf-stripe" style={{ background: color }} />
      <div className="dc-rf-body">
        <span className="dc-rf-name">{data.label}</span>
        <span className="dc-rf-type" style={{ color }}>{typeLabel}</span>
        {data.program && <span className="dc-kg-program-hint">{data.program}</span>}
      </div>
      <Handle type="source" position={Position.Right} style={{ opacity: 0 }} />
    </div>
  )
}
const KG_NODE_TYPES = { kgNode: KGRFNode }

// ── HelpPopover ──────────────────────────────────────────────────────────────
// Lightweight click-to-open help popover used throughout the Define Services
// workbench.  Uses position:fixed so it is never clipped by parent overflow.

function HelpPopover({ title, body, placement = 'bottom-right' }) {
  const [open, setOpen]   = useState(false)
  const [pos,  setPos]    = useState({ top: 0, left: 0 })
  const wrapRef = useRef(null)
  const btnRef  = useRef(null)

  const recalc = useCallback(() => {
    if (!btnRef.current) return
    const r     = btnRef.current.getBoundingClientRect()
    const width = 254
    let top  = r.bottom + 6
    let left = placement.endsWith('right') ? r.left : r.right - width
    if (left < 8) left = 8
    if (left + width > window.innerWidth - 8) left = window.innerWidth - width - 8
    setPos({ top, left })
  }, [placement])

  useEffect(() => {
    if (!open) return
    recalc()
    const onDown = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false) }
    const onKey  = (e) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown',   onKey)
    window.addEventListener('resize',  recalc)
    window.addEventListener('scroll',  recalc, true)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown',   onKey)
      window.removeEventListener('resize',  recalc)
      window.removeEventListener('scroll',  recalc, true)
    }
  }, [open, recalc])

  return (
    <span className="dc-help-wrap" ref={wrapRef}>
      <button
        ref={btnRef}
        className={`dc-help-btn${open ? ' open' : ''}`}
        onClick={e => { e.stopPropagation(); setOpen(v => !v) }}
        aria-label="Help"
      >
        <HelpCircle size={11} />
      </button>
      {open && (
        <div
          className="dc-help-popover"
          role="tooltip"
          style={{ position: 'fixed', top: pos.top, left: pos.left, width: 254 }}
        >
          {title && <div className="dc-help-popover-title">{title}</div>}
          <div className="dc-help-popover-body">{body}</div>
        </div>
      )}
    </span>
  )
}

// ── Component ─────────────────────────────────────────────────────────────────

function DecomposeCodeView({
  project,
  caJobId = null,
  accountId = null,
  onBack,
  onBackToProjects,
  onGoToAnalyze,
  onGoToGenerateDocs,
  onGoToServiceGeneration,   // called with { approvedJobId } — navigates to Journey Service Generation stage
  onGoToExtractBizLogic,
  onGoToJavaPkg,             // Navigate directly to Build / Package (menu path)
  onGoToRefineCode,          // Navigate to Refine Code stage
  onGoToRefactorCode,        // Navigate to Refactor Code stage
  onOpenSettings,
  onOpenHelp,
  onOpenReports,
  userProfile = {},
}) {
  const [activeTab,         setActiveTab]         = useState('workbench')

  // ── Step 4: Define Decomposition view mode (Domains / Services / Graph / List) ──
  // Sub-tab bar inside the Workbench. Default is 'services' so today's live
  // Define Services flow is unchanged. Last-used choice persisted in
  // localStorage; reads are best-effort and never throw.
  const [decompositionViewMode, setDecompositionViewMode] = useState(() => {
    let stored = null
    try {
      stored = window.localStorage?.getItem('decompositionViewMode') ?? null
    } catch (e) {
      // localStorage may be unavailable (private browsing, SSR, etc.)
      void e
    }
    if (stored && DECOMPOSITION_VIEW_MODES.includes(stored)) return stored
    return DEFAULT_DECOMPOSITION_VIEW_MODE
  })
  // ── Step 8: Cross-view selection — Graph / List sub-tabs bubble asset ────────
  // selection up so renderSignals() can show an asset-detail panel.
  // Reset to null whenever the active sub-tab changes.
  const [crossViewSelection, setCrossViewSelection] = useState(null) // { type:'asset', data } | null

  // ── Step 10: Cmd+K command palette ───────────────────────────────────────────
  const [paletteOpen, setPaletteOpen] = useState(false)
  // Counter passed as a prop to DomainsGraphView so "Re-apply graph layout"
  // from the palette can trigger DomainsGraphView's internal layout useEffect.
  const [paletteLayoutRevision, setPaletteLayoutRevision] = useState(0)

  const handleDecompositionViewModeChange = useCallback(async (next) => {
    if (!DECOMPOSITION_VIEW_MODES.includes(next)) return

    // If we're leaving the Domains board, flush any pending debounced save
    // BEFORE setDecompositionViewMode — the board must still be mounted for the
    // flush to reach live React state.  After the flush the saved contract is on
    // disk and the re-fetch below will read it.
    if (decompositionViewMode === 'domains' && next !== 'domains') {
      try {
        await domainsBoardFlushRef.current?.()
      } catch (e) {
        void e // non-fatal — switch tabs anyway
      }
    }

    setDecompositionViewMode(next)
    setCrossViewSelection(null)            // clear cross-view selection on tab switch
    try {
      window.localStorage?.setItem('decompositionViewMode', next)
    } catch (e) {
      // best-effort persistence; ignore
      void e
    }

    // Bump domainVersion so loadDomains re-fetches the freshly-saved contract
    // whenever the user switches to Services (or any other tab that uses domainList).
    if (next !== 'domains') {
      setDomainVersion(v => v + 1)
    }
  }, [decompositionViewMode])

  const handleCrossViewSelection = useCallback((sel) => {
    setCrossViewSelection(sel)
  }, [])

  // ── Step 10: Global Cmd+K / Ctrl+K shortcut — opens / toggles command palette ──
  useEffect(() => {
    function onKeyDown(e) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        setPaletteOpen(p => !p)
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [])

  const [selectedServiceId, setSelectedServiceId] = useState('svc-customer')
  const [browserSearch,     setBrowserSearch]     = useState('')
  const [showMoreMenu,      setShowMoreMenu]      = useState(false)
  const [showUserMenu,      setShowUserMenu]      = useState(false)
  const [leftPanelCollapsed,  setLeftPanelCollapsed]  = useState(false)
  const [rightPanelCollapsed, setRightPanelCollapsed] = useState(false)
  const [hoveredNode,       setHoveredNode]       = useState(null)

  // ── Graph view state ────────────────────────────────────────────────────
  const [graphView,         setGraphView]         = useState('dependencies')  // 'services' | 'dependencies' | 'knowledge'
  const [assetGraph,        setAssetGraph]        = useState(null)            // CA guided_analyze_asset_graph.json
  const [depGraphSelected,    setDepGraphSelected]    = useState(null)
  const [graphServiceName,    setGraphServiceName]    = useState('')
  const [graphAddToServiceId, setGraphAddToServiceId] = useState('')
  const [rfNodes, setRfNodes, onRfNodesChange] = useNodesState([])
  const [rfEdges, setRfEdges, onRfEdgesChange] = useEdgesState([])

  // ── Knowledge Graph view state (Neo4j) ─────────────────────────────────
  const [neoGraph,          setNeoGraph]          = useState(null)  // { nodes: [], edges: [] } from /define-services/graph
  const [neoGraphLoading,   setNeoGraphLoading]   = useState(false)
  const [kgNodes, setKgNodes, onKgNodesChange]    = useNodesState([])
  const [kgEdges, setKgEdges, onKgEdgesChange]    = useEdgesState([])
  const [kgSelectedNode,    setKgSelectedNode]    = useState(null)  // selected node id for inspector

  // ── Real data loading state ───────────────────────────────────────────────
  // loadPhase: idle | loading | no-ca | no-mi | proposals-loading | loaded | error
  const [loadPhase,         setLoadPhase]         = useState('idle')
  // refreshCounter: incremented by the proposals-loading poll to re-run the main useEffect
  const [refreshCounter,    setRefreshCounter]    = useState(0)
  const [loadError,         setLoadError]         = useState(null)
  const [decompositionData, setDecompositionData] = useState(null)  // MI decomposition_strategy.json
  const [modularityData,    setModularityData]    = useState(null)  // MI modularity_metrics.json
  const [couplingData,      setCouplingData]      = useState(null)  // DM coupling_metrics.json
  const [upstreamJobs,      setUpstreamJobs]      = useState({})    // flow_type → job_id
  const [miJobId,           setMiJobId]           = useState(null)
  const [dmJobId,           setDmJobId]           = useState(null)
  const [anJobId,           setAnJobId]           = useState(null)
  const [namingSuggestionsData, setNamingSuggestionsData] = useState(null) // AN naming_suggestions.json
  const [serviceGroupingsData,  setServiceGroupingsData]  = useState(null) // AN service_groupings.json
  const [callGraphData,         setCallGraphData]         = useState(null) // DM call_graph.json
  // ── Phase 1 Boundary Intelligence ────────────────────────────────────────
  const [intelligenceMetrics,   setIntelligenceMetrics]   = useState(null) // CA intelligence_metrics.json
  const [depGraphData,          setDepGraphData]          = useState(null) // DM dependency_graph.json
  // ── Graph-powered Define Services (v3) ──────────────────────────────────
  const [dsJobId,               setDsJobId]               = useState(null)  // define_services job_id
  const [dsProposals,           setDsProposals]           = useState(null)  // proposals from graph clustering + AIL
  const [dsGenerating,          setDsGenerating]          = useState(false) // true while POST /define-services/proposals is in flight
  // selectedProgramCtx: name of COBOL program clicked in the tree — drives right panel program view
  const [selectedProgramCtx,    setSelectedProgramCtx]   = useState(null)
  // ── HITL state ─────────────────────────────────────────────────────────
  const [serviceStatuses,   setServiceStatuses]   = useState({})    // svc.id → overridden status
  const [discResolutions,   setDiscResolutions]   = useState({})    // disc.id → 'assign'|'common'|'exclude'
  const [isSaved,           setIsSaved]           = useState(false)
  const [isApproved,        setIsApproved]        = useState(false)
  const [isSaving,          setIsSaving]          = useState(false)
  const [isApproving,       setIsApproving]       = useState(false)
  const [approveError,      setApproveError]      = useState(null)   // non-null = last approve attempt failed
  const [approvedJobId,     setApprovedJobId]     = useState(null)
  // True when services have been edited after finalization — triggers re-finalize prompt
  const [isStaleFinalised,  setIsStaleFinalised]  = useState(false)
  const hasAutoSelectedRef  = useRef(false)
  // triggerFiredRef: guards the trigger-decompose-pipeline call so it fires once per project
  const triggerFiredRef     = useRef(false)
  // dsAutoTriggeredRef: guards auto-trigger of graph proposals so it fires once per project
  const dsAutoTriggeredRef  = useRef(false)
  // currentProjectKeyRef: tracks the loaded project so poll ticks are distinguished from project changes
  const currentProjectKeyRef = useRef(null)
  // pollCountRef: counts consecutive poll ticks to prevent infinite polling
  const pollCountRef         = useRef(0)

  // ── Tree expansion state ──────────────────────────────────────────────────
  const [treeExpanded, setTreeExpanded] = useState(
    () => new Set(['sec-source', 'sec-cobol', 'sec-copybooks', 'sec-svcgroups', 'sec-proposed'])
  )

  // ── Source inventory (from ingest/CA/CR, independent of MI) ─────────────
  const [sourceInventory, setSourceInventory] = useState(null)
  // { programs: [{name, displayName, loc}], copybooks: [{name}],
  //   jcl: [{name}], dataAssets: [{name, type}], javaFiles: [{name}] }

  // ── Manual services (HITL-created when MI hasn't run) ─────────────────────
  const [manualServices, setManualServices]   = useState([])

  // ── Step 12: domain contract for per-domain service scoping ──────────────
  // Loaded independently of the MI/CA load chain. Falls back gracefully when
  // no domain contract exists — existing Define Services flow is unchanged.
  const [domainList,       setDomainList]       = useState([])    // domains[]
  const [selectedDomainId, setSelectedDomainId] = useState(null)  // null = All
  const [domainLoadState,  setDomainLoadState]  = useState('idle') // 'idle'|'loading'|'ok'
  // Bumped when switching away from the Domains tab so loadDomains re-fetches
  // the freshly-saved contract for the Services view.
  const [domainVersion,    setDomainVersion]    = useState(0)
  // Ref wired into DomainsBoardView — calling its current() flushes any pending
  // debounced save synchronously before the board unmounts.
  const domainsBoardFlushRef = useRef(null)

  // ── Dismissed MI proposals ─────────────────────────────────────────────────
  const [dismissedMiIds, setDismissedMiIds]   = useState(new Set())

  // ── Inline new-service creation form ──────────────────────────────────────
  const [newSvcDraft, setNewSvcDraft]         = useState(null)  // { name, domain } | null

  // ── Inline rename state ────────────────────────────────────────────────────
  const [renamingSvcId, setRenamingSvcId]     = useState(null)  // id of service being renamed
  const [renameValue,   setRenameValue]       = useState('')

  // ── Drag-and-drop state ────────────────────────────────────────────────────
  const [dragOverSvcId, setDragOverSvcId]     = useState(null)

  // ── Multi-select state ────────────────────────────────────────────────────
  const [treeSelectionMode, setTreeSelectionMode] = useState(false)
  const [treeSelection,     setTreeSelection]     = useState(new Set())  // Set of "type:name" keys
  // ── Single-file AI action modal ───────────────────────────────────────────
  const [dcAiModal,         setDcAiModal]         = useState(null)       // { action, file } | null
  // ── Multi-select AI action modal + dropdown ───────────────────────────────
  const [dcBulkAiModal,     setDcBulkAiModal]     = useState(null)       // { action, files[] } | null
  const [dcBulkAiMenu,      setDcBulkAiMenu]      = useState(null)       // { top, left } | null

  // ── Tree context menu + file viewer ───────────────────────────────────────
  // contextMenu: { x, y, item: { name, displayName, path, type } } | null
  const [contextMenu, setContextMenu]         = useState(null)
  // fileViewer:  { loading, content, error, name, displayName, path, type } | null
  const [fileViewer,  setFileViewer]          = useState(null)
  // approvedContract: locked snapshot of the contract at finalization time
  const [approvedContract, setApprovedContract] = useState(null)

  // ── Decomposition Lab Phase 1 — Alternative groupings panel ──────────────
  // Collapsed by default. Expanding shows the proposal sets returned by the
  // pure UI proposalEngine — no fetches, computed from data already in state.
  const [labExpanded, setLabExpanded] = useState(false)

  useEffect(() => {
    const acct = project?.account
    const app  = project?.name
    if (!acct || !app) return

    const API_BASE = getAPIBaseUrl()
    const token    = localStorage.getItem('modernizeit_token') || ''
    const headers  = token ? { Authorization: `Bearer ${token}` } : {}
    const acctEnc  = encodeURIComponent(acct)
    const appEnc   = encodeURIComponent(app)

    // Detect whether this is a fresh project load or a background poll tick.
    // Poll ticks must NOT reset loading state or re-run source inventory fetches —
    // that was the root cause of the "Loading decomposition data..." flash-loop and
    // the repeated 404s for the CR catalog on every 5-second interval.
    const projectKey   = `${acct}:${app}`
    const isNewProject = currentProjectKeyRef.current !== projectKey
    currentProjectKeyRef.current = projectKey

    if (isNewProject) {
      // Fresh project load — reset everything
      setLoadPhase('loading')
      setLoadError(null)
      setUpstreamJobs({})
      setMiJobId(null)
      setDmJobId(null)
      setDecompositionData(null)
      setModularityData(null)
      hasAutoSelectedRef.current = false
      triggerFiredRef.current = false
      dsAutoTriggeredRef.current = false
      pollCountRef.current = 0
    }

    // Build flow_type → job_id map for all SUCCEEDED jobs for this project
    const findUpstream = async () => {
      let succeededMap = {}

      for (const statusVal of ['SUCCEEDED', 'completed']) {
        try {
          const r = await fetch(`${API_BASE}/jobs/by-app/${acctEnc}/${appEnc}?status=${statusVal}`, { headers })
          if (!r.ok) continue
          const data = await r.json()
          const jobs = data.jobs || []
          const jobMap   = {}
          const jobDates = {}   // flow_type → created_at ISO string
          jobs.forEach(j => {
            if (j.flow_type) {
              jobMap[j.flow_type]   = j.job_id
              jobDates[j.flow_type] = j.created_at || ''
            }
          })

          // ── Staleness guard ────────────────────────────────────────────────
          // When a project is deleted and re-created with the same name, old
          // SUCCEEDED jobs remain in the DB.  MI / DM / AN jobs from a prior run
          // predate the current CA job — they belong to a different pipeline
          // instance and their S3 artifacts are gone.  Discard any downstream
          // job whose created_at is earlier than the current code_analysis job
          // so the UI correctly detects "MI hasn't run yet" and enters the
          // trigger / proposals-loading path.
          const caDate = jobDates['code_analysis']
          if (caDate) {
            for (const downstream of ['monolith_identifier', 'dependency_mapper', 'ai_naming', 'code_refactor']) {
              const d = jobDates[downstream]
              if (d && d < caDate) {
                console.warn(
                  '[DecomposeCode] Discarding stale', downstream, 'job', jobMap[downstream],
                  '(created', d, ') — predates current CA job (created', caDate, ').',
                  'Project was likely re-created. Will re-trigger pipeline.'
                )
                delete jobMap[downstream]
              }
            }
          }
          // ──────────────────────────────────────────────────────────────────

          if (Object.keys(jobMap).length > 0) {
            succeededMap = jobMap
            break
          }
        } catch (err) {
          // try next status
        }
      }

      // ── Active pipeline detection ────────────────────────────────────────────
      // If CR hasn't succeeded yet, check whether the upstream pipeline is still
      // actively RUNNING or QUEUED so we show proposals-loading (not no-mi) while
      // analysis is executing.
      //
      // RC-UI-F: Previously only checked code_refactor in RUNNING/QUEUED.
      // If the user opens Define Services while CA is still running (before CR has
      // even been queued), crIsActive was false → immediate no-mi → polling stopped
      // → page stuck even after pipeline completed (ClaimsModel12 regression).
      // Now also checks code_analysis and ingest so the full pipeline window is
      // covered: CA running, CA done+CR not yet queued, CR running — all treated
      // as "pipeline active" and surface proposals-loading instead of no-mi.
      let crIsActive = false
      if (!succeededMap['code_refactor']) {
        for (const activeStatus of ['RUNNING', 'QUEUED']) {
          try {
            const r = await fetch(`${API_BASE}/jobs/by-app/${acctEnc}/${appEnc}?status=${activeStatus}`, { headers })
            if (!r.ok) continue
            const data = await r.json()
            const jobs = data.jobs || []
            // RC-UI-F: check all upstream pipeline jobs, not just code_refactor
            const pipelineActive = jobs.some(j =>
              j.flow_type === 'code_refactor' ||
              j.flow_type === 'code_analysis' ||
              j.flow_type === 'ingest'
            )
            if (pipelineActive) {
              crIsActive = true
              const activeTypes = jobs.filter(j =>
                ['code_refactor', 'code_analysis', 'ingest'].includes(j.flow_type)
              ).map(j => j.flow_type).join(', ')
              console.log('[DecomposeCode] Pipeline actively', activeStatus, '(', activeTypes, ') — entering proposals-loading early')
              break
            }
          } catch (_) {}
        }
      }

      return { jobMap: succeededMap, crIsActive }
    }

    findUpstream().then(async ({ jobMap, crIsActive }) => {
      setUpstreamJobs(jobMap)

      // Load source inventory — only on fresh project load, not on poll ticks.
      // Re-running this every 5 seconds was the source of repeated 404s for the
      // CR transformed_java_catalog when the file didn't yet exist on S3.
      if (isNewProject) {
      ;(async () => {
        const ingestId = jobMap['ingest']
        const caId     = jobMap['code_analysis']
        const crId     = jobMap['code_refactor']

        // Helper: extract clean name from S3 path
        const baseName = (p) => (p || '').split('/').pop() || p
        const progName = (p) => baseName(p).replace(/\.(cbl|cob|cobol)$/i, '').toUpperCase()
        const copyName = (p) => baseName(p).replace(/\.(cpy|copy)$/i, '').toUpperCase()
        const jclName  = (p) => baseName(p).replace(/\.jcl$/i, '').toUpperCase()

        let programs = [], copybooks = [], jcl = [], dataAssets = [], javaFiles = [], classMapping = {}

        // ── 1. Ingest classified_catalog: the authoritative full-file inventory ──
        if (ingestId) {
          try {
            const r = await fetch(`${API_BASE}/ingest/results/${ingestId}`, { headers })
            if (r.ok) {
              const data = await r.json()
              const cls  = data?.classified_catalog?.classifications || {}
              // COBOL programs
              ;(cls.cobol || []).forEach(p => {
                const name = progName(p)
                if (name && !programs.some(x => x.name === name))
                  programs.push({ name, displayName: baseName(p), loc: 0, path: p })
              })
              // Copybooks
              ;(cls.copybook || []).forEach(p => {
                const name = copyName(p)
                if (name && !copybooks.some(x => x.name === name))
                  copybooks.push({ name, displayName: baseName(p), path: p })
              })
              // JCL
              ;(cls.jcl || []).forEach(p => {
                const name = jclName(p)
                if (name && !jcl.some(x => x.name === name))
                  jcl.push({ name, displayName: baseName(p), path: p })
              })
              // Data assets (VSAM, SQL, DB2 DDL, etc.)
              ;['vsam', 'sql', 'ddl', 'config'].forEach(type => {
                ;(cls[type] || []).forEach(p => {
                  const name = baseName(p)
                  if (name && !dataAssets.some(x => x.name === name))
                    dataAssets.push({ name, type: type.toUpperCase(), path: p })
                })
              })
            }
          } catch (_) {}
        }

        // ── 2. CA job detail: fallback if ingest not in jobMap (legacy / direct CA) ──
        if (programs.length === 0 && caId) {
          try {
            const r = await fetch(`${API_BASE}/jobs/${caId}`, { headers })
            if (r.ok) {
              const j    = await r.json()
              const sum  = j?.output_data?.summary || {}
              const prog = j?.output_data?.main_program || sum.main_program
              if (prog) programs.push({ name: prog, displayName: prog, loc: sum.source_lines || 0 })
            }
          } catch (_) {}
        }

        // ── 3. CR transformed_java_catalog: OOP Java class names ─────────────
        // Keys = COBOL-derived source names ("CUSTUPD").
        // Values = OOP class filenames produced by LCOM4 splitting (["CustupdService.java"]).
        // Show the OOP class names in Generated Java so they match what MI's members[] reports.
        // Keep the raw mapping so buildServicesFromMI() can reverse-map back to COBOL keys.
        //
        // Guard: fetch the CR job detail first and check output_data.pointers.
        // CR running in analyze-mode never writes the catalog file — fetching without
        // this check produces a 404 on every project load even though it's optional.
        if (crId) {
          try {
            const jobDetailR = await fetch(`${API_BASE}/jobs/${crId}`, { headers })
            const crJobData  = jobDetailR.ok ? await jobDetailR.json() : null
            const hasCatalog = !!crJobData?.output_data?.pointers?.transformed_java_catalog
            if (hasCatalog) {
              const r = await fetch(`${API_BASE}/coderefactor/${crId}/results/json/transformed_java_catalog.json`, { headers })
              if (r.ok) {
                const cat = await r.json()
                const m   = cat?.class_mapping || {}
                classMapping = m
                const oopClasses = [...new Set(
                  Object.values(m).flat().map(f => String(f).replace(/\.java$/i, ''))
                )]
                javaFiles = oopClasses.length
                  ? oopClasses.map(name => ({ name }))
                  : Object.keys(m).map(name => ({ name })) // fallback: no split classes
              }
            }
          } catch (_) {}
        }

        // ── 4. SG service_summary: Java class names from Service Generation ─────
        // When CR hasn't run (or ran in analyze-mode with no catalog), service
        // generation output has service_summary.json with generated Java filenames.
        if (javaFiles.length === 0) {
          const sgId = jobMap['service_generation']
          if (sgId) {
            try {
              const r = await fetch(`${API_BASE}/servicegeneration/${sgId}/results/json/service_summary.json`, { headers })
              if (r.ok) {
                const summary = await r.json()
                const services = summary?.services || []
                const sgClasses = services
                  .map(s => s.filename)
                  .filter(Boolean)
                  .map(f => f.replace(/\.java$/i, ''))
                if (sgClasses.length > 0) {
                  javaFiles = sgClasses.map(name => ({ name }))
                  console.log('[DecomposeCode] Loaded', sgClasses.length, 'Java classes from service_generation summary')
                }
              }
            } catch (_) {}
          }
        }

        if (programs.length || copybooks.length || jcl.length || javaFiles.length)
          setSourceInventory({ programs, copybooks, jcl, dataAssets, javaFiles, classMapping })
      })()
      } // end isNewProject guard for source inventory

      // ── Load asset graph from Code Analysis (for relationship-assisted graph) ──
      const caJobIdForGraph = jobMap['code_analysis']
      if (caJobIdForGraph) {
        fetch(`${API_BASE}/codeanalysis/${caJobIdForGraph}/results/json/guided_analyze_asset_graph.json`, { headers })
          .then(r => r.ok ? r.json() : null)
          .then(data => { if (data) setAssetGraph(data) })
          .catch(() => {})
      }

      // ── Draft reload: check for existing decompose_code job ────────────
      // If the user previously saved a draft or approved contract, reload it
      // so they don't lose their work on re-entry.
      const dcJobId = jobMap['decompose_code']
      if (dcJobId) {
        try {
          const dcRes = await fetch(`${API_BASE}/decomposeCode/${dcJobId}/contract`, { headers })
          if (dcRes.ok) {
            const contract = await dcRes.json()
            console.log('[DecomposeCode] Reloaded existing contract:', {
              job_id: dcJobId,
              is_approved: contract.is_approved,
              services: contract.services?.length || 0,
            })
            // Restore manual services from contract
            const restoredServices = (contract.services || []).map((svc, i) => ({
              id: svc.id || `svc-restored-${i}`,
              name: svc.name,
              domain: svc.domain || 'Unclassified',
              classes: svc.classes || (svc.members || []).length,
              loc: svc.loc || 0,
              cohesion: svc.cohesion || 'medium',
              couplingOut: svc.coupling_out || 0,
              businessHint: svc.domain || '',
              status: svc.status || 'proposed',
              cobolOrigins: svc.cobol_origins || [],
              members: svc.members || [],
              whySignals: { dataShared: 'Restored from saved draft', internalDensity: '', circular: '', aiConfidence: null },
              deps: [],
              isManual: true,
              // Restore domain-promotion origin so promotedDomainIds correctly
              // filters out the matching domain-derived candidate on re-render.
              promotedFromDomainId: svc.promoted_from_domain_id || null,
            }))
            if (restoredServices.length > 0) {
              setManualServices(restoredServices)
              setSelectedServiceId(restoredServices[0].id)
              // Restore statuses
              const statuses = {}
              restoredServices.forEach(s => { if (s.status) statuses[s.id] = s.status })
              setServiceStatuses(statuses)
              // Restore approval state
              if (contract.is_approved) {
                setIsApproved(true)
                setApprovedJobId(dcJobId)
                setApprovedContract(contract)
              }
            }
          }
        } catch (e) {
          console.warn('[DecomposeCode] Draft reload failed (non-fatal):', e.message)
        }
      } else {
        // Also check localStorage fallback
        try {
          const localDraft = localStorage.getItem(`dc_draft_${acct}_${app}`)
          const localApproved = localStorage.getItem(`dc_approved_${acct}_${app}`)
          const saved = localApproved || localDraft
          if (saved) {
            const contract = JSON.parse(saved)
            const restoredServices = (contract.services || []).map((svc, i) => ({
              id: svc.id || `svc-local-${i}`,
              name: svc.name,
              domain: svc.domain || 'Unclassified',
              classes: svc.classes || (svc.members || []).length,
              loc: svc.loc || 0,
              cohesion: svc.cohesion || 'medium',
              couplingOut: svc.coupling_out || 0,
              businessHint: svc.domain || '',
              status: svc.status || 'proposed',
              cobolOrigins: svc.cobol_origins || [],
              members: svc.members || [],
              whySignals: { dataShared: 'Restored from local storage', internalDensity: '', circular: '', aiConfidence: null },
              deps: [],
              isManual: true,
              promotedFromDomainId: svc.promoted_from_domain_id || null,
            }))
            if (restoredServices.length > 0) {
              setManualServices(restoredServices)
              setSelectedServiceId(restoredServices[0].id)
              console.log('[DecomposeCode] Restored draft from localStorage:', restoredServices.length, 'services')
            }
          }
        } catch (_) {}
      }

      // ── Check for existing define_services job (graph-powered proposals) ──
      const dsExistingJobId = jobMap['define_services']
      if (dsExistingJobId) {
        try {
          const dsRes = await fetch(`${API_BASE}/define-services/proposals/${dsExistingJobId}`, { headers })
          if (dsRes.ok) {
            const dsData = await dsRes.json()
            if (dsData.proposals?.length > 0) {
              console.log('[DecomposeCode] Loaded graph proposals from job', dsExistingJobId, ':', dsData.proposals.length, 'clusters')
              setDsJobId(dsExistingJobId)
              setDsProposals(dsData.proposals)
              // Only add graph services if not already restored from draft
              if (manualServices.length === 0) {
                const graphServices = buildServicesFromGraphProposals(dsData.proposals)
                if (graphServices.length > 0) {
                  setManualServices(graphServices)
                  setSelectedServiceId(graphServices[0].id)
                  // Restore confirmation statuses from backend services
                  const confirmedIds = new Set(
                    (dsData.services || [])
                      .filter(s => s.confirmed)
                      .map(s => s.service_id)
                  )
                  const statuses = {}
                  graphServices.forEach(s => {
                    statuses[s.id] = confirmedIds.has(s.clusterId) ? 'approved' : 'proposed'
                  })
                  setServiceStatuses(statuses)

                  // Restore approval state from define_services job
                  if (dsData.locked) {
                    setIsApproved(true)
                    setApprovedJobId(dsExistingJobId)
                    // Build contract in the shape renderReport expects
                    setApprovedContract({
                      schema_version: 1,
                      decomposition_id: dsExistingJobId,
                      project: { account_id: project?.account, application_name: project?.name },
                      source: {
                        seeded_from: 'monolith_identifier',
                        dm_job_id: jobMap['dependency_mapper'] || null,
                      },
                      approved_at: new Date().toISOString(),
                      is_approved: true,
                      shared_programs: [],
                      services: graphServices.map(svc => ({
                        id: svc.id,
                        name: svc.name,
                        domain: svc.domain || svc.businessHint || '',
                        status: confirmedIds.has(svc.clusterId) ? 'approved' : 'proposed',
                        classes: svc.classes,
                        loc: svc.loc,
                        cohesion: svc.cohesion,
                        coupling_out: svc.couplingOut,
                        cobol_origins: svc.cobolOrigins,
                        members: svc.members,
                      })),
                      disconnected: [],
                      common: [],
                    })
                  }
                }
              }
              setLoadPhase('loaded')
              return
            }
          }
        } catch (e) {
          console.warn('[DecomposeCode] Define services job load failed (non-fatal):', e.message)
        }
      }

      // Phase: nothing has run at all for this project
      if (!jobMap['code_analysis'] && !jobMap['ingest']) {
        setLoadPhase('no-ca')
        return
      }

      // Phase: CA ran but MI hasn't yet
      if (!jobMap['monolith_identifier']) {
        if (jobMap['code_refactor']) {
          // CR is done — auto-trigger MI + DM + AI Naming if not already fired
          if (!triggerFiredRef.current) {
            triggerFiredRef.current = true
            const userId = (() => {
              try { return JSON.parse(localStorage.getItem('modernizeit_user') || '{}').user_id || null }
              catch (_) { return null }
            })()
            fetch(`${API_BASE}/codeanalysis/trigger-decompose-pipeline`, {
              method: 'POST',
              headers: { ...headers, 'Content-Type': 'application/json' },
              body: JSON.stringify({
                scout_account_id: acct,
                application_name: app,
                user_id: userId,
                ca_job_id: jobMap['code_analysis'] || '',
              }),
            })
              .then(r => {
                // RC-2026-D: If the API returns 202 cr_still_running, a newer CR is still
                // in flight and the trigger was deferred to avoid using a stale manifest.
                // Reset triggerFiredRef so it will re-fire on the next poll tick once
                // the in-flight CR completes and _find_succeeded() returns the fresh job.
                if (r.status === 202) {
                  return r.json().then(d => {
                    if (d.status === 'cr_still_running') {
                      console.log(
                        '[DecomposeCode] trigger deferred — newer CR still in flight:',
                        d.cr_job_id, d.inflight_status, '— will retry when CR completes'
                      )
                      // RC-2026-D: reset both guards so:
                      // 1) the trigger re-fires next poll once CR completes
                      // 2) the 180-tick MI timeout clock doesn't run while we're
                      //    still waiting for CR — the clock should only measure
                      //    how long MI takes AFTER the trigger successfully queues it.
                      triggerFiredRef.current = false
                      pollCountRef.current = 0
                    } else {
                      console.log('[DecomposeCode] trigger-decompose-pipeline 202:', d)
                    }
                    return d
                  })
                }
                // RC-UI-E+: HTTP error responses (4xx/5xx) go through .then(), not .catch().
                // Without this guard, a transient 400/404 would permanently block retries
                // (triggerFiredRef stayed true). Now reset the guard so the next poll retries.
                if (!r.ok) {
                  console.warn('[DecomposeCode] trigger-decompose-pipeline HTTP', r.status, '— resetting for retry')
                  triggerFiredRef.current = false
                  return r.json().catch(() => ({}))
                }
                return r.json().then(d => {
                  console.log('[DecomposeCode] trigger-decompose-pipeline:', d)
                  return d
                })
              })
              .catch(e => {
                // RC-UI-E: Network errors (e.g. uvicorn --reload mid-restart) silently
                // ate the trigger call and left triggerFiredRef=true, blocking all
                // retries.  Reset the guard so the next poll tick re-fires the trigger.
                console.warn('[DecomposeCode] Trigger pipeline failed — will retry:', e)
                triggerFiredRef.current = false
              })
          }
          // Stay in proposals-loading — polling useEffect will re-check every 2s.
          // After 180 ticks (6 minutes) give up and drop to no-mi so the screen
          // doesn't spin forever when the background job is stuck or failed.
          pollCountRef.current += 1
          if (pollCountRef.current > 180) {
            console.warn('[DecomposeCode] MI/DM proposals did not arrive after 6 minutes — dropping to manual mode')
            setLoadPhase('no-mi')
          } else {
            setLoadPhase('proposals-loading')
          }
        } else if (crIsActive) {
          // CR is QUEUED or RUNNING — pipeline is in flight but hasn't produced
          // output yet.  Show proposals-loading now so the user isn't left on
          // the misleading no-mi screen while analysis is actively executing.
          // Do NOT increment pollCountRef here — the MI timeout clock only
          // starts once CR is SUCCEEDED and the trigger has fired.
          setLoadPhase('proposals-loading')
        } else {
          // CR hasn't run at all (or it failed) — show standard manual guidance
          setLoadPhase('no-mi')
        }
        return
      }

      // Phase: MI ran — load artifacts
      const foundMiJobId = jobMap['monolith_identifier']
      const foundDmJobId = jobMap['dependency_mapper'] || null
      const foundAnJobId = jobMap['ai_naming'] || null
      setMiJobId(foundMiJobId)
      setDmJobId(foundDmJobId)
      setAnJobId(foundAnJobId)

      const [decomp, mod, coupling, naming, svcGroups, callGraph, intMetrics, depGraph] = await Promise.all([
        fetch(`${API_BASE}/monolithidentifier/${foundMiJobId}/results/json/decomposition_strategy.json`, { headers })
          .then(r => r.ok ? r.json() : null).catch(() => null),
        fetch(`${API_BASE}/monolithidentifier/${foundMiJobId}/results/json/modularity_metrics.json`, { headers })
          .then(r => r.ok ? r.json() : null).catch(() => null),
        foundDmJobId
          ? fetch(`${API_BASE}/dependencymapper/${foundDmJobId}/results/json/coupling_metrics.json`, { headers })
              .then(r => r.ok ? r.json() : null).catch(() => null)
          : Promise.resolve(null),
        foundAnJobId
          ? fetch(`${API_BASE}/ainaming/${foundAnJobId}/results/json/naming_suggestions.json`, { headers })
              .then(r => r.ok ? r.json() : null).catch(() => null)
          : Promise.resolve(null),
        foundAnJobId
          ? fetch(`${API_BASE}/ainaming/${foundAnJobId}/results/json/service_groupings.json`, { headers })
              .then(r => r.ok ? r.json() : null).catch(() => null)
          : Promise.resolve(null),
        foundDmJobId
          ? fetch(`${API_BASE}/dependencymapper/${foundDmJobId}/results/json/call_graph.json`, { headers })
              .then(r => r.ok ? r.json() : null).catch(() => null)
          : Promise.resolve(null),
        // ── Phase 1 Boundary Intelligence: CA intelligence_metrics ────────────
        caJobIdForGraph
          ? fetch(`${API_BASE}/codeanalysis/${caJobIdForGraph}/results/json/intelligence_metrics.json`, { headers })
              .then(r => r.ok ? r.json() : null).catch(() => null)
          : Promise.resolve(null),
        // ── Phase 1 Boundary Intelligence: DM dependency_graph (coupling leak) ─
        foundDmJobId
          ? fetch(`${API_BASE}/dependencymapper/${foundDmJobId}/results/json/dependency_graph.json`, { headers })
              .then(r => r.ok ? r.json() : null).catch(() => null)
          : Promise.resolve(null),
      ])

      // MI ran but produced no service groups — treat as no-mi
      if (!decomp?.recommended_services?.length) {
        if (!decomp) {
          // fetch returned null — 404, network error, or non-ok response from API
          console.warn(
            '[DecomposeCode] decomposition_strategy.json fetch returned null for MI job',
            foundMiJobId,
            '— API returned 404 or request failed. Dropping to manual mode.',
            'Check /monolithidentifier/' + foundMiJobId + '/results/json/decomposition_strategy.json'
          )
        } else {
          // file fetched successfully but recommended_services is absent or empty
          console.warn(
            '[DecomposeCode] decomposition_strategy.json has empty recommended_services for MI job',
            foundMiJobId,
            '— MI ran but produced 0 service candidates. Dropping to manual mode.'
          )
        }
        setLoadPhase('no-mi')
        return
      }

      setDecompositionData(decomp)
      setModularityData(mod)
      setCouplingData(coupling)
      setNamingSuggestionsData(naming)
      setServiceGroupingsData(svcGroups)
      setCallGraphData(callGraph)
      // Phase 1 Boundary Intelligence — fail-safe: only set if fetch succeeded
      if (intMetrics) setIntelligenceMetrics(intMetrics)
      if (depGraph)   setDepGraphData(depGraph)
      setLoadPhase('loaded')
    }).catch(err => {
      console.error('[DecomposeCode] Data load error:', err)
      setLoadError(err.message || 'Unknown error')
      setLoadPhase('error')
    })
  }, [project?.account, project?.name, refreshCounter])  // eslint-disable-line react-hooks/exhaustive-deps

  // ── Polling: when proposals are in-flight, re-check every 2 seconds ─────────
  // 2s (down from 5s) reduces detection lag for both the CR-complete and
  // MI-complete transitions, cutting perceived wait by 2-4 seconds per cycle.
  useEffect(() => {
    if (loadPhase !== 'proposals-loading') return
    const timer = setInterval(() => {
      console.log('[DecomposeCode] Polling for MI/DM proposals…')
      setRefreshCounter(c => c + 1)
    }, 2000)
    return () => clearInterval(timer)
  }, [loadPhase])

  // Auto-select removed: AI proposal cards now show Confirm + Dismiss in
  // the collapsed header, so forcing expansion on load is unnecessary and
  // was the root cause of the compact-layout bypass (Task 7 regression).

  // Close context menu on outside click + close either overlay on Escape
  useEffect(() => {
    const onDocClick  = () => setContextMenu(null)
    const onKeyDown   = (e) => {
      if (e.key === 'Escape') {
        if (dcAiMenu)    { setDcAiMenu(null);    return }
        if (fileViewer)  { setFileViewer(null);  return }
        if (contextMenu) { setContextMenu(null); return }
      }
    }
    document.addEventListener('click',   onDocClick)
    document.addEventListener('keydown',  onKeyDown)
    return () => {
      document.removeEventListener('click',  onDocClick)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [fileViewer, contextMenu])

  // ── Step 12: load domain contract for per-domain service scoping ─────────────
  // Independent of the main MI/CA load chain. Same contract→proposals→none
  // precedence as Steps 5–7. All setState inside named async function per
  // react-hooks/set-state-in-effect rule.

  useEffect(() => {
    let cancelled = false
    async function loadDomains() {
      if (!project?.account || !project?.name) {
        if (!cancelled) { setDomainList([]); setDomainLoadState('ok') }
        return
      }
      setDomainLoadState('loading')
      try {
        const jobId = await findDefineDomainsJobId(project.account, project.name)
        if (cancelled) return
        if (!jobId) {
          setDomainList([])
          setDomainLoadState('ok')
          return
        }
        let doms = null
        try {
          const contract = await getDomainContract(jobId)
          if (contract?.domains?.length) doms = contract.domains
        } catch (e) { void e }
        if (!doms) {
          try {
            const proposals = await getDomainProposals(jobId)
            if (proposals?.domains?.length) doms = proposals.domains
          } catch (e) { void e }
        }
        if (!cancelled) {
          setDomainList(doms || [])
          setDomainLoadState('ok')
        }
      } catch (e) {
        void e
        if (!cancelled) { setDomainList([]); setDomainLoadState('ok') }
      }
    }
    loadDomains()
    return () => { cancelled = true }
  }, [project?.account, project?.name, domainVersion])


  // ── Tree context menu & file viewer ──────────────────────────────────────

  // Look up an item's real S3 path from sourceInventory by name + type
  const findItemPath = (name, type) => {
    if (type === 'COBOL')    return (sourceInventory?.programs  || []).find(p => p.name === name)?.path || null
    if (type === 'copybook') return (sourceInventory?.copybooks || []).find(c => c.name === name)?.path || null
    if (type === 'JCL')      return (sourceInventory?.jcl       || []).find(j => j.name === name)?.path || null
    return null
  }

  // Right-click on a tree item — open custom context menu at cursor position
  const handleTreeContextMenu = (e, item) => {
    e.preventDefault()
    e.stopPropagation()
    // Adjust position to stay within viewport
    const x = Math.min(e.clientX, window.innerWidth  - 170)
    const y = Math.min(e.clientY, window.innerHeight - 90)
    setContextMenu({ x, y, item })
  }

  // "View" action from context menu — fetch real file content and open viewer
  const handleContextMenuView = async () => {
    const { item } = contextMenu
    setContextMenu(null)

    const path    = item.path || findItemPath(item.name, item.type)
    const ingestId = upstreamJobs['ingest']

    setFileViewer({ loading: true, content: null, error: null,
      name: item.displayName || item.name, path, type: item.type })

    if (!path) {
      setFileViewer(prev => ({ ...prev, loading: false, error: 'File path not available in inventory.' }))
      return
    }
    if (!ingestId) {
      setFileViewer(prev => ({ ...prev, loading: false, error: 'No Ingest job found for this project — run Upload/Ingest first.' }))
      return
    }

    const API_BASE = getAPIBaseUrl()
    const token    = localStorage.getItem('modernizeit_token') || ''
    const headers  = token ? { Authorization: `Bearer ${token}` } : {}
    const url = `${API_BASE}/editor/cobol/file?job_id=${encodeURIComponent(ingestId)}&path=${encodeURIComponent(path)}`

    console.log('[DecomposeCode] Viewing source file:', { file: item.name, path, ingestId })

    try {
      const r = await fetch(url, { headers })
      if (!r.ok) throw new Error(`HTTP ${r.status}: ${r.statusText}`)
      const data = await r.json()
      setFileViewer(prev => ({ ...prev, loading: false, content: data.content || '', error: null }))
    } catch (err) {
      console.warn('[DecomposeCode] File content unavailable:', err.message)
      setFileViewer(prev => ({ ...prev, loading: false, content: null, error: `Could not load file: ${err.message}` }))
    }
  }

  // ── HITL handlers & contract builder ─────────────────────────────────────

  const buildApprovedContract = (svcs, discs, approved) => {
    // Detect shared programs — any COBOL program whose paragraphs span 2+ services
    const _cobolCounts = {}
    svcs.forEach(svc => {
      (svc.cobolOrigins || []).forEach(prog => {
        _cobolCounts[prog] = (_cobolCounts[prog] || 0) + 1
      })
    })
    const sharedPrograms = Object.entries(_cobolCounts)
      .filter(([, count]) => count > 1)
      .map(([prog]) => prog)

    return ({
      schema_version: 1,
      decomposition_id: `dc_${project?.account}_${project?.name}_${Date.now()}`,
      project: { account_id: project?.account, application_name: project?.name },
      source: {
        mi_job_id: miJobId,
        dm_job_id: dmJobId,
        seeded_from: isReal ? 'monolith_identifier' : manualServices.length > 0 ? 'manual_hitl' : 'no_data',
        cr_job_id: upstreamJobs['code_refactor'] || null,
      },
      created_at: new Date().toISOString(),
      is_approved: !!approved,
      approved_at: approved ? new Date().toISOString() : null,
      shared_programs: sharedPrograms,
      services: svcs.map(svc => ({
        id: svc.id,
        name: svc.name,
        domain: svc.domain,
        status: serviceStatuses[svc.id] || svc.status,
        classes: svc.classes,
        loc: svc.loc,
        cohesion: svc.cohesion,
        coupling_out: svc.couplingOut,
        cobol_origins: svc.cobolOrigins,
        members: svc.members,
        // Preserve domain-promotion origin so reload can exclude the matching
        // domain-derived candidate and avoid showing duplicate cards.
        promoted_from_domain_id: svc.promotedFromDomainId || null,
      })),
      disconnected: discs.map(d => ({
        id: d.id, name: d.name, cobol: d.cobol,
        resolution: discResolutions[d.id] || null,
      })),
      common: common.map(c => ({ id: c.id, name: c.name, classes: c.classes })),
    })
  }

  // ── Generate graph-powered proposals via Define Services backend ──────────
  const handleGenerateGraphProposals = async () => {
    const acct = project?.account
    const app  = project?.name
    if (!acct || !app) return

    setDsGenerating(true)
    const API_BASE = getAPIBaseUrl()
    const token    = localStorage.getItem('modernizeit_token') || ''
    const headers  = { 'Content-Type': 'application/json' }
    if (token) headers['Authorization'] = `Bearer ${token}`

    try {
      const r = await fetch(`${API_BASE}/define-services/proposals`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          account_id: acct,
          application_name: app,
          user_id: (() => {
            try { return JSON.parse(localStorage.getItem('modernizeit_user') || '{}').user_id || null }
            catch (_) { return null }
          })(),
        }),
      })
      if (!r.ok) {
        const err = await r.json().catch(() => ({ detail: r.statusText }))
        console.error('[DecomposeCode] Generate graph proposals failed:', err)
        setDsGenerating(false)
        return
      }
      const data = await r.json()
      console.log('[DecomposeCode] Graph proposals generated:', data)
      setDsJobId(data.job_id)
      setDsProposals(data.proposals || [])

      // Convert proposals to manual services so existing card rendering works
      const graphServices = buildServicesFromGraphProposals(data.proposals || [])
      if (graphServices.length > 0) {
        setManualServices(prev => [...prev, ...graphServices])
        setSelectedServiceId(graphServices[0].id)
        // Set all to proposed status
        const statuses = {}
        graphServices.forEach(s => { statuses[s.id] = 'proposed' })
        setServiceStatuses(prev => ({ ...prev, ...statuses }))
      }
      setLoadPhase('loaded')
    } catch (e) {
      console.error('[DecomposeCode] Generate graph proposals error:', e)
    } finally {
      setDsGenerating(false)
    }
  }

  const handleSaveDraft = async (svcs, discs) => {
    const contract = buildApprovedContract(svcs, discs, false)
    const API_BASE = getAPIBaseUrl()
    const token    = localStorage.getItem('modernizeit_token') || ''
    const headers  = { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) }
    const userId   = (() => { try { return JSON.parse(localStorage.getItem('modernizeit_user') || '{}').user_id || null } catch (_) { return null } })()

    setIsSaving(true)
    try {
      // Sync domain name changes to define-services backend (if graph proposals active)
      if (dsJobId) {
        const graphSvcs = svcs.filter(s => s.isGraphProposal && s.clusterId)
        if (graphSvcs.length > 0) {
          await fetch(`${API_BASE}/define-services/proposals/${dsJobId}/domains`, {
            method: 'PUT',
            headers,
            body: JSON.stringify({
              domains: graphSvcs.map(s => ({ cluster_id: s.clusterId, domain_name: s.name })),
            }),
          }).catch(e => console.warn('[DecomposeCode] Domain sync failed (non-fatal):', e))
        }
      }

      const res = await fetch(`${API_BASE}/decomposeCode/save-draft`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          scout_account_id: project?.account,
          application_name: project?.name,
          user_id: userId,
          contract,
        }),
      })
      if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        console.error('[DecomposeCode] Save draft failed:', err)
      }
      setIsSaved(true)
      setTimeout(() => setIsSaved(false), 2500)
    } catch (e) {
      console.error('[DecomposeCode] Save draft error:', e)
      // Fallback: at least save locally so work isn't lost
      try { localStorage.setItem(`dc_draft_${project?.account}_${project?.name}`, JSON.stringify(contract)) } catch (_) {}
      setIsSaved(true)
      setTimeout(() => setIsSaved(false), 2500)
    } finally {
      setIsSaving(false)
    }
  }

  const handleExportJSON = (svcs, discs) => {
    const contract = buildApprovedContract(svcs, discs, isApproved)
    const blob = new Blob([JSON.stringify(contract, null, 2)], { type: 'application/json' })
    const url  = URL.createObjectURL(blob)
    const a    = document.createElement('a')
    a.href     = url
    a.download = `decomposition_${project?.name || 'project'}.json`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  const handleApproveService = (svcId) => {
    setServiceStatuses(prev => ({ ...prev, [svcId]: 'approved' }))
  }

  const handleUnconfirmService = (svcId) => {
    setServiceStatuses(prev => ({ ...prev, [svcId]: 'proposed' }))
    if (isApproved) setIsStaleFinalised(true)
  }

  // ── Decomposition Lab Phase 1 — Apply a proposal set ────────────────────────
  // Adds a proposal set's services to manualServices as proposed cards.
  //
  // Phase 1.x duplicate guardrail: skips proposal services whose normalized
  // membership matches a service already in the workbench (MI proposals or
  // manual cards). Equivalence is "same normalized cobol-origin set" —
  // see proposalEngine.membershipKey. Prevents the dual-ownership warning
  // that the original Apply triggered when re-applying MI Balanced.
  //
  // Authority model preserved:
  //   - status is always 'proposed' — never 'approved'
  //   - approved services are NEVER overwritten or modified
  //   - duplicate of an APPROVED service is also skipped (same key match)
  const handleApplyProposalSet = (set) => {
    if (!set || !Array.isArray(set.services) || set.services.length === 0) return
    const existingForCheck = isReal
      ? [
          ...buildServicesFromMI(decompositionData, modularityData, couplingData, sourceInventory?.classMapping, sourceInventory?.programs)
            .filter(s => !dismissedMiIds.has(s.id)),
          ...manualServices,
        ]
      : manualServices
    const { newServices, duplicateCount } = filterNewProposalServices(set, existingForCheck)
    if (newServices.length === 0) {
      console.log(`[DecomposeCode] Apply skipped — "${set.name}" all ${duplicateCount} services already represented in workbench`)
      return
    }
    const cards = proposalSetToManualServices({ ...set, services: newServices })
    if (cards.length === 0) return
    setManualServices(prev => [...prev, ...cards])
    setSelectedServiceId(cards[0].id)
    if (isApproved) setIsStaleFinalised(true)
    console.log(`[DecomposeCode] Applied proposal set "${set.name}" (${set.algorithm}) — added ${cards.length} new card${cards.length !== 1 ? 's' : ''}${duplicateCount > 0 ? `; skipped ${duplicateCount} duplicate${duplicateCount !== 1 ? 's' : ''}` : ''}`)
  }

  const handleDismissMiProposal = (svcId) => {
    setDismissedMiIds(prev => new Set([...prev, svcId]))
    if (selectedServiceId === svcId) setSelectedServiceId(null)
  }

  const handleResolveDisc = (discId, action) => {
    setDiscResolutions(prev => ({ ...prev, [discId]: action }))
  }

  const handleApprove = async (svcs, discs) => {
    const contract = buildApprovedContract(svcs, discs, true)
    const API_BASE = getAPIBaseUrl()
    const token    = localStorage.getItem('modernizeit_token') || ''
    const headers  = { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) }
    const userId   = (() => { try { return JSON.parse(localStorage.getItem('modernizeit_user') || '{}').user_id || null } catch (_) { return null } })()

    setIsApproving(true)
    setApproveError(null)
    try {
      // If graph proposals are active, run the define-services lock chain
      if (dsJobId) {
        const graphSvcs = svcs.filter(s => s.isGraphProposal && s.clusterId)

        // 1. Sync domain names
        if (graphSvcs.length > 0) {
          await fetch(`${API_BASE}/define-services/proposals/${dsJobId}/domains`, {
            method: 'PUT',
            headers,
            body: JSON.stringify({
              domains: graphSvcs.map(s => ({ cluster_id: s.clusterId, domain_name: s.name })),
            }),
          }).catch(e => console.warn('[DecomposeCode] Domain sync on lock failed:', e))
        }

        // 2. Promote to services
        await fetch(`${API_BASE}/define-services/proposals/${dsJobId}/promote`, {
          method: 'POST',
          headers,
        }).catch(e => console.warn('[DecomposeCode] Promote on lock failed:', e))

        // 3. Confirm each service
        for (const svc of graphSvcs) {
          const status = serviceStatuses[svc.id] || svc.status
          if (status === 'approved') {
            await fetch(`${API_BASE}/define-services/proposals/${dsJobId}/confirm/${svc.clusterId}`, {
              method: 'PUT',
              headers,
            }).catch(e => console.warn('[DecomposeCode] Confirm service failed:', e))
          }
        }

        // 4. Lock plan
        const lockRes = await fetch(`${API_BASE}/define-services/proposals/${dsJobId}/lock`, {
          method: 'POST',
          headers,
        })
        if (lockRes.ok) {
          const lockData = await lockRes.json()
          console.log('[DecomposeCode] Define services plan locked:', lockData)
          setApprovedJobId(lockData.job_id || dsJobId || null)
          setIsApproved(true)
          setIsStaleFinalised(false)
          setApprovedContract(contract)
          setApproveError(null)
          setActiveTab('report')
        } else {
          const err = await lockRes.json().catch(() => ({}))
          const detail = err?.detail || `HTTP ${lockRes.status}`
          console.error('[DecomposeCode] Lock failed:', err)
          setApproveError(`Lock Plan failed — ${detail}. Please try again.`)
        }
      } else {
        // No define-services job — fall back to legacy decompose_code approve
        const res = await fetch(`${API_BASE}/decomposeCode/approve`, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            scout_account_id: project?.account,
            application_name: project?.name,
            user_id: userId,
            contract,
          }),
        })
        if (res.ok) {
          const data = await res.json()
          setApprovedJobId(data.job_id || dsJobId || null)
          setIsApproved(true)
          setIsStaleFinalised(false)
          setApprovedContract(contract)
          setApproveError(null)
          setActiveTab('report')
        } else {
          const err = await res.json().catch(() => ({}))
          const detail = err?.detail || `HTTP ${res.status}`
          console.error('[DecomposeCode] Approve failed:', err)
          setApproveError(`Lock Plan failed — the approved contract was not saved. (${detail}) Please try again.`)
        }
      }
    } catch (e) {
      console.error('[DecomposeCode] Approve error:', e)
      setApproveError('Lock Plan failed — the request did not reach the server. Check your connection and try again.')
    } finally {
      setIsApproving(false)
    }
  }

  // ── Manual HITL handlers ──────────────────────────────────────────────────

  const handleCreateService = () => {
    if (!newSvcDraft?.name?.trim()) return
    const id = `svc-manual-${Date.now()}`
    const svc = {
      id, name: newSvcDraft.name.trim(),
      domain: newSvcDraft.domain?.trim() || 'Unclassified',
      classes: 0, loc: 0, cohesion: 'medium', couplingOut: 0,
      businessHint: newSvcDraft.domain?.trim() || '',
      status: 'proposed', cobolOrigins: [], members: [],
      whySignals: { dataShared: 'Manual grouping', internalDensity: 'User-defined', circular: 'N/A', aiConfidence: null },
      deps: [], isManual: true,
    }
    setManualServices(prev => [...prev, svc])
    setSelectedServiceId(id)
    setNewSvcDraft(null)
    if (isApproved) setIsStaleFinalised(true)
  }

  // Promote a domain-derived service candidate to a real editable manual service.
  // The domain-derived entry is then excluded from activeDomainDerived via
  // promotedDomainIds so there's no duplication.
  // Auto-saves immediately so the promotion survives a page refresh without
  // requiring the user to click Save Draft manually.
  const handlePromoteDomainService = (domainSvc) => {
    const promoted = {
      ...domainSvc,
      id: `svc-manual-${Date.now()}`,
      isManual: true,
      isDomainDerived: false,
      promotedFromDomainId: domainSvc.domainId,
    }
    setManualServices(prev => [...prev, promoted])
    setSelectedServiceId(promoted.id)
    if (isApproved) setIsStaleFinalised(true)

    // Auto-save: React state hasn't updated yet so build the post-promotion
    // services list directly — replace the domain-derived candidate with the
    // promoted manual service, keep everything else unchanged.
    const updatedServicesForSave = [
      ...services.filter(s => !(s.isDomainDerived && s.domainId === domainSvc.domainId)),
      promoted,
    ]
    handleSaveDraft(updatedServicesForSave, disconnected)
  }

  // Mark a service Modified if it was Confirmed before this edit
  const _markModifiedIfConfirmed = (svcId) => {
    setServiceStatuses(prev => {
      if (prev[svcId] === 'approved') return { ...prev, [svcId]: 'modified' }
      return prev
    })
    if (isApproved) setIsStaleFinalised(true)
  }

  const handleAddProgramToService = (svcId, programName, programLoc = 0) => {
    setManualServices(prev => prev.map(s => {
      if (s.id !== svcId) return s
      if ((s.cobolOrigins || []).includes(programName)) return s  // already there
      return {
        ...s,
        cobolOrigins: [...(s.cobolOrigins || []), programName],
        members: [...(s.members || []), { name: programName, cobol: programName, loc: programLoc, extDeps: 0 }],
        classes: (s.classes || 0) + 1,
        loc: (s.loc || 0) + programLoc,
      }
    }))
    _markModifiedIfConfirmed(svcId)
  }

  const handleAddJavaToService = (svcId, className) => {
    setManualServices(prev => prev.map(s => {
      if (s.id !== svcId) return s
      if ((s.members || []).some(m => m.name === className)) return s  // already there
      return {
        ...s,
        members: [...(s.members || []), { name: className, cobol: '', loc: 0, extDeps: 0 }],
        classes: (s.classes || 0) + 1,
      }
    }))
    _markModifiedIfConfirmed(svcId)
  }

  // ── Multi-select / AI action handlers ─────────────────────────────────────

  const handleToggleTreeSelection = (key) => {
    setTreeSelection(prev => {
      const next = new Set(prev)
      next.has(key) ? next.delete(key) : next.add(key)
      return next
    })
  }

  const handleClearTreeSelection = () => {
    setTreeSelection(new Set())
    setTreeSelectionMode(false)
  }

  // Convert the current treeSelection Set into the files[] shape BulkInventoryAIModal expects
  const selectionToFiles = () => [...treeSelection].map(key => {
    const ci   = key.indexOf(':')
    const type = key.slice(0, ci)
    const name = key.slice(ci + 1)
    const pd   = (sourceInventory?.programs || []).find(p => p.name === name)
    return { name, path: pd?.path || findItemPath(name, type), group: type }
  })


  // Create a draft manual service from the current multi-selection.
  // Result is always status=proposed (draft) — user must rename and confirm it.
  const handleCreateServiceFromSelection = () => {
    if (treeSelection.size === 0) return
    setDcAiMenu(null)

    const selectedItems = [...treeSelection].map(key => {
      const colonIdx = key.indexOf(':')
      return { type: key.slice(0, colonIdx), name: key.slice(colonIdx + 1) }
    })

    const members = selectedItems.map(item => {
      const progData = (sourceInventory?.programs || []).find(p => p.name === item.name)
      return { name: item.name, cobol: item.type === 'COBOL' ? item.name : '', loc: progData?.loc || 0, extDeps: 0 }
    })

    const cobolOrigins = selectedItems.filter(i => i.type === 'COBOL').map(i => i.name)
    const id           = `svc-sel-${Date.now()}`
    const defaultName  = `New Service ${manualServices.length + 1}`

    const newSvc = {
      id, name: defaultName,
      domain: 'Unclassified', classes: members.length,
      loc: members.reduce((sum, m) => sum + (m.loc || 0), 0),
      cohesion: 'medium', couplingOut: 0, businessHint: '',
      status: 'proposed', cobolOrigins, members,
      whySignals: { dataShared: 'Manual grouping', internalDensity: 'User-defined', circular: 'N/A', aiConfidence: null },
      deps: [], isManual: true,
    }

    setManualServices(prev => [...prev, newSvc])
    setSelectedServiceId(id)
    setTreeSelection(new Set())
    setTreeSelectionMode(false)
    setRenamingSvcId(id)
    setRenameValue(defaultName)
    if (isApproved) setIsStaleFinalised(true)
    console.log('[DecomposeCode] Created service from selection:', defaultName, 'members:', members.length)
  }

  // Context-menu shortcut: enter selection mode (or add to existing selection)
  const handleContextMenuSelect = () => {
    const { item } = contextMenu
    setContextMenu(null)
    const key = `${item.type}:${item.name}`
    if (treeSelectionMode) {
      setTreeSelection(prev => { const next = new Set(prev); next.add(key); return next })
    } else {
      setTreeSelection(new Set([key]))
      setTreeSelectionMode(true)
    }
  }

  const handleRemoveMember = (svcId, memberName) => {
    let willBeEmpty = false
    setManualServices(prev => prev.map(s => {
      if (s.id !== svcId) return s
      const member = (s.members || []).find(m => m.name === memberName)
      const newMembers = (s.members || []).filter(m => m.name !== memberName)
      if (newMembers.length === 0) willBeEmpty = true
      return {
        ...s,
        members: newMembers,
        cobolOrigins: (s.cobolOrigins || []).filter(p => p !== memberName),
        classes: Math.max(0, s.classes - 1),
        loc: Math.max(0, s.loc - (member?.loc || 0)),
      }
    }))
    if (willBeEmpty) {
      // Service is now empty — reset to draft so it doesn't block validation as a confirmed empty service
      setServiceStatuses(prev => ({ ...prev, [svcId]: 'proposed' }))
      if (isApproved) setIsStaleFinalised(true)
    } else {
      _markModifiedIfConfirmed(svcId)
    }
  }

  const handleDeleteService = (svcId) => {
    setManualServices(prev => prev.filter(s => s.id !== svcId))
    if (selectedServiceId === svcId) setSelectedServiceId(null)
    if (isApproved) setIsStaleFinalised(true)
  }

  const handleStartRename = (svc) => {
    setRenamingSvcId(svc.id)
    setRenameValue(svc.name)
  }

  const handleCommitRename = (svcId) => {
    const trimmed = renameValue.trim()
    if (trimmed) {
      setManualServices(prev => prev.map(s =>
        s.id === svcId ? { ...s, name: trimmed } : s
      ))
      _markModifiedIfConfirmed(svcId)
    }
    setRenamingSvcId(null)
    setRenameValue('')
  }

  // ── Drag-and-drop handlers ─────────────────────────────────────────────────

  // Resolve member type for badge display
  const getMemberType = (member) => {
    if ((sourceInventory?.jcl || []).some(j => j.name === member.name)) return 'JCL'
    if ((sourceInventory?.javaFiles || []).some(f => f.name === member.name)) return 'Java'
    if (member.cobol || (sourceInventory?.programs || []).some(p => p.name === member.name)) return 'COBOL'
    return 'Java'
  }

  const handleDrop = (svcId, e) => {
    e.preventDefault()
    setDragOverSvcId(null)
    try {
      const raw  = e.dataTransfer.getData('dc-asset')
      if (!raw) return
      const data = JSON.parse(raw)
      if (data.type === 'Java') {
        handleAddJavaToService(svcId, data.name)
      } else {
        handleAddProgramToService(svcId, data.name, data.loc || 0)
      }
    } catch (_) {}
  }

  // ── Computed service lists ────────────────────────────────────────────────
  const isReal       = loadPhase === 'loaded' && decompositionData != null
  const isNoMi       = loadPhase === 'no-mi'
  const isNoCa       = loadPhase === 'no-ca'

  // When no MI decomposition data exists, auto-switch from the Domains tab
  // (which has no backend) to the Services tab where graph-powered proposals appear.
  // Checks both isNoMi and the loaded-but-no-MI-data case (graph proposals loaded).
  useEffect(() => {
    if (!decompositionData && loadPhase !== 'idle' && loadPhase !== 'loading' && decompositionViewMode === 'domains') {
      setDecompositionViewMode('services')
    }
  }, [decompositionData, loadPhase]) // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-trigger graph-powered proposals when no MI data and graph data exists.
  // This replaces the manual "Generate Proposals" button click — proposals
  // appear automatically on page load.
  useEffect(() => {
    if (
      isNoMi
      && upstreamJobs['code_analysis']
      && !dsProposals
      && !dsGenerating
      && !dsAutoTriggeredRef.current
    ) {
      dsAutoTriggeredRef.current = true
      handleGenerateGraphProposals()
    }
  }, [isNoMi, upstreamJobs, dsProposals, dsGenerating]) // eslint-disable-line react-hooks/exhaustive-deps

  // Domain → service candidates: each saved domain with members becomes a
  // draft service candidate visible in the Services tab.
  const domainDerivedServices = useMemo(
    () => domainList.filter(d => (d.members?.length || 0) > 0).map(buildServiceFromDomain),
    [domainList]
  )
  const hasDomainWork = domainDerivedServices.length > 0

  // IDs of domain-derived services that have been promoted to manual services.
  // Promoted services are stored in manualServices with promotedFromDomainId set;
  // the corresponding domain-derived entry is then excluded to avoid duplication.
  const promotedDomainIds = new Set(manualServices.map(s => s.promotedFromDomainId).filter(Boolean))

  // Active domain-derived services: exclude dismissed and promoted ones.
  const activeDomainDerived = domainDerivedServices.filter(
    s => !dismissedMiIds.has(s.id) && !promotedDomainIds.has(s.domainId)
  )

  // Services used for readiness / approve / finalize — single source of truth.
  // Priority: MI proposals (if available) > domain-derived (if domain work exists) > manual only.
  // MI proposals + manual always takes precedence when MI data is present.
  const services     = isReal
    ? [...buildServicesFromMI(decompositionData, modularityData, couplingData, sourceInventory?.classMapping, sourceInventory?.programs).filter(s => !dismissedMiIds.has(s.id)), ...manualServices]
    : hasDomainWork
      ? [...activeDomainDerived, ...manualServices]
      : manualServices
  const disconnected = isReal ? buildDisconnectedFromMI(decompositionData) : []
  // Common/shared items: derive from MI shared_data when available, otherwise empty
  const common       = isReal
    ? (() => {
        const sharedItems = []
        const seen = new Set()
        ;(decompositionData?.recommended_services || []).forEach(svc => {
          ;(svc.shared_data || []).forEach(item => {
            if (!seen.has(item)) {
              seen.add(item)
              sharedItems.push({ id: `common-${item}`, name: item, classes: 0, loc: 0, cobolOrigins: [] })
            }
          })
        })
        return sharedItems
      })()
    : []

  const projectName  = project?.name || 'Project'
  const selectedSvc  = services.find(s => s.id === selectedServiceId) || services[0]

  // Status counts — derived from serviceStatuses overrides
  const proposedCount = services.filter(s => (serviceStatuses[s.id] || s.status) === 'proposed').length
  const modifiedCount = services.filter(s => (serviceStatuses[s.id] || s.status) === 'modified').length
  const approvedCount = services.filter(s => (serviceStatuses[s.id] || s.status) === 'approved').length
  const disconnCount  = disconnected.filter(d => !discResolutions[d.id]).length

  // Structured readiness — single source of truth for all finalization gates and UI hints
  const readiness = computeReadiness(services, serviceStatuses, sourceInventory, disconnected, discResolutions, isReal)

  // canFinalize: readiness gates + stale-finalization re-entry
  const canFinalize = !isNoCa && (!isApproved || isStaleFinalised) && readiness.isReadyToFinalize
  const canApprove  = canFinalize  // alias kept for handler compatibility

  // Dynamic pipeline nodes reflecting actual upstream run status
  const computedPipelineNodes = [
    {
      id: 'refactor',  label: 'Code Refactor',
      contrib: upstreamJobs['code_refactor'] ? 'LCOM4-split Java classes — complete' : 'LCOM4-split Java classes',
      done: !!upstreamJobs['code_refactor'],        active: false,
    },
    {
      id: 'monolith',  label: 'Monolith Identifier',
      contrib: upstreamJobs['monolith_identifier'] ? 'Service grouping proposals — complete' : 'Service grouping proposals',
      done: !!upstreamJobs['monolith_identifier'],  active: false,
    },
    {
      id: 'depmap',    label: 'Dependency Mapper',
      contrib: upstreamJobs['dependency_mapper'] ? 'Coupling metrics + call graph — complete' : 'Coupling metrics + call graph',
      done: !!upstreamJobs['dependency_mapper'],    active: false,
    },
    {
      id: 'decompose', label: 'Define Services',
      contrib: isStaleFinalised
        ? 'Services modified after finalization — re-finalize required'
        : isApproved
          ? 'Service plan locked ✓'
          : 'Define and lock your service plan',
      done: isApproved && !isStaleFinalised,
      active: !isApproved || isStaleFinalised,
    },
    {
      id: 'svcgen',    label: 'Build Services',
      contrib: isApproved && !isStaleFinalised ? 'Ready — begin now' : 'Generate Spring Boot services',
      done: false, active: isApproved && !isStaleFinalised,
    },
  ]

  const getShortName = () => {
    if (userProfile.firstName && userProfile.lastName) {
      return `${userProfile.firstName} ${userProfile.lastName.charAt(0)}.`
    }
    return userProfile.displayName || 'User'
  }

  // ── Tree helpers ──────────────────────────────────────────────────────────

  const toggleTree = (id) => setTreeExpanded(prev => {
    const next = new Set(prev)
    next.has(id) ? next.delete(id) : next.add(id)
    return next
  })

  // Resolved user ID for child-component API calls (DomainsBoardView, DecompositionListView).
  // userProfile from useSettings() does NOT carry user_id — the auth layer stores it in
  // localStorage under 'modernizeit_user'. Reading here is the same approach used for the
  // inline save-draft calls in this component (see handleSaveDraft / handleApprove).
  const resolvedUserId = (() => {
    try { return JSON.parse(localStorage.getItem('modernizeit_user') || '{}').user_id || null }
    catch { return null }
  })()

  // Source assets for tree — always the authoritative ingest/CR inventory so the
  // user has a complete working set for HITL regardless of whether MI has loaded.
  // svcForCobol/svcForJava badge each asset with whichever proposed service claims it.
  const allCobolPrograms = (sourceInventory?.programs  || []).map(p => p.name)
  const allJavaClasses   = (sourceInventory?.javaFiles || []).map(f => f.name)
  const allCopybooks     = sourceInventory?.copybooks  || []
  const allJcl           = sourceInventory?.jcl        || []
  const allDataAssets    = sourceInventory?.dataAssets || []
  const svcForCobol = (prog) => services.find(s => (s.cobolOrigins || []).includes(prog))
  const svcForJava  = (cls)  => services.find(s => (s.members || []).some(m => m.name === cls))

  // ── Step 12: domain-scoping helpers ──────────────────────────────────────────
  // programToDomainId: program name → domain_id (majority-vote across paragraph members)
  // Same algorithm as DomainsGraphView / DecompositionListView for visual consistency.
  const programToDomainId = useMemo(() => {
    if (!domainList.length) return {}
    const counts = {}
    domainList.forEach(d => {
      ;(d.members || []).forEach(m => {
        const prog = (m.source_program || '').trim()
        if (!prog) return
        if (!counts[prog]) counts[prog] = {}
        counts[prog][d.domain_id] = (counts[prog][d.domain_id] || 0) + 1
      })
    })
    const result = {}
    Object.entries(counts).forEach(([prog, domCounts]) => {
      const [bestDomId] = Object.entries(domCounts).sort((a, b) => b[1] - a[1])[0]
      result[prog] = bestDomId
    })
    return result
  }, [domainList])

  // programsForDomain: domain_id → Set<programName>
  const programsForDomain = useMemo(() => {
    const map = {}
    Object.entries(programToDomainId).forEach(([prog, domId]) => {
      if (!map[domId]) map[domId] = new Set()
      map[domId].add(prog)
    })
    return map
  }, [programToDomainId])

  // Active domain's program Set (null when "All" is selected — no filter)
  const activeDomPrograms = (selectedDomainId && programsForDomain[selectedDomainId]) || null

  // COBOL programs visible in the tree — scoped to active domain when a tab is selected
  const domainVisiblePrograms = activeDomPrograms
    ? allCobolPrograms.filter(p => activeDomPrograms.has(p))
    : allCobolPrograms

  // ── Step 11: Pipeline Overview inset data ────────────────────────────────────
  // Per-domain program count + service count for the flow inset.
  // serviceCount per domain: services whose cobolOrigins overlap the domain's programs.
  const flowInsetDomains = useMemo(() => {
    return domainList.map(d => {
      const progSet = programsForDomain[d.domain_id] || new Set()
      const svcCount = services.filter(s =>
        (s.cobolOrigins || []).some(p => progSet.has(p))
      ).length
      return {
        domain_id:    d.domain_id,
        name:         d.name,
        programCount: progSet.size,
        serviceCount: svcCount,
      }
    })
  }, [domainList, programsForDomain, services])

  // ── Step 10: Command palette commands ────────────────────────────────────────
  // Built fresh each render (deps tracked by useMemo). Commands are real — each
  // action calls an existing state setter or handler already tested in Steps 5–12.
  const paletteCommands = useMemo(() => {
    const cmds = []

    // ── Navigation: sub-tab switching ────────────────────────────────────────
    if (decompositionViewMode !== 'domains') {
      cmds.push({ id: 'nav-domains', label: 'Switch to Domains view', group: 'Navigation',
        action: () => handleDecompositionViewModeChange('domains') })
    }
    if (decompositionViewMode !== 'services') {
      cmds.push({ id: 'nav-services', label: 'Switch to Services view', group: 'Navigation',
        action: () => handleDecompositionViewModeChange('services') })
    }
    if (decompositionViewMode !== 'graph') {
      cmds.push({ id: 'nav-graph', label: 'Switch to Graph view', group: 'Navigation',
        action: () => handleDecompositionViewModeChange('graph') })
    }
    if (decompositionViewMode !== 'list') {
      cmds.push({ id: 'nav-list', label: 'Switch to List view', group: 'Navigation',
        action: () => handleDecompositionViewModeChange('list') })
    }

    // ── Navigation: main tab switching ────────────────────────────────────
    if (activeTab !== 'workbench') {
      cmds.push({ id: 'nav-workbench', label: 'Go to Workbench', group: 'Navigation',
        action: () => setActiveTab('workbench') })
    }
    if (activeTab !== 'report') {
      cmds.push({ id: 'nav-report', label: 'Go to Handoff Plan / Report', group: 'Navigation',
        action: () => setActiveTab('report') })
    }

    // ── Domain filter commands (only when domains loaded + services view) ──
    if (domainList.length > 0 && decompositionViewMode === 'services') {
      if (selectedDomainId !== null) {
        cmds.push({ id: 'dom-all', label: 'Show all domains (clear filter)', group: 'Domains',
          action: () => setSelectedDomainId(null) })
      }
      domainList.forEach(d => {
        if (selectedDomainId !== d.domain_id) {
          cmds.push({
            id: `dom-${d.domain_id}`,
            label: `Filter by domain: ${d.name}`,
            group: 'Domains',
            action: () => setSelectedDomainId(d.domain_id),
          })
        }
      })
    }

    // ── Layout: re-apply graph layout (only in graph view) ────────────────
    if (decompositionViewMode === 'graph') {
      cmds.push({ id: 'layout-reapply', label: 'Re-apply graph layout', group: 'Layout',
        action: () => setPaletteLayoutRevision(r => r + 1) })
    }

    // ── Actions: finalization ─────────────────────────────────────────────
    if (isApproved && !isStaleFinalised) {
      cmds.push({ id: 'act-finalized', label: 'Plan already locked ✓', group: 'Actions',
        disabled: true, action: () => {} })
    } else if (canFinalize && !isApproving) {
      cmds.push({ id: 'act-finalize', label: 'Lock Plan', group: 'Actions',
        action: () => handleApprove(services, disconnected) })
    }

    return cmds
  }, [
    decompositionViewMode, activeTab, domainList, selectedDomainId,
    canFinalize, isApproving, isApproved, isStaleFinalised,
    handleDecompositionViewModeChange, handleApprove, services, disconnected,
  ])

  // ── HEADER (top nav bar) ────────────────────────────────────────────────────

  const renderHeader = () => (
    <div className="runs-header-bar">
      <div className="runs-header-left">
        <button className="runs-back-btn" onClick={onBack}>
          <ArrowLeft size={15} /><span>Runs</span>
        </button>
        <div className="runs-breadcrumb">
          <span className="runs-bc-project">{projectName}</span>
          <ChevronRight size={14} className="runs-bc-sep" />
          <span className="runs-bc-page">Define Services</span>
        </div>
      </div>

      <div className="runs-header-center">
        <div className="mode-switcher">
          <button className="mode-btn" onClick={onBackToProjects || onBack} title="Projects">
            <Home size={16} /><span>Home</span>
          </button>
          <button className="mode-btn" onClick={() => onGoToAnalyze?.()} title="Analyze">
            <BarChart2 size={16} /><span>Analyze</span>
          </button>
          {/* Workflow and MCP hidden — accessible via Journey dropdown
          <button className="mode-btn" disabled title="Workflow">
            <Workflow size={16} /><span>Workflow</span>
          </button>
          <button className="mode-btn" disabled title="MCP — coming soon">
            <Plug size={16} /><span>MCP</span>
          </button>
          */}
          <button className="mode-btn" onClick={() => onOpenReports?.()} title="Reports">
            <FileBarChart size={16} /><span>Reports</span>
          </button>

          <div className="more-menu-container">
            <button
              className={`mode-btn more-trigger ${showMoreMenu ? 'active' : ''}`}
              onClick={() => setShowMoreMenu(v => !v)}
              disabled={false}
              title="HITL Journey"
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="4" y1="6" x2="20" y2="6" />
                <line x1="4" y1="12" x2="20" y2="12" />
                <line x1="4" y1="18" x2="20" y2="18" />
              </svg>
              <ChevronDown size={12} className={`more-chevron ${showMoreMenu ? 'open' : ''}`} />
            </button>

            {showMoreMenu && (
              <>
                <div className="more-backdrop" onClick={() => setShowMoreMenu(false)} />
                <div className="more-dropdown">
                  <div className="dropdown-section-label">MODERNIZATION FLOW</div>
                  <button className="dropdown-item" onClick={() => { setShowMoreMenu(false); onGoToAnalyze?.() }}>
                    <span>Analyze</span>
                  </button>
                  <button className="dropdown-item" onClick={() => { setShowMoreMenu(false); onGoToGenerateDocs?.() }}>
                    <span>Generate Documentation</span>
                  </button>
                  {/* EBL disabled — graph + AIL replaces it (see docs/20260721_define_services_architecture.md)
                  <button className="dropdown-item" onClick={() => { setShowMoreMenu(false); onGoToExtractBizLogic?.(caJobId) }}>
                    <span>Extract Business Logic</span>
                  </button>
                  */}
                  <button className="dropdown-item active-item" onClick={() => setShowMoreMenu(false)}>
                    <span>Define Services</span>
                  </button>
                  <div className="dropdown-divider" />
                  <button className="dropdown-item" onClick={() => { setShowMoreMenu(false); onGoToRefactorCode?.() }}><span>Refactor Code</span></button>
                  <button className="dropdown-item" onClick={() => { setShowMoreMenu(false); onGoToRefineCode?.() }}>
                    <span>Refine Code</span>
                  </button>
                  <div className="dropdown-divider" />
                  <button className="dropdown-item" onClick={() => { setShowMoreMenu(false); onGoToJavaPkg?.([]) }}>
                    <span>Build / Package</span>
                  </button>
                  <div className="dropdown-divider" />
                  <button className="dropdown-item" onClick={() => { setShowMoreMenu(false); onOpenReports?.() }}>
                    <span>Reports</span>
                  </button>
                  <button className="dropdown-item" onClick={() => { setShowMoreMenu(false); onOpenSettings?.() }}>
                    <span>Settings</span>
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="runs-header-right">
        <button className="icon-btn" onClick={onOpenSettings} title="Settings">
          <Settings size={18} />
        </button>
        <button className="icon-btn" onClick={onOpenHelp} title="Help">
          <HelpCircle size={18} />
        </button>
        <div className="user-menu-container">
          <button className="user-btn" onClick={() => setShowUserMenu(v => !v)}>
            <div className="user-avatar"><User size={16} /></div>
            <span>{getShortName()}</span>
            <ChevronDown size={14} />
          </button>
          {showUserMenu && (
            <div className="user-dropdown">
              <div className="dropdown-header">
                <span className="user-name">{userProfile.displayName || 'User'}</span>
                <span className="user-email">{userProfile.email || ''}</span>
              </div>
              <div className="dropdown-divider" />
              <button className="dropdown-item" onClick={() => { setShowUserMenu(false); onOpenSettings?.() }}>
                <Settings size={16} /><span>Settings</span>
              </button>
              <button className="dropdown-item" onClick={() => { setShowUserMenu(false); onBack() }}>
                <Home size={16} /><span>Back to Runs</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )

  // ── STAGE HEADER (title + chips + actions + tabs) ──────────────────────────

  const renderStageHeader = () => (
    <div className="dc-stage-header">
      <div className="dc-stage-header-top">
        <div className="dc-stage-header-left">
          <div className="dc-stage-title-row">
            <h2 className="dc-stage-title">Define Services</h2>
            <span className="dc-stage-set-name">{projectName}</span>
          </div>
          {activeTab !== 'graph' && activeTab !== 'workbench' && (
            <p className="dc-stage-subtitle">
              {isApproved && !isStaleFinalised
                ? 'Plan locked — proceed to Build Services from the menu when ready.'
                : isReal
                  ? 'Review the suggested groups, pick the ones you want as services, confirm each one, then lock the plan.'
                  : 'Add services from the left panel, assign source programs to each one, then lock the plan.'
              }
            </p>
          )}
          {/* Status chips — only shown outside workbench, where step guide is not visible */}
          {activeTab !== 'workbench' && (
            <div className="dc-status-summary">
              {(proposedCount + modifiedCount) > 0 && (
                <span className="dc-status-chip proposed">{proposedCount + modifiedCount} to review</span>
              )}
              {approvedCount > 0 && (
                <span className="dc-status-chip approved">{approvedCount} confirmed</span>
              )}
              {disconnCount > 0 && (
                <span className="dc-status-chip unassigned">{disconnCount} unassigned</span>
              )}
              {(proposedCount + modifiedCount + approvedCount) === 0 && services.length === 0 && (
                <span className="dc-status-chip proposed">No services yet</span>
              )}
            </div>
          )}
        </div>

        <div className="dc-stage-actions">
          {/* Save Draft disabled: draft is saved to S3 (status=RUNNING) but the
              reload path only queries SUCCEEDED jobs, so it is never restored.
              Re-enable once findUpstream() includes RUNNING decompose_code jobs. */}
          <button
            className="dc-action-btn secondary"
            disabled={true}
            title="Save Draft is temporarily unavailable — use Lock Plan to save your work"
          >
            <Download size={13} />
            Save Draft
          </button>
          <button
            className="dc-action-btn secondary"
            onClick={() => handleExportJSON(services, disconnected)}
            disabled={isNoCa}
          >
            <Download size={13} />
            Export JSON
          </button>
          <button
            className={`dc-action-btn primary${canFinalize ? '' : ' disabled'}${isStaleFinalised ? ' stale' : ''}`}
            disabled={!canFinalize || isApproving}
            onClick={canFinalize && !isApproving ? () => handleApprove(services, disconnected) : undefined}
            title={
              (isApproved && !isStaleFinalised)
                ? 'Plan locked — edit services to update'
              : isStaleFinalised && !readiness.isReadyToFinalize
                ? `Cannot lock: ${readiness.blockingReasons[0]}`
              : isStaleFinalised
                ? 'Services changed since last lock — re-lock to update'
              : isNoCa
                ? 'Run Code Analysis first'
              : !readiness.isReadyToFinalize
                ? readiness.blockingReasons[0] || 'Not ready to lock yet'
              : 'Lock your service plan — this does not generate code yet'
            }
          >
            {isApproving
              ? <Loader2 size={13} className="dc-spin" />
              : (isApproved && !isStaleFinalised)
                ? <CheckCircle2 size={13} />
                : <Lock size={13} />
            }
            {isApproving
              ? 'Locking…'
              : (isApproved && !isStaleFinalised)
                ? 'Plan Locked ✓'
                : isStaleFinalised
                  ? 'Re-lock Plan'
                  : 'Lock Plan'
            }
          </button>
        </div>
      </div>

      {/* ── Approve error banner — shown when Lock Plan fails (backend or network) ── */}
      {approveError && (
        <div className="dc-readiness-strip blocked" style={{ cursor: 'default' }}>
          <div className="dc-readiness-row blocking">
            <AlertCircle size={11} className="dc-readiness-ico" />
            <span className="dc-readiness-reason blocking">{approveError}</span>
            <button
              style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', fontSize: '11px', opacity: 0.7 }}
              onClick={() => setApproveError(null)}
            >✕</button>
          </div>
        </div>
      )}

      {/* ── Readiness strip — blockers only, one at a time, visible before finalization ── */}
      {!isNoCa && !isApproving && !(isApproved && !isStaleFinalised) &&
        readiness.blockingReasons.length > 0 && (
        <div className="dc-readiness-strip blocked">
          <div className="dc-readiness-row blocking">
            <AlertCircle size={11} className="dc-readiness-ico" />
            <span className="dc-readiness-reason blocking">{readiness.blockingReasons[0]}</span>
            {readiness.blockingReasons.length > 1 && (
              <span className="dc-readiness-more">+{readiness.blockingReasons.length - 1} more issue{readiness.blockingReasons.length > 2 ? 's' : ''}</span>
            )}
          </div>
        </div>
      )}

      {/* Tab bar */}
      <div className="dc-tab-bar">
        {[
          { id: 'workbench',  label: 'Workbench',      icon: Layers        },
          { id: 'graph',      label: 'Graph',           icon: Network       },
          { id: 'report',     label: 'Handoff Plan',    icon: ClipboardList, badge: isApproved && !isStaleFinalised },
          // { id: 'automation', label: 'Automation Flow', icon: GitBranch     }, // commented out — does nothing yet
        ].map(tab => (
          <button
            key={tab.id}
            className={`dc-tab ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            <tab.icon size={14} />
            <span>{tab.label}</span>
            {tab.badge && <span className="dc-tab-badge" />}
          </button>
        ))}
      </div>
    </div>
  )

  // ── WORKBENCH — LEFT PANEL (Inventory Tree) ──────────────────────────────────

  const renderInventoryTree = () => {
    const isExp = (id) => treeExpanded.has(id)
    const matchSearch = (str) => !browserSearch || str.toLowerCase().includes(browserSearch.toLowerCase())

    const proposedSvcs  = services.filter(s => (serviceStatuses[s.id] || s.status) !== 'approved')
    const approvedSvcs  = services.filter(s => (serviceStatuses[s.id] || s.status) === 'approved')

    const TreeSectionHdr = ({ id, icon: Icon, label, count, danger }) => (
      <button
        className={`dc-tree-sec-hdr${danger ? ' dc-tree-danger' : ''}`}
        onClick={() => toggleTree(id)}
      >
        <ChevronRight size={11} className={`dc-tree-chev ${isExp(id) ? 'open' : ''}`} />
        <Icon size={13} className="dc-tree-sec-ico" />
        <span className="dc-tree-sec-lbl">{label}</span>
        {count != null && (
          <span className={`dc-tree-badge${danger ? ' danger' : ''}`}>{count}</span>
        )}
      </button>
    )

    const TreeGroupHdr = ({ id, depth = 1, icon: Icon, label, count, danger }) => (
      <button
        className={`dc-tree-grp-hdr depth-${depth}${danger ? ' danger' : ''}`}
        onClick={() => toggleTree(id)}
      >
        <div className={`dc-tree-indent depth-${depth}`} />
        <ChevronRight size={10} className={`dc-tree-chev ${isExp(id) ? 'open' : ''}`} />
        <Icon size={12} className="dc-tree-grp-ico" />
        <span className="dc-tree-grp-lbl">{label}</span>
        {count != null && (
          <span className={`dc-tree-badge sm${danger ? ' danger' : ''}`}>{count}</span>
        )}
      </button>
    )

    // Selection key helpers
    const selKey = (type, name) => `${type}:${name}`
    const isSel  = (type, name) => treeSelectionMode && treeSelection.has(selKey(type, name))

    // ── Collapsed strip ────────────────────────────────────────────────────
    if (leftPanelCollapsed) {
      return (
        <aside className="dc-tree dc-tree--collapsed">
          <button
            className="dc-panel-tab dc-panel-tab--left"
            onClick={() => setLeftPanelCollapsed(false)}
            title="Expand source assets"
          >
            <ChevronRight size={15} />
          </button>
        </aside>
      )
    }

    return (
      <aside className="dc-tree">
        {/* Search + collapse toggle */}
        <div className="dc-tree-search-row">
          <Search size={12} className="dc-tree-search-ico" />
          <input
            className="dc-tree-search"
            placeholder="Filter assets…"
            value={browserSearch}
            onChange={e => setBrowserSearch(e.target.value)}
          />
          <button
            className="dc-panel-collapse-btn"
            onClick={() => setLeftPanelCollapsed(true)}
            title="Collapse panel"
          >
            <X size={12} />
          </button>
        </div>

        {/* ── Multi-select controls ─────────────────────────────────────────── */}
        <div className="dc-tree-multisel-bar">
          <button
            className={`dc-tree-multisel-toggle${treeSelectionMode ? ' active' : ''}`}
            onClick={() => treeSelectionMode ? handleClearTreeSelection() : setTreeSelectionMode(true)}
          >
            Multi-select: {treeSelectionMode ? 'On' : 'Off'}
          </button>
          {treeSelectionMode && treeSelection.size > 0 && (
            <>
              <button className="dc-tree-multisel-action" onClick={handleCreateServiceFromSelection}>
                Create Service ({treeSelection.size})
              </button>
              <button className="dc-tree-multisel-action" onClick={e => {
                const rect = e.currentTarget.getBoundingClientRect()
                setDcBulkAiMenu({ top: rect.bottom + 4, left: rect.left })
              }}>
                AI Actions ({treeSelection.size})
              </button>
              <button className="dc-tree-multisel-clear" onClick={handleClearTreeSelection}>
                Clear
              </button>
            </>
          )}
        </div>

        {/* ── SOURCE ASSETS ──────────────────────────────────── */}
        {/* Step 12: counts and list use domainVisiblePrograms so the tree reflects the active domain tab */}
        <TreeSectionHdr id="sec-source" icon={Layers} label="Source Assets"
          count={domainVisiblePrograms.length + allCopybooks.length + allJcl.length + allDataAssets.length + allJavaClasses.length || null}
        />

        {isExp('sec-source') && <>
          {/* COBOL Programs */}
          <TreeGroupHdr id="sec-cobol" depth={1} icon={FileCode}
            label="COBOL Programs" count={domainVisiblePrograms.length} />
          {isExp('sec-cobol') && domainVisiblePrograms.filter(p => matchSearch(p)).map(prog => {
            const svc = svcForCobol(prog)
            return (
              (() => {
                const inManualSvc = svc?.isManual
                const canAdd = !svc && selectedServiceId &&
                  manualServices.some(s => s.id === selectedServiceId)
                const progData = (sourceInventory?.programs || []).find(p => p.name === prog)
                const selActive = isSel('COBOL', prog)
                return (
                  <button
                    key={`cobol-${prog}`}
                    className={`dc-tree-leaf depth-2${svc?.id === selectedServiceId ? ' linked-sel' : ''}${canAdd ? ' addable' : ''}${!isReal && !svc && !treeSelectionMode ? ' draggable-item' : ''}${selActive ? ' sel-active' : ''}`}
                    draggable={!svc && !treeSelectionMode}
                    onContextMenu={e => handleTreeContextMenu(e, { name: prog, displayName: progData?.displayName || prog, path: progData?.path || null, type: 'COBOL' })}
                    onDragStart={e => {
                      if (!svc && !treeSelectionMode) {
                        e.dataTransfer.setData('dc-asset', JSON.stringify({ name: prog, type: 'COBOL', loc: progData?.loc || 0 }))
                        e.dataTransfer.effectAllowed = 'copy'
                      }
                    }}
                    onClick={() => {
                      if (treeSelectionMode) { handleToggleTreeSelection(selKey('COBOL', prog)); return }
                      setSelectedProgramCtx(prog)
                      if (svc) setSelectedServiceId(svc.id)
                      else if (canAdd) handleAddProgramToService(selectedServiceId, prog, progData?.loc || 0)
                    }}
                    title={treeSelectionMode ? (selActive ? 'Click to deselect' : 'Click to add to selection') : svc ? `Part of ${svc.name} · right-click to view` : canAdd ? `Click or drag to add · right-click to view` : 'Drag onto a service card to assign · right-click to view'}
                  >
                    <div className="dc-tree-indent depth-2" />
                    {treeSelectionMode && (
                      <span className={`dc-sel-icon${selActive ? ' checked' : ''}`}>
                        {selActive ? <CheckCircle2 size={11} /> : <Circle size={11} />}
                      </span>
                    )}
                    <FileText size={11} className="dc-tree-leaf-ico cobol" />
                    <span className="dc-tree-leaf-lbl">{prog}</span>
                    {progData?.loc > 0 && !svc && (
                      <span className="dc-tree-leaf-loc">{progData.loc}L</span>
                    )}
                    {svc && (
                      <span className="dc-tree-svc-tag">
                        {svc.name.replace('ManagementService','MgmtSvc').replace('ProcessingService','ProcSvc').substring(0, 9)}
                      </span>
                    )}
                    {canAdd && !treeSelectionMode && <span className="dc-tree-add-ico">+</span>}
                  </button>
                )
              })()
            )
          })}

          {/* Copybooks */}
          {allCopybooks.length > 0 && <>
            <TreeGroupHdr id="sec-copybooks" depth={1} icon={FileText}
              label="Copybooks" count={allCopybooks.length} />
            {isExp('sec-copybooks') && allCopybooks.filter(c => matchSearch(c.name)).map(cb => (
              <div
                key={`cb-${cb.name}`}
                className="dc-tree-leaf depth-2"
                onContextMenu={e => handleTreeContextMenu(e, { name: cb.name, displayName: cb.displayName || cb.name, path: cb.path || null, type: 'copybook' })}
                title="Right-click to view"
              >
                <div className="dc-tree-indent depth-2" />
                <FileText size={11} className="dc-tree-leaf-ico cobol" style={{ opacity: 0.6 }} />
                <span className="dc-tree-leaf-lbl">{cb.displayName || cb.name}</span>
                <span className="dc-tree-type-tag copybook">CPY</span>
              </div>
            ))}
          </>}

          {/* JCL */}
          {allJcl.length > 0 && <>
            <TreeGroupHdr id="sec-jcl" depth={1} icon={Cpu}
              label="JCL Jobs" count={allJcl.length} />
            {isExp('sec-jcl') && allJcl.filter(j => matchSearch(j.name)).map(j => {
              const canAdd = !isReal && selectedServiceId &&
                manualServices.some(s => s.id === selectedServiceId)
              return (
                <button
                  key={`jcl-${j.name}`}
                  className={`dc-tree-leaf depth-2${canAdd ? ' addable' : ''} draggable-item`}
                  draggable={!isReal}
                  onDragStart={e => {
                    e.dataTransfer.setData('dc-asset', JSON.stringify({ name: j.name, type: 'JCL', loc: 0 }))
                    e.dataTransfer.effectAllowed = 'copy'
                  }}
                  onClick={() => canAdd && handleAddProgramToService(selectedServiceId, j.name, 0)}
                  onContextMenu={e => handleTreeContextMenu(e, { name: j.name, displayName: j.displayName || j.name, path: j.path || null, type: 'JCL' })}
                  title={canAdd ? 'Click or drag to add · right-click to view' : 'Drag onto a service card to assign · right-click to view'}
                >
                  <div className="dc-tree-indent depth-2" />
                  <FileText size={11} className="dc-tree-leaf-ico" style={{ color: '#f59e0b' }} />
                  <span className="dc-tree-leaf-lbl">{j.displayName || j.name}</span>
                  <span className="dc-tree-type-tag jcl">JCL</span>
                  {canAdd && <span className="dc-tree-add-ico">+</span>}
                </button>
              )
            })}
          </>}

          {/* Data Assets */}
          {allDataAssets.length > 0 && <>
            <TreeGroupHdr id="sec-data" depth={1} icon={Share2}
              label="Data Assets" count={allDataAssets.length} />
            {isExp('sec-data') && allDataAssets.filter(d => matchSearch(d.name)).map(d => (
              <div key={`da-${d.name}`} className="dc-tree-leaf depth-2">
                <div className="dc-tree-indent depth-2" />
                <FileText size={11} className="dc-tree-leaf-ico" style={{ color: '#a78bfa' }} />
                <span className="dc-tree-leaf-lbl">{d.name}</span>
                <span className="dc-tree-type-tag data">{d.type?.substring(0, 4)}</span>
              </div>
            ))}
          </>}

          {/* Generated Java */}
          <TreeGroupHdr id="sec-java" depth={1} icon={Code2}
            label="Generated Java" count={allJavaClasses.length} />
          {isExp('sec-java') && allJavaClasses.filter(c => matchSearch(c)).map(cls => {
            const svc = svcForJava(cls)
            return (
              (() => {
                const canAdd = !svc && selectedServiceId &&
                  manualServices.some(s => s.id === selectedServiceId)
                const selActive = isSel('Java', cls)
                return (
                  <button
                    key={`java-${cls}`}
                    className={`dc-tree-leaf depth-2${svc?.id === selectedServiceId ? ' linked-sel' : ''}${canAdd ? ' addable' : ''}${!svc && !treeSelectionMode ? ' draggable-item' : ''}${selActive ? ' sel-active' : ''}`}
                    draggable={!svc && !treeSelectionMode}
                    onDragStart={e => {
                      if (!svc && !treeSelectionMode) {
                        e.dataTransfer.setData('dc-asset', JSON.stringify({ name: cls, type: 'Java', loc: 0 }))
                        e.dataTransfer.effectAllowed = 'copy'
                      }
                    }}
                    onClick={() => {
                      if (treeSelectionMode) { handleToggleTreeSelection(selKey('Java', cls)); return }
                      if (svc) setSelectedServiceId(svc.id)
                      else if (canAdd) handleAddJavaToService(selectedServiceId, cls)
                    }}
                    title={treeSelectionMode ? (selActive ? 'Click to deselect' : 'Click to add to selection') : svc ? `Part of ${svc.name}` : canAdd ? `Click or drag to add to selected service` : 'Drag onto a service card to assign'}
                  >
                    <div className="dc-tree-indent depth-2" />
                    {treeSelectionMode && (
                      <span className={`dc-sel-icon${selActive ? ' checked' : ''}`}>
                        {selActive ? <CheckCircle2 size={11} /> : <Circle size={11} />}
                      </span>
                    )}
                    <FileText size={11} className="dc-tree-leaf-ico java" />
                    <span className="dc-tree-leaf-lbl">{cls}</span>
                    {svc && (
                      <span className="dc-tree-svc-tag java">
                        {svc.name.replace('ManagementService','MgmtSvc').replace('ProcessingService','ProcSvc').substring(0, 9)}
                      </span>
                    )}
                    {canAdd && !treeSelectionMode && <span className="dc-tree-add-ico">+</span>}
                  </button>
                )
              })()
            )
          })}
        </>}

        {/* ── SERVICE GROUPS ─────────────────────────────────── */}
        <TreeSectionHdr id="sec-svcgroups" icon={Package} label="Service Groups" />

        {isExp('sec-svcgroups') && <>
          {/* Draft Target Services */}
          <TreeGroupHdr id="sec-proposed" depth={1} icon={Box}
            label="Draft" count={proposedSvcs.length} />
          {isExp('sec-proposed') && proposedSvcs.filter(s => matchSearch(s.name)).map(svc => {
            const effStatus = serviceStatuses[svc.id] || svc.status
            const isSelected = svc.id === selectedServiceId
            const svcNodeId = `svc-node-${svc.id}`
            return (
              <div key={svc.id}>
                <button
                  className={`dc-tree-svc-item depth-2${isSelected ? ' selected' : ''}${svc.cohesion === 'low' ? ' warn' : ''}`}
                  onClick={() => setSelectedServiceId(svc.id)}
                >
                  <div className="dc-tree-indent depth-2" />
                  <ChevronRight
                    size={10}
                    className={`dc-tree-chev inline ${isExp(svcNodeId) ? 'open' : ''}`}
                    onClick={e => { e.stopPropagation(); toggleTree(svcNodeId) }}
                  />
                  <Box size={12} className={`dc-tree-svc-ico ${effStatus}`} />
                  <span className="dc-tree-svc-lbl">
                    {svc.name.replace('ManagementService','MgmtSvc').replace('ProcessingService','ProcSvc')}
                  </span>
                  <span className={`dc-tree-status-dot ${effStatus}`} />
                  {svc.cohesion === 'low' && <AlertTriangle size={9} className="dc-tree-warn-ico" />}
                </button>
                {isExp(svcNodeId) && (
                  <div className="dc-tree-svc-children">
                    {/* Show one row per member. For MI-derived services members[] has
                        Java class names with m.cobol = the COBOL source they came from.
                        We show the Java name as primary and the COBOL origin as a small
                        secondary hint — one row per logical asset, no CBL+JVA duplication. */}
                    {(svc.members?.length > 0
                      ? svc.members
                      : (svc.cobolOrigins || []).map(o => ({ name: o, cobol: o }))
                    ).slice(0, 5).map(m => (
                      <div key={m.name} className="dc-tree-leaf depth-3">
                        <div className="dc-tree-indent depth-3" />
                        <FileText size={10} className="dc-tree-leaf-ico java" />
                        <span className="dc-tree-leaf-lbl sm">{m.name.substring(0, 20)}</span>
                        {m.cobol && m.cobol !== m.name && (
                          <span className="dc-tree-leaf-cobol-hint" title={`From COBOL: ${m.cobol}`}>
                            {m.cobol.substring(0, 8)}
                          </span>
                        )}
                        <span className="dc-tree-type-tag java">JVA</span>
                      </div>
                    ))}
                    {(svc.members?.length || 0) > 5 && (
                      <div className="dc-tree-leaf depth-3 muted">
                        <div className="dc-tree-indent depth-3" />
                        <span className="dc-tree-leaf-lbl sm">+{svc.members.length - 5} more…</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )
          })}

          {/* Confirmed */}
          <TreeGroupHdr id="sec-approved-svc" depth={1} icon={CheckCircle2}
            label="Confirmed" count={approvedSvcs.length} />
          {isExp('sec-approved-svc') && approvedSvcs.map(svc => (
            <button
              key={svc.id}
              className={`dc-tree-svc-item depth-2 approved${svc.id === selectedServiceId ? ' selected' : ''}`}
              onClick={() => setSelectedServiceId(svc.id)}
            >
              <div className="dc-tree-indent depth-2" />
              <div className="dc-tree-indent-gap" />
              <CheckCircle2 size={12} className="dc-tree-svc-ico approved" />
              <span className="dc-tree-svc-lbl">
                {svc.name.replace('ManagementService','MgmtSvc')}
              </span>
              <span className="dc-tree-status-dot approved" />
            </button>
          ))}

          {/* Shared / Common */}
          <TreeGroupHdr id="sec-common-svc" depth={1} icon={Share2}
            label="Shared / Common" count={common.length} />
          {isExp('sec-common-svc') && common.map(c => (
            <button key={c.id} className="dc-tree-svc-item depth-2 common">
              <div className="dc-tree-indent depth-2" />
              <div className="dc-tree-indent-gap" />
              <Share2 size={12} className="dc-tree-svc-ico common" />
              <span className="dc-tree-svc-lbl">{c.name}</span>
            </button>
          ))}

          {/* Unassigned */}
          <TreeGroupHdr id="sec-disc-svc" depth={1} icon={AlertCircle}
            label="Unassigned" count={disconnected.length} />
          {isExp('sec-disc-svc') && disconnected.map(d => {
            const resolved = !!discResolutions[d.id]
            return (
              <button key={d.id} className={`dc-tree-svc-item depth-2 disc${resolved ? ' resolved' : ''}`}>
                <div className="dc-tree-indent depth-2" />
                <div className="dc-tree-indent-gap" />
                {resolved
                  ? <CheckCircle2 size={12} className="dc-tree-svc-ico resolved" />
                  : <AlertTriangle size={12} className="dc-tree-svc-ico disc" />
                }
                <span className="dc-tree-svc-lbl">{d.name}</span>
              </button>
            )
          })}
        </>}
      </aside>
    )
  }

  // ── WORKBENCH — CENTER PANEL (Service Builder) ───────────────────────────────

  const renderServiceBuilder = () => {
    // ── Step 12: domain-filtered service lists for card display ──────────────
    // NOTE: readiness / approve / finalize logic ALWAYS uses the full `services`
    // and `manualServices` arrays. These filtered lists are for display only.
    const domainFilteredManualSvcs = activeDomPrograms
      ? manualServices.filter(svc =>
          !svc.cobolOrigins?.length ||
          svc.cobolOrigins.some(p => activeDomPrograms.has(p))
        )
      : manualServices

    // Domain-derived candidates filtered to the active domain view
    const domainFilteredDomainDerived = activeDomPrograms
      ? activeDomainDerived.filter(svc =>
          !svc.cobolOrigins?.length ||
          svc.cobolOrigins.some(p => activeDomPrograms.has(p))
        )
      : activeDomainDerived

    if (isNoMi || isNoCa) {
      const hasCR = !!upstreamJobs['code_refactor']
      const selectedManualSvc = manualServices.find(s => s.id === selectedServiceId)

      // Toolbar summary: prefer domain candidate count when domain work exists
      const totalDomainDerivedCount = activeDomainDerived.length
      const totalManualCount        = manualServices.length

      return (
        <div className="dc-builder">
          {/* Toolbar — always present in manual mode */}
          <div className="dc-builder-toolbar">
            <button
              className="dc-builder-cmd"
              onClick={() => { if (!newSvcDraft) setNewSvcDraft({ name: '', domain: '' }) }}
              title="Create a new service boundary"
            >
              <Plus size={12} />
              New Service
            </button>
            <div className="dc-builder-toolbar-sep" />
            <span className="dc-builder-summary">
              {hasDomainWork && totalDomainDerivedCount > 0
                ? <>
                    <span className="dc-builder-domain-tag">{totalDomainDerivedCount} from domains</span>
                    {totalManualCount > 0 && <>&nbsp;·&nbsp;<strong>{totalManualCount} manual</strong></>}
                  </>
                : (manualServices.length === 0
                  ? 'Manual mode — click New Service to start'
                  : `${manualServices.length} service${manualServices.length !== 1 ? 's' : ''} · ${manualServices.reduce((n, s) => n + (s.members?.length || 0), 0)} members`
                )
              }
            </span>
          </div>

          <div className="dc-builder-scroll">

            {/* Loading: shown while auto-generating graph proposals */}
            {dsGenerating && manualServices.length === 0 && (
              <div className="dc-builder-manual-onboard">
                <div className="dc-builder-manual-onboard-ico">
                  <Loader2 size={28} className="dc-spin" />
                </div>
                <div className="dc-builder-manual-onboard-body">
                  <strong>Analyzing application structure...</strong>
                  <p>
                    Clustering programs by shared data fields, call patterns, and
                    dataset access. AI is naming the proposed service domains.
                  </p>
                </div>
              </div>
            )}

            {/* Onboarding: shown only when there's no domain work AND nothing created yet AND not generating */}
            {manualServices.length === 0 && !newSvcDraft && !hasDomainWork && !dsGenerating && (
              <div className="dc-builder-manual-onboard">
                <div className="dc-builder-manual-onboard-ico">
                  <Package size={28} />
                </div>
                <div className="dc-builder-manual-onboard-body">
                  <strong>Shape service boundaries manually</strong>
                  <p>
                    {isNoCa
                      ? <>No upstream analysis found. Define service boundaries from scratch, or run <strong>Code Analysis</strong> first to populate the source inventory.</>
                      : hasCR
                        ? <>Code Refactor is complete. The source inventory on the left shows known programs — click items to add them to a service. Or run <strong>Monolith Identifier</strong> for AI-driven proposals.</>
                        : <>Run <strong>Monolith Identifier</strong> for AI proposals, or build boundaries manually now using the source inventory on the left.</>
                    }
                  </p>
                  <div className="dc-builder-manual-onboard-actions">
                    <button
                      className="dc-action-btn primary"
                      onClick={() => setNewSvcDraft({ name: '', domain: '' })}
                    >
                      <Plus size={13} /> Create First Service
                    </button>
                    {/* <button
                      className="dc-action-btn secondary"
                      onClick={() => setActiveTab('automation')}
                    >
                      <GitBranch size={13} /> View Pipeline
                    </button> */}{/* commented out — Automation Flow tab disabled */}
                  </div>
                </div>
              </div>
            )}

            {/* ── Domain-derived service candidates ─────────────────────────────
                Each domain from the Domains tab becomes a draft service candidate
                here. Users can promote them to editable services or dismiss them. */}
            {hasDomainWork && domainFilteredDomainDerived.length > 0 && (
              <>
                <div className="dc-domain-section-hdr">
                  <Layers size={11} />
                  <span>Suggested from your groups</span>
                  <span className="dc-domain-section-hint">
                    Based on your domain groupings — promote to keep, or build services manually
                  </span>
                </div>

                {domainFilteredDomainDerived.map(svc => {
                  const isExpanded  = svc.id === selectedServiceId
                  const handleToggle = () => setSelectedServiceId(isExpanded ? null : svc.id)
                  const memberCount  = svc.members?.length || 0
                  const hasPrograms  = svc.cobolOrigins.length > 0

                  return (
                    <div
                      key={svc.id}
                      className={['dc-svc-card-manual dc-svc-card-domain-derived proposed', isExpanded ? 'expanded' : ''].filter(Boolean).join(' ')}
                    >
                      <div className="dc-svc-card-led proposed" />
                      <div className="dc-svc-card-inner">

                        {/* Header */}
                        <div className="dc-svc-card-header" onClick={handleToggle} role="button" tabIndex={0} onKeyDown={e => e.key === 'Enter' && handleToggle()}>
                          <div className="dc-svc-card-hdr-left">
                            <ChevronRight size={12} className={`dc-svc-chevron${isExpanded ? ' open' : ''}`} />
                            <div className="dc-svc-card-title">
                              <span className="dc-svc-card-name">{svc.name}</span>
                              {memberCount > 0 && (
                                <span className="dc-svc-card-meta-line">{memberCount} member{memberCount !== 1 ? 's' : ''}</span>
                              )}
                            </div>
                          </div>
                          <div className="dc-svc-card-hdr-right" onClick={e => e.stopPropagation()}>
                            {svc.pinned && <span className="dc-domain-pinned-badge" title="Pinned domain">📌</span>}
                            <span className="dc-svc-source-badge domain">Domain</span>
                            <button
                              className="dc-svc-btn-confirm"
                              onClick={e => { e.stopPropagation(); handlePromoteDomainService(svc) }}
                              title="Promote to an editable service boundary"
                            >
                              <Plus size={10} /> Use as Service
                            </button>
                          </div>
                        </div>

                        {/* Summary: COBOL origins / members */}
                        {isExpanded && (
                        <div className="dc-svc-card-summary" onClick={e => e.stopPropagation()}>
                          {hasPrograms ? (
                            <div className="dc-svc-member-chips">
                              {svc.cobolOrigins.map((p, i) => (
                                <div key={i} className="dc-svc-member-chip cobol">
                                  <span className="dc-chip-type-badge cobol">CBL</span>
                                  <span className="dc-chip-name">{p}</span>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="dc-svc-drop-zone">
                              <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                                {memberCount} domain member{memberCount !== 1 ? 's' : ''} — source programs not yet resolved
                              </span>
                            </div>
                          )}
                        </div>
                        )}

                        {/* Expanded details */}
                        {isExpanded && (
                          <div className="dc-svc-card-details" onClick={e => e.stopPropagation()}>
                            {svc.businessHint && svc.businessHint !== svc.name && (
                              <div className="dc-svc-card-desc">{svc.businessHint}</div>
                            )}
                            <div className="dc-svc-card-actions">
                              <button
                                className="dc-svc-act-btn danger"
                                onClick={() => setDismissedMiIds(prev => new Set([...prev, svc.id]))}
                                title="Dismiss this domain candidate"
                              >
                                <X size={11} /> Dismiss
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )
                })}
              </>
            )}

            {/* Inline new-service creation form */}
            {newSvcDraft !== null && (
              <div className="dc-svc-create-form">
                <div className="dc-svc-create-title">
                  <Package size={13} />
                  <span>New Service</span>
                </div>
                <div className="dc-svc-create-fields">
                  <input
                    className="dc-svc-create-input"
                    placeholder="Service name (e.g. CustomerManagementService)"
                    value={newSvcDraft.name}
                    onChange={e => setNewSvcDraft(p => ({ ...p, name: e.target.value }))}
                    onKeyDown={e => { if (e.key === 'Enter') handleCreateService(); if (e.key === 'Escape') setNewSvcDraft(null) }}
                    autoFocus
                  />
                  <input
                    className="dc-svc-create-input"
                    placeholder="Domain / capability (e.g. Customer Management)"
                    value={newSvcDraft.domain}
                    onChange={e => setNewSvcDraft(p => ({ ...p, domain: e.target.value }))}
                    onKeyDown={e => { if (e.key === 'Enter') handleCreateService(); if (e.key === 'Escape') setNewSvcDraft(null) }}
                  />
                </div>
                <div className="dc-svc-create-actions">
                  <button
                    className="dc-svc-create-btn primary"
                    onClick={handleCreateService}
                    disabled={!newSvcDraft.name?.trim()}
                  >
                    <Plus size={11} /> Create Service
                  </button>
                  <button className="dc-svc-create-btn" onClick={() => setNewSvcDraft(null)}>
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {/* Manual service cards — Step 12: filtered by active domain */}
            {domainFilteredManualSvcs.map(svc => {
              const effStatus       = serviceStatuses[svc.id] || svc.status
              const isExpanded      = svc.id === selectedServiceId
              const alreadyApproved = effStatus === 'approved'
              const isDragTarget    = dragOverSvcId === svc.id
              const isEmpty         = !svc.members || svc.members.length === 0
              const memberCount     = svc.members?.length || 0
              const handleToggle    = () => setSelectedServiceId(isExpanded ? null : svc.id)

              return (
                <div
                  key={svc.id}
                  className={['dc-svc-card-manual', effStatus, isExpanded ? 'expanded' : '', isDragTarget ? 'drag-over' : ''].filter(Boolean).join(' ')}
                  onDragOver={e => { e.preventDefault(); setDragOverSvcId(svc.id) }}
                  onDragEnter={e => { e.preventDefault(); setDragOverSvcId(svc.id) }}
                  onDragLeave={e => { if (!e.relatedTarget || !e.currentTarget.contains(e.relatedTarget)) setDragOverSvcId(null) }}
                  onDrop={e => handleDrop(svc.id, e)}
                >
                  <div className={`dc-svc-card-led ${effStatus}`} />
                  <div className="dc-svc-card-inner">

                    {/* Row 1: Header */}
                    <div className="dc-svc-card-header" onClick={handleToggle} role="button" tabIndex={0} onKeyDown={e => e.key === 'Enter' && handleToggle()}>
                      <div className="dc-svc-card-hdr-left">
                        <ChevronRight size={12} className={`dc-svc-chevron${isExpanded ? ' open' : ''}`} />
                        <div className="dc-svc-card-title">
                          <span className="dc-svc-card-name">{svc.name}</span>
                          {!isEmpty && (
                            <span className="dc-svc-card-meta-line">{memberCount} class{memberCount !== 1 ? 'es' : ''}</span>
                          )}
                        </div>
                      </div>
                      <div className="dc-svc-card-hdr-right" onClick={e => e.stopPropagation()}>
                        {alreadyApproved && <CheckCircle2 size={12} className="dc-svc-card-hdr-ico approved" />}
                        <span className={`dc-svc-source-badge ${svc.promotedFromDomainId ? 'domain' : 'manual'}`}>
                          {svc.promotedFromDomainId ? 'Domain' : 'Manual'}
                        </span>
                        {/* Status chip only when confirmed — buttons communicate actionable states */}
                        {alreadyApproved && (
                          <button
                            className={`dc-status-chip ${effStatus}`}
                            onClick={e => { e.stopPropagation(); handleUnconfirmService(svc.id) }}
                            title="Click to unconfirm this service"
                          >
                            {statusLabel(effStatus)}
                          </button>
                        )}
                        {!alreadyApproved && (!isApproved || isStaleFinalised) && (
                          <button
                            className="dc-svc-btn-confirm"
                            onClick={e => { e.stopPropagation(); handleApproveService(svc.id) }}
                            title="Confirm this service boundary"
                          >
                            <CheckCircle2 size={10} /> Confirm
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Row 2: Summary — visible when expanded */}
                    {isExpanded && (
                    <div className="dc-svc-card-summary" onClick={e => e.stopPropagation()}>
                      {isEmpty ? (
                        <div className={`dc-svc-drop-zone${isDragTarget ? ' drag-active' : ''}`}>
                          <Plus size={13} className="dc-svc-drop-ico" />
                          <span>Drag source assets here, or click assets in the tree to add</span>
                        </div>
                      ) : (
                        <div className={`dc-svc-member-chips${isDragTarget ? ' drag-active' : ''}`}>
                          {svc.members.map((m, i) => {
                            const mType = getMemberType(m)
                            const typeKey = mType.toLowerCase()
                            return (
                              <div key={i} className={`dc-svc-member-chip ${typeKey}`}>
                                <span className={`dc-chip-type-badge ${typeKey}`}>
                                  {mType === 'Java' ? 'JVA' : mType === 'COBOL' ? 'CBL' : mType.substring(0, 3)}
                                </span>
                                <span className="dc-chip-name">{m.name}</span>
                                {m.loc > 0 && <span className="dc-chip-loc">{m.loc >= 1000 ? `${(m.loc/1000).toFixed(1)}k` : m.loc}L</span>}
                                <button className="dc-chip-remove" onClick={e => { e.stopPropagation(); handleRemoveMember(svc.id, m.name) }} title={`Remove ${m.name}`}>
                                  <X size={9} />
                                </button>
                              </div>
                            )
                          })}
                          {isDragTarget && <div className="dc-chip-drop-hint"><Plus size={11} /> Drop to add</div>}
                        </div>
                      )}
                    </div>
                    )}

                    {/* Row 3: Expanded details */}
                    {isExpanded && (
                      <div className="dc-svc-card-details" onClick={e => e.stopPropagation()}>
                        <div className="dc-svc-card-actions">
                          <button className="dc-svc-act-btn danger" onClick={() => handleDeleteService(svc.id)} title="Delete this service">
                            <Trash2 size={11} /> Delete
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )
            })}

            <div className="dc-builder-bottom-pad" />
          </div>
        </div>
      )
    }

    // MI-loaded path — proposals shown as read-only reference cards;
    // user can create additional manual services alongside them.
    const miServices     = buildServicesFromMI(decompositionData, modularityData, couplingData, sourceInventory?.classMapping, sourceInventory?.programs).filter(s => !dismissedMiIds.has(s.id))
    const dismissedCount = dismissedMiIds.size
    const manualCount    = domainFilteredManualSvcs.length

    // Step 12: domain-filtered MI proposals (display only — readiness/approve use `miServices`)
    const domainFilteredMiSvcs = activeDomPrograms
      ? miServices.filter(svc =>
          !svc.cobolOrigins?.length ||
          svc.cobolOrigins.some(p => activeDomPrograms.has(p))
        )
      : miServices

    // ── Decomposition Lab Phase 1 — compute alternative proposal sets ───────
    // Pure UI engine; reads only `decompositionData` already in state.
    const labResult       = computeProposalSets({ decompositionData })
    const labProposalSets = (labResult?.proposal_sets) || []

    return (
      <div className="dc-builder">
        {/* Command toolbar */}
        <div className="dc-builder-toolbar">
          <button
            className="dc-builder-cmd"
            onClick={() => { if (!newSvcDraft) setNewSvcDraft({ name: '', domain: '' }) }}
            title="Create a new service boundary"
          >
            <Plus size={12} />
            New Service
          </button>
          <div className="dc-builder-toolbar-sep" />
          <span className="dc-builder-summary">
            {services.length > 0
              ? <>
                  {services.length} service{services.length !== 1 ? 's' : ''}
                  {approvedCount > 0 && <>&nbsp;·&nbsp;<span className="dc-builder-ok-txt">{approvedCount} confirmed</span></>}
                  {disconnCount > 0 && (
                    <>&nbsp;·&nbsp;<span className="dc-builder-warn-txt">{disconnCount} unassigned</span></>
                  )}
                </>
              : <span className="dc-builder-summary-empty">No services yet — choose a suggestion below or click New Service</span>
            }
          </span>
        </div>

        <div className="dc-builder-scroll">
          {/* ── Decomposition Lab — Alternative groupings (Phase 1) ──────── */}
          {labProposalSets.length > 0 && (() => {
            // User-facing labels for proposal sets — keeps technical algorithm
            // identifiers off the screen. Provenance stays available via title.
            const SET_DISPLAY = {
              mi_balanced: { label: 'Balanced grouping',     family: 'Structural' },
              god_split:   { label: 'Split god programs',    family: 'Structural' },
              shared_data: { label: 'Data-aligned grouping', family: 'Data'       },
            }
            const displayFor = (set) => SET_DISPLAY[set.id] || { label: set.name, family: 'Structural' }

            return (
              <div className={`dc-lab-panel${labExpanded ? ' expanded' : ''}`}>
                <div className="dc-lab-panel-hdr-row">
                  <button
                    className="dc-lab-panel-hdr"
                    onClick={() => setLabExpanded(v => !v)}
                    aria-expanded={labExpanded}
                  >
                    <ChevronRight size={11} className={`dc-lab-chev${labExpanded ? ' open' : ''}`} />
                    <Layers size={11} className="dc-lab-ico" />
                    <span className="dc-lab-title">Alternative groupings</span>
                    <span className="dc-lab-count">{labProposalSets.length} suggested</span>
                  </button>
                  <HelpPopover
                    placement="bottom-right"
                    title="Alternative groupings"
                    body="Different AI-suggested ways to organise this application into services. Each grouping uses a different algorithm — structural, balanced, or data-aligned. Applying one adds its service cards to the workbench below as drafts. Drafts are not finalised until you confirm each card individually."
                  />
                  <span className="dc-lab-hint">Apply here, then confirm below</span>
                </div>
                {labExpanded && (
                  <div className="dc-lab-panel-body">
                    <div className="dc-lab-intro">
                      Different AI-suggested ways to group this application into services. <strong>Apply</strong> adds the grouping to the workbench below as draft service cards — it does <strong>not</strong> finalise anything. Review each draft and confirm the ones you want to keep.
                    </div>
                    {labProposalSets.map(set => {
                      const disp = displayFor(set)
                      // Phase 1.x — per-set duplicate signal so Apply can be
                      // disabled when nothing new would be added.
                      const { newCount, duplicateCount } = filterNewProposalServices(set, services)
                      const allAlreadyPresent = newCount === 0
                      return (
                        <div key={set.id} className={`dc-lab-set${allAlreadyPresent ? ' already-present' : ''}`}>
                          <div className="dc-lab-set-hdr">
                            <div className="dc-lab-set-title">
                              <span className="dc-lab-set-name">{disp.label}</span>
                              <span
                                className={`dc-lab-set-badge family-${(set.algorithm_family || 'structural').toLowerCase()}`}
                                title={`Algorithm: ${set.algorithm}`}
                              >
                                {disp.family}
                              </span>
                              <span className="dc-lab-set-count">
                                {set.services.length} service{set.services.length !== 1 ? 's' : ''}
                                {duplicateCount > 0 && newCount > 0 && (
                                  <> · <span className="dc-lab-set-newcount">{newCount} new</span></>
                                )}
                              </span>
                              {allAlreadyPresent && (
                                <span className="dc-lab-already-tag" title="This grouping is already reflected in the workbench below">
                                  <CheckCircle2 size={9} /> Already shown below
                                </span>
                              )}
                            </div>
                            <button
                              className="dc-lab-apply-btn"
                              onClick={() => handleApplyProposalSet(set)}
                              disabled={allAlreadyPresent}
                              title={
                                allAlreadyPresent
                                  ? 'This grouping is already shown in the workbench below'
                                  : duplicateCount > 0
                                    ? `Add ${newCount} new service${newCount !== 1 ? 's' : ''} to the workbench (${duplicateCount} already there will be skipped)`
                                    : `Add ${set.services.length} service${set.services.length !== 1 ? 's' : ''} from "${disp.label}" to the workbench as draft cards`
                              }
                            >
                              <Plus size={10} /> Apply
                            </button>
                          </div>
                          {set.rationale && (
                            <div className="dc-lab-set-rationale">{set.rationale}</div>
                          )}
                          <div className="dc-lab-set-services">
                            {set.services.slice(0, 6).map(s => (
                              <span key={s.id} className="dc-lab-svc-chip" title={s.rationale || s.proposed_name}>
                                {s.proposed_name}
                                <span className="dc-lab-svc-chip-meta">
                                  {(s.cobol_origins || []).length}
                                </span>
                              </span>
                            ))}
                            {set.services.length > 6 && (
                              <span className="dc-lab-svc-chip more">+{set.services.length - 6} more</span>
                            )}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            )
          })()}

          {/* ── Inline creation form ─────────────────────────────────────── */}
          {newSvcDraft !== null && (
            <div className="dc-svc-create-form">
              <div className="dc-svc-create-title">
                <Package size={13} />
                <span>New Service</span>
              </div>
              <div className="dc-svc-create-fields">
                <input
                  className="dc-svc-create-input"
                  placeholder="Service name (e.g. CustomerManagementService)"
                  value={newSvcDraft.name}
                  onChange={e => setNewSvcDraft(p => ({ ...p, name: e.target.value }))}
                  onKeyDown={e => { if (e.key === 'Enter') handleCreateService(); if (e.key === 'Escape') setNewSvcDraft(null) }}
                  autoFocus
                />
                <input
                  className="dc-svc-create-input"
                  placeholder="Domain / capability (e.g. Customer Management)"
                  value={newSvcDraft.domain}
                  onChange={e => setNewSvcDraft(p => ({ ...p, domain: e.target.value }))}
                  onKeyDown={e => { if (e.key === 'Enter') handleCreateService(); if (e.key === 'Escape') setNewSvcDraft(null) }}
                />
              </div>
              <div className="dc-svc-create-actions">
                <button
                  className="dc-svc-create-btn primary"
                  onClick={handleCreateService}
                  disabled={!newSvcDraft.name?.trim()}
                >
                  <Plus size={11} /> Create Service
                </button>
                <button className="dc-svc-create-btn" onClick={() => setNewSvcDraft(null)}>
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* ── Your services — services you have added or promoted ── */}
          {domainFilteredManualSvcs.length > 0 && (
            <div className="dc-section-heading-yours">
              <span>Your services</span>
              {approvedCount > 0 && approvedCount === domainFilteredManualSvcs.length && (
                <span className="dc-section-heading-yours-done">
                  <CheckCircle2 size={10} /> All confirmed
                </span>
              )}
            </div>
          )}

          {/* ── Manual services — Step 12: filtered by active domain ── */}
          {domainFilteredManualSvcs.map(svc => {
            const effStatus       = serviceStatuses[svc.id] || svc.status
            const isExpanded      = svc.id === selectedServiceId
            const alreadyApproved = effStatus === 'approved'
            const isDragTarget    = dragOverSvcId === svc.id
            const isEmpty         = !svc.members || svc.members.length === 0
            const isRenaming      = renamingSvcId === svc.id
            const memberCount     = svc.members?.length || 0
            const handleToggle    = () => { if (isRenaming) return; setSelectedServiceId(isExpanded ? null : svc.id) }

            return (
              <div
                key={svc.id}
                className={['dc-svc-card-manual', effStatus, isExpanded ? 'expanded' : '', isDragTarget ? 'drag-over' : ''].filter(Boolean).join(' ')}
                onDragOver={e => { e.preventDefault(); setDragOverSvcId(svc.id) }}
                onDragEnter={e => { e.preventDefault(); setDragOverSvcId(svc.id) }}
                onDragLeave={e => { if (!e.relatedTarget || !e.currentTarget.contains(e.relatedTarget)) setDragOverSvcId(null) }}
                onDrop={e => handleDrop(svc.id, e)}
              >
                <div className={`dc-svc-card-led ${effStatus}`} />
                <div className="dc-svc-card-inner">

                  {/* Row 1: Header */}
                  <div className="dc-svc-card-header" onClick={handleToggle} role="button" tabIndex={0} onKeyDown={e => e.key === 'Enter' && handleToggle()}>
                    <div className="dc-svc-card-hdr-left">
                      <ChevronRight size={12} className={`dc-svc-chevron${isExpanded ? ' open' : ''}`} />
                      <div className="dc-svc-card-title" onClick={e => isRenaming && e.stopPropagation()}>
                        {isRenaming ? (
                          <input
                            className="dc-svc-rename-input"
                            value={renameValue}
                            autoFocus
                            onChange={e => setRenameValue(e.target.value)}
                            onKeyDown={e => {
                              e.stopPropagation()
                              if (e.key === 'Enter')  handleCommitRename(svc.id)
                              if (e.key === 'Escape') { setRenamingSvcId(null); setRenameValue('') }
                            }}
                            onBlur={() => handleCommitRename(svc.id)}
                            onClick={e => e.stopPropagation()}
                          />
                        ) : (
                          <span className="dc-svc-card-name" onDoubleClick={e => { e.stopPropagation(); handleStartRename(svc) }} title="Double-click to rename">
                            {svc.name}
                          </span>
                        )}
                        {!isEmpty && !isRenaming && (
                          <span className="dc-svc-card-meta-line">{memberCount} class{memberCount !== 1 ? 'es' : ''}</span>
                        )}
                      </div>
                    </div>
                    <div className="dc-svc-card-hdr-right" onClick={e => e.stopPropagation()}>
                      {alreadyApproved && <CheckCircle2 size={12} className="dc-svc-card-hdr-ico approved" />}
                      <span className={`dc-svc-source-badge ${svc.promotedFromDomainId ? 'domain' : 'manual'}`}>
                        {svc.promotedFromDomainId ? 'Domain' : 'Manual'}
                      </span>
                      {alreadyApproved && (
                        <button
                          className={`dc-status-chip ${effStatus}`}
                          onClick={e => { e.stopPropagation(); handleUnconfirmService(svc.id) }}
                          title="Click to unconfirm this service"
                        >
                          {statusLabel(effStatus)}
                        </button>
                      )}
                      {!alreadyApproved && (!isApproved || isStaleFinalised) && (
                        <button className="dc-svc-btn-confirm" onClick={e => { e.stopPropagation(); handleApproveService(svc.id) }} title="Confirm this service boundary">
                          <CheckCircle2 size={10} /> Confirm
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Row 2: Summary — visible when expanded */}
                  {isExpanded && (
                  <div className="dc-svc-card-summary" onClick={e => e.stopPropagation()}>
                    {isEmpty ? (
                      <div className={`dc-svc-drop-zone${isDragTarget ? ' drag-active' : ''}`}>
                        <Plus size={13} className="dc-svc-drop-ico" />
                        <span>Drag source assets here, or click assets in the tree to add</span>
                      </div>
                    ) : (
                      <div className={`dc-svc-member-chips${isDragTarget ? ' drag-active' : ''}`}>
                        {svc.members.map((m, i) => {
                          const mType = getMemberType(m)
                          const typeKey = mType.toLowerCase()
                          return (
                            <div key={i} className={`dc-svc-member-chip ${typeKey}`}>
                              <span className={`dc-chip-type-badge ${typeKey}`}>
                                {mType === 'Java' ? 'JVA' : mType === 'COBOL' ? 'CBL' : mType.substring(0, 3)}
                              </span>
                              <span className="dc-chip-name">{m.name}</span>
                              {m.loc > 0 && <span className="dc-chip-loc">{m.loc >= 1000 ? `${(m.loc/1000).toFixed(1)}k` : m.loc}L</span>}
                              <button className="dc-chip-remove" onClick={e => { e.stopPropagation(); handleRemoveMember(svc.id, m.name) }} title={`Remove ${m.name}`}>
                                <X size={9} />
                              </button>
                            </div>
                          )
                        })}
                        {isDragTarget && <div className="dc-chip-drop-hint"><Plus size={11} /> Drop to add</div>}
                      </div>
                    )}
                  </div>
                  )}

                  {/* Row 3: Expanded details */}
                  {isExpanded && (
                    <div className="dc-svc-card-details" onClick={e => e.stopPropagation()}>
                      <div className="dc-svc-card-actions">
                        <button className="dc-svc-act-btn" onClick={e => { e.stopPropagation(); handleStartRename(svc) }} title="Rename">
                          Rename
                        </button>
                        <button className="dc-svc-act-btn danger" onClick={() => handleDeleteService(svc.id)} title="Delete this service">
                          <Trash2 size={11} /> Delete
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )
          })}

          {/* ── Domain Candidates (from Domains tab) ────────────────────────── */}
          {hasDomainWork && domainFilteredDomainDerived.length > 0 && (
            <>
              <div className="dc-domain-section-hdr">
                <Layers size={11} />
                <span>Domain Candidates</span>
                <span className="dc-domain-section-hint">
                  From your Domains tab — promote to edit or use alongside AI proposals
                </span>
              </div>

              {domainFilteredDomainDerived.map(svc => {
                const isExpanded   = svc.id === selectedServiceId
                const handleToggle = () => setSelectedServiceId(isExpanded ? null : svc.id)
                const memberCount  = svc.members?.length || 0
                const hasPrograms  = svc.cobolOrigins.length > 0

                return (
                  <div
                    key={svc.id}
                    className={['dc-svc-card-manual dc-svc-card-domain-derived proposed', isExpanded ? 'expanded' : ''].filter(Boolean).join(' ')}
                  >
                    <div className="dc-svc-card-led proposed" />
                    <div className="dc-svc-card-inner">
                      <div className="dc-svc-card-header" onClick={handleToggle} role="button" tabIndex={0} onKeyDown={e => e.key === 'Enter' && handleToggle()}>
                        <div className="dc-svc-card-hdr-left">
                          <ChevronRight size={12} className={`dc-svc-chevron${isExpanded ? ' open' : ''}`} />
                          <div className="dc-svc-card-title">
                            <span className="dc-svc-card-name">{svc.name}</span>
                            {memberCount > 0 && (
                              <span className="dc-svc-card-meta-line">{memberCount} member{memberCount !== 1 ? 's' : ''}</span>
                            )}
                          </div>
                        </div>
                        <div className="dc-svc-card-hdr-right" onClick={e => e.stopPropagation()}>
                          {svc.pinned && <span className="dc-domain-pinned-badge" title="Pinned domain">📌</span>}
                          <span className="dc-svc-source-badge domain">Domain</span>
                          <button
                            className="dc-svc-btn-confirm"
                            onClick={e => { e.stopPropagation(); handlePromoteDomainService(svc) }}
                            title="Promote to an editable service boundary"
                          >
                            <Plus size={10} /> Use as Service
                          </button>
                        </div>
                      </div>
                      {isExpanded && (
                      <div className="dc-svc-card-summary" onClick={e => e.stopPropagation()}>
                        {hasPrograms ? (
                          <div className="dc-svc-member-chips">
                            {svc.cobolOrigins.map((p, i) => (
                              <div key={i} className="dc-svc-member-chip cobol">
                                <span className="dc-chip-type-badge cobol">CBL</span>
                                <span className="dc-chip-name">{p}</span>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="dc-svc-drop-zone">
                            <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                              {memberCount} domain member{memberCount !== 1 ? 's' : ''} — source programs not yet resolved
                            </span>
                          </div>
                        )}
                      </div>
                      )}
                      {isExpanded && (
                        <div className="dc-svc-card-details" onClick={e => e.stopPropagation()}>
                          <div className="dc-svc-card-actions">
                            <button
                              className="dc-svc-act-btn primary"
                              onClick={() => handlePromoteDomainService(svc)}
                            >
                              <Plus size={11} /> Promote to Editable Service
                            </button>
                            <button
                              className="dc-svc-act-btn danger"
                              onClick={() => setDismissedMiIds(prev => new Set([...prev, svc.id]))}
                            >
                              <X size={11} /> Dismiss
                            </button>
                          </div>
                          {svc.businessHint && svc.businessHint !== svc.name && (
                            <div className="dc-svc-card-desc">{svc.businessHint}</div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )
              })}
            </>
          )}

          {/* ── Suggested services — AI proposals, read-only reference ── */}
          {domainFilteredMiSvcs.length > 0 && (
            <div className="dc-proposals-section-hdr">
              <Sparkles size={11} />
              <span>Suggested services</span>
              <HelpPopover
                placement="bottom-right"
                title="AI-suggested service boundaries"
                body="These groups were generated by analysing your COBOL programs for shared patterns and business capabilities. They are suggestions only — Confirm the ones you want to keep, or Dismiss ones you don't need."
              />
              <span className="dc-proposals-hdr-hint">suggestions — confirm to keep, dismiss to remove</span>
            </div>
          )}

          {/* Service cards */}
          {services.length === 0 && !newSvcDraft && (
            <div className="dc-builder-no-services">
              No service proposals loaded yet.
            </div>
          )}

          {/* Step 12: no domain-scoped services yet — nudge to All or Domains board */}
          {activeDomPrograms && domainFilteredMiSvcs.length === 0 && domainFilteredManualSvcs.length === 0 && services.length > 0 && (
            <div className="dc-builder-domain-empty">
              No services mapped to this domain yet.
              <button className="dc-builder-domain-empty-link" onClick={() => setSelectedDomainId(null)}>
                View all domains
              </button>
            </div>
          )}

          {/* Hint: no domains saved yet → invite user to define domains first */}
          {domainLoadState === 'ok' && domainList.length === 0 && (
            <div className="dc-domain-hint-banner">
              <Layers size={11} />
              <span>
                No domains defined yet.{' '}
                <button
                  className="dc-domain-hint-link"
                  onClick={() => setDecompositionViewMode('domains')}
                >
                  Go to Domains tab
                </button>
                {' '}to define logical groupings — they'll appear here as service candidates.
              </span>
            </div>
          )}

          {domainFilteredMiSvcs.map(svc => {
            const effStatus       = serviceStatuses[svc.id] || svc.status
            const isExpanded      = svc.id === selectedServiceId
            const alreadyApproved = effStatus === 'approved'
            const memberCount     = svc.members?.length || svc.classes || 0

            const originsAreMapped = (svc.cobolOrigins || []).some(o =>
              !(svc.members || []).some(m => m.name === o || m.name.replace(/\.java$/i, '') === o)
            )
            const handleToggle = () => { setSelectedServiceId(isExpanded ? null : svc.id); setSelectedProgramCtx(null) }

            return (
              <div key={svc.id} className={`dc-svc-card-ai ${effStatus}${isExpanded ? ' expanded' : ''}`}>
                <div className={`dc-svc-card-led ${effStatus}`} />
                <div className="dc-svc-card-inner">

                  {/* Row 1: Header */}
                  <div className="dc-svc-card-header" onClick={handleToggle} role="button" tabIndex={0} onKeyDown={e => e.key === 'Enter' && handleToggle()}>
                    <div className="dc-svc-card-hdr-left">
                      <ChevronRight size={12} className={`dc-svc-chevron${isExpanded ? ' open' : ''}`} />
                      <div className="dc-svc-card-title">
                        <span className="dc-svc-card-name">{svc.name}</span>
                        <span className="dc-svc-card-meta-line">
                          {memberCount} class{memberCount !== 1 ? 'es' : ''}
                          {(svc.loc || 0) > 0 && <>&nbsp;·&nbsp;{svc.loc.toLocaleString()}L</>}
                          {svc.businessHint && <>&nbsp;·&nbsp;{svc.businessHint}</>}
                        </span>
                      </div>
                    </div>
                    <div className="dc-svc-card-hdr-right" onClick={e => e.stopPropagation()}>
                      {svc.cohesion === 'low' && (
                        <HelpPopover
                          placement="bottom-left"
                          title="Low cohesion"
                          body="The classes in this group do not share many dependencies or data flows. This means they may not naturally belong together as one service. Review the member list and consider splitting this boundary before confirming."
                        />
                      )}
                      {alreadyApproved && <CheckCircle2 size={12} className="dc-svc-card-hdr-ico approved" />}
                      <span className="dc-svc-source-badge ai">AI</span>
                      {/* Status chip only when confirmed — Confirm+Dismiss buttons communicate pending states */}
                      {alreadyApproved && (
                        <button
                          className={`dc-status-chip ${effStatus}`}
                          onClick={e => { e.stopPropagation(); handleUnconfirmService(svc.id) }}
                          title="Click to unconfirm this service"
                        >
                          {statusLabel(effStatus)}
                        </button>
                      )}
                      {!alreadyApproved && (
                        <button className="dc-svc-btn-confirm" onClick={e => { e.stopPropagation(); handleApproveService(svc.id) }} title="Include this service in your finalization plan">
                          <CheckCircle2 size={10} /> Confirm
                        </button>
                      )}
                      {!alreadyApproved && (
                        <button className="dc-svc-btn-dismiss" onClick={e => { e.stopPropagation(); handleDismissMiProposal(svc.id) }} title="Dismiss this AI proposal">
                          <X size={10} /> Dismiss
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Row 2: Summary — visible when expanded */}
                  {isExpanded && (
                  <div className="dc-svc-card-summary" onClick={handleToggle}>
                    <div className="dc-svc-summary-chips">
                      {(svc.members || []).slice(0, 3).map((m, i) => (
                        <span key={i} className="dc-svc-summary-chip" title={m.name}>
                          <FileText size={9} className="dc-svc-summary-chip-ico" />
                          {m.name}
                        </span>
                      ))}
                      {memberCount > 3 && <span className="dc-svc-summary-more">+{memberCount - 3} more</span>}
                      {memberCount === 0 && <span className="dc-svc-summary-empty">No members defined</span>}
                    </div>
                    {(originsAreMapped && (svc.cobolOrigins || []).length > 0) || svc.cohesion === 'medium' || svc.cohesion === 'low' ? (
                      <div className="dc-svc-summary-meta">
                        {originsAreMapped && (svc.cobolOrigins || []).slice(0, 2).map(o => (
                          <span key={o} className="dc-svc-origin-tag sm" title={`COBOL source: ${o}`}>{o}</span>
                        ))}
                        {(svc.cohesion === 'medium' || svc.cohesion === 'low') && (
                          <span className={`dc-svc-summary-cohesion ${svc.cohesion}`}>
                            {COHESION_LABEL[svc.cohesion]} cohesion
                          </span>
                        )}
                      </div>
                    ) : null}
                  </div>
                  )}

                  {/* Row 3: Expanded details */}
                  {isExpanded && (
                    <div className="dc-svc-card-details">
                      {(svc.members || []).length > 0 && (
                        <>
                          <div className="dc-svc-member-col-hdrs">
                            <span className="dc-svc-member-col-hdr">Generated Java class</span>
                            <span className="dc-svc-member-col-hdr right">{originsAreMapped ? 'COBOL source · LOC' : 'LOC'}</span>
                          </div>
                          <div className="dc-svc-card-members">
                            {(svc.members || []).map((m, i) => (
                              <div key={i} className="dc-svc-member-row">
                                <div className="dc-svc-member-left">
                                  <FileText size={11} className="dc-svc-member-ico java" />
                                  <span className="dc-svc-member-name">{m.name}</span>
                                </div>
                                <div className="dc-svc-member-right">
                                  {m.cobol && m.cobol !== m.name && (
                                    <span className="dc-svc-member-origin" title={`Derived from COBOL: ${m.cobol}`}>{m.cobol}</span>
                                  )}
                                  {m.loc > 0 && <span className="dc-svc-member-loc">{m.loc}L</span>}
                                  {m.extDeps > 0 && <span className="dc-svc-member-ext">{m.extDeps} ext</span>}
                                </div>
                              </div>
                            ))}
                          </div>
                        </>
                      )}
                      {originsAreMapped && (svc.cobolOrigins || []).length > 2 && (
                        <div className="dc-svc-details-row">
                          <span className="dc-svc-origin-key">All COBOL sources:</span>
                          {(svc.cobolOrigins || []).map(o => (
                            <span key={o} className="dc-svc-origin-tag">{o}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )
          })}

          {/* Outside-the-boundaries — actionable callout when MI ran and programs remain unassigned */}
          {isReal && readiness.unassignedCobolCount > 0 && (
            <div className="dc-unassigned-context">
              <div className="dc-unassigned-context-hdr">
                <Info size={11} className="dc-unassigned-context-ico" />
                <span>
                  {readiness.unassignedCobolCount} source program{readiness.unassignedCobolCount !== 1 ? 's' : ''} are not in any service boundary
                </span>
              </div>
              <p className="dc-unassigned-context-body">
                These are typically shared utilities, batch jobs, or infrastructure
                programs — leaving them unassigned is usually fine.
                To include one, select it in the left inventory tree and drag it into a service above.
              </p>
            </div>
          )}

          {/* Unassigned items */}
          {disconnected.length > 0 && (
            <div className={`dc-builder-disc-section${disconnCount === 0 ? ' all-resolved' : ''}`}>
              <div className="dc-builder-disc-hdr">
                {disconnCount > 0
                  ? <><AlertCircle size={13} /><span>{disconnCount} unassigned item{disconnCount !== 1 ? 's' : ''} — optionally assign before finalizing</span></>
                  : <><CheckCircle2 size={13} /><span>All items assigned ✓</span></>
                }
                {disconnCount > 0 && (
                  <HelpPopover
                    placement="bottom-right"
                    title="Unassigned items"
                    body={
                      <span>
                        The AI analysis found these Java classes but did not place them in any service boundary.
                        They are optional to resolve before finalising.
                        <br /><br />
                        <strong>Assign</strong> — add this class to the currently selected service.<br />
                        <strong>Common</strong> — mark it as shared utility logic, not owned by any one service.<br />
                        <strong>Exclude</strong> — leave it out of generated services entirely.
                      </span>
                    }
                  />
                )}
              </div>
              <div className="dc-builder-disc-list">
                {disconnected.map(d => {
                  const res = discResolutions[d.id]
                  return (
                    <div key={d.id} className={`dc-disc-row${res ? ' resolved' : ''}`}>
                      <div className="dc-disc-row-left">
                        {res
                          ? <CheckCircle2 size={11} className="dc-disc-ico resolved" />
                          : <AlertTriangle size={11} className="dc-disc-ico" />
                        }
                        <span className="dc-disc-name">{d.name}</span>
                        <span className="dc-disc-cobol">{d.cobol}</span>
                      </div>
                      <div className="dc-disc-row-right">
                        {res
                          ? <span className="dc-disc-resolved-badge">✓ {res}</span>
                          : <>
                              <button className="dc-disc-btn" onClick={() => handleResolveDisc(d.id, 'assign')}>Assign</button>
                              <button className="dc-disc-btn" onClick={() => handleResolveDisc(d.id, 'common')}>Common</button>
                              <button className="dc-disc-btn" onClick={() => handleResolveDisc(d.id, 'exclude')}>Exclude</button>
                            </>
                        }
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          <div className="dc-builder-bottom-pad" />
        </div>
      </div>
    )
  } // end renderServiceBuilder

  // ── WORKBENCH — RIGHT PANEL (Signals) ────────────────────────────────────────

  // ── Program Context Panel helper ─────────────────────────────────────────
  const renderProgramContext = (progName) => {
    // Pull AI description from naming_suggestions.json
    // Structure: { programs: [ { program_name, description, suggested_java_name, key_responsibilities: [] } ] }
    const suggestions = namingSuggestionsData?.programs || []
    const progSuggestion = suggestions.find(p =>
      p.program_name === progName ||
      p.program_name?.replace(/\.cbl$/i, '') === progName?.replace(/\.cbl$/i, '')
    )

    // Pull outbound calls from call_graph.json
    // Structure: { edges: [ { source, target, call_type } ], nodes: [ { id, type, ... } ] }
    const outboundEdges = (callGraphData?.edges || []).filter(e =>
      e.source === progName ||
      e.source?.replace(/\.cbl$/i, '') === progName?.replace(/\.cbl$/i, '')
    )
    const dataAccesses = (callGraphData?.nodes || []).filter(n =>
      n.type === 'file' || n.type === 'table' || n.type === 'vsam'
    ).filter(n =>
      outboundEdges.some(e => e.target === n.id)
    )

    // Pull cohesion from modularity_metrics.json
    // Structure: { programs: [ { name, cohesion_class, lcom4_score } ] }
    const modPrograms = modularityData?.programs || modularityData?.classes || []
    const modEntry = modPrograms.find(p =>
      p.name === progName ||
      p.program_name === progName ||
      p.name?.replace(/\.cbl$/i, '') === progName?.replace(/\.cbl$/i, '')
    )

    // Pull fan-out from coupling_metrics.json
    // Structure: { programs: [ { name, fan_out, fan_in } ] }
    const coupPrograms = couplingData?.programs || couplingData?.classes || []
    const coupEntry = coupPrograms.find(p =>
      p.name === progName ||
      p.program_name === progName ||
      p.name?.replace(/\.cbl$/i, '') === progName?.replace(/\.cbl$/i, '')
    )

    return (
      <aside className="dc-signals">
        <div className="dc-signals-header">
          <button
            className="dc-signals-back-btn"
            onClick={() => setSelectedProgramCtx(null)}
            title="Back to service details"
          >
            ← Back
          </button>
          <span className="dc-signals-title">PROGRAM CONTEXT</span>
        </div>
        <div className="dc-signals-body">
          <div className="dc-signals-svc-name">{progName}</div>

          {/* AI Description */}
          {progSuggestion?.description && (
            <div className="dc-signals-sec">
              <span className="dc-signals-lbl">AI Summary</span>
              <p className="dc-signals-ai-desc">{progSuggestion.description}</p>
            </div>
          )}

          {/* Key Responsibilities */}
          {progSuggestion?.key_responsibilities?.length > 0 && (
            <div className="dc-signals-sec">
              <span className="dc-signals-lbl">Key Responsibilities</span>
              <ul className="dc-signals-list">
                {progSuggestion.key_responsibilities.slice(0, 5).map((r, i) => (
                  <li key={i}>{r}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Cohesion + Fan-out stats */}
          {(modEntry || coupEntry) && (
            <div className="dc-signals-summary-row">
              {modEntry?.cohesion_class && (
                <div className="dc-signals-summary-stat">
                  <span className={`dc-signals-summary-num cohesion-${modEntry.cohesion_class}`}>
                    {COHESION_LABEL[modEntry.cohesion_class] || modEntry.cohesion_class}
                  </span>
                  <span className="dc-signals-summary-lbl">cohesion</span>
                </div>
              )}
              {coupEntry?.fan_out > 0 && (
                <div className="dc-signals-summary-stat">
                  <span className="dc-signals-summary-num">{coupEntry.fan_out}</span>
                  <span className="dc-signals-summary-lbl">fan-out</span>
                </div>
              )}
              {coupEntry?.fan_in > 0 && (
                <div className="dc-signals-summary-stat">
                  <span className="dc-signals-summary-num">{coupEntry.fan_in}</span>
                  <span className="dc-signals-summary-lbl">fan-in</span>
                </div>
              )}
            </div>
          )}

          {/* Outbound Calls */}
          {outboundEdges.length > 0 && (
            <div className="dc-signals-sec">
              <span className="dc-signals-lbl">Outbound Calls ({outboundEdges.length})</span>
              <div className="dc-signals-deps">
                {outboundEdges.slice(0, 10).map((e, i) => (
                  <div key={i} className="dc-signals-dep-row">
                    <MoveRight size={10} />
                    <span>{e.target}</span>
                    {e.call_type && <span className="dc-signals-java-type">{e.call_type}</span>}
                  </div>
                ))}
                {outboundEdges.length > 10 && (
                  <div className="dc-signals-dep-row" style={{ opacity: 0.6 }}>
                    +{outboundEdges.length - 10} more
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Data Accesses (tables/files) */}
          {dataAccesses.length > 0 && (
            <div className="dc-signals-sec">
              <span className="dc-signals-lbl">Data Accesses ({dataAccesses.length})</span>
              <div className="dc-signals-deps">
                {dataAccesses.map((n, i) => (
                  <div key={i} className="dc-signals-dep-row">
                    <Database size={10} />
                    <span>{n.id || n.name}</span>
                    {n.type && <span className="dc-signals-java-type">{n.type}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Suggested Java name */}
          {progSuggestion?.suggested_java_name && (
            <div className="dc-signals-sec">
              <span className="dc-signals-lbl">Suggested Java Name</span>
              <span className="dc-signals-val dc-signals-source-tag">{progSuggestion.suggested_java_name}</span>
            </div>
          )}

          {/* No data fallback */}
          {!progSuggestion && outboundEdges.length === 0 && !modEntry && !coupEntry && (
            <div className="dc-signals-guide-hint" style={{ marginTop: 12 }}>
              No AI analysis available for this program yet.
              Run AI Naming and Dependency Mapper to populate this panel.
            </div>
          )}
        </div>
      </aside>
    )
  }

  // ── Step 8: New right-panel renderers for non-services sub-tabs ─────────────

  /** Static guide shown when the Domains board sub-tab is active. */
  const renderDomainsGuidePanel = () => (
    <aside className="dc-signals">
      <div className="dc-signals-guide">
        <div className="dc-signals-guide-hdr">
          <Layers size={16} className="dc-signals-guide-ico" />
          <span>Domain Board</span>
        </div>
        <p className="dc-signals-guide-text">
          Organise COBOL programs into business domains using the board in the centre panel.
          When satisfied, switch to <strong>Services</strong> to define service boundaries.
        </p>
        <div className="dc-signals-guide-steps">
          <div className="dc-signals-guide-step">
            <span className="dc-signals-guide-num">1</span>
            <span>Review AI-proposed domains in the centre panel</span>
          </div>
          <div className="dc-signals-guide-step">
            <span className="dc-signals-guide-num">2</span>
            <span>Drag programs between domain columns to refine groupings</span>
          </div>
          <div className="dc-signals-guide-step">
            <span className="dc-signals-guide-num">3</span>
            <span>Switch to <strong>Services</strong> to define service boundaries</span>
          </div>
        </div>
      </div>
    </aside>
  )

  /**
   * Shown in Graph / List sub-tabs when nothing is selected yet.
   * mode: 'graph' | 'list'
   */
  const renderGraphListNoSelectionPanel = (mode) => (
    <aside className="dc-signals">
      <div className="dc-signals-guide">
        <div className="dc-signals-guide-hdr">
          {mode === 'graph'
            ? <Network     size={16} className="dc-signals-guide-ico" />
            : <ClipboardList size={16} className="dc-signals-guide-ico" />
          }
          <span>{mode === 'graph' ? 'Domain Graph' : 'Asset List'}</span>
        </div>
        <p className="dc-signals-guide-text">
          {mode === 'graph'
            ? 'Click any node in the graph to inspect its domain assignment and asset details here.'
            : 'Click any row in the table to inspect asset details and domain assignment here.'
          }
        </p>
      </div>
    </aside>
  )

  /** Asset-detail panel shown when a node/row is selected in Graph / List sub-tabs. */
  const renderCrossViewAssetPanel = (data) => {
    const ASSET_TYPE_LABELS = {
      COBOL_PROGRAM: 'Program', COPYBOOK: 'Copybook',
      DB2_TABLE: 'DB2 Table', DB2_DCLGEN: 'DB2 DCLGEN',
      VSAM_DATASET: 'VSAM', DATA_FILE: 'File', LOGICAL_FILE: 'File',
      JCL_JOB: 'JCL Job', BMS_MAP: 'BMS Map',
    }
    const typeLabel  = ASSET_TYPE_LABELS[data.assetType] || (data.assetType || 'Asset')
    const color      = data.domainColor || '#475569'
    const hasMetrics = (data.paragraphCount ?? null) !== null ||
                       (data.connectionCount ?? null) !== null ||
                       (data.confidence ?? null) !== null
    return (
      <aside className="dc-signals">
        <div className="dc-signals-header">
          <span className="dc-signals-title">ASSET DETAIL</span>
        </div>
        <div className="dc-signals-body">

          {/* ── Identity ──────────────────────────────────────────────── */}
          <div className="dc-signals-identity">
            <div
              className="dc-signals-svc-name"
              style={{ fontFamily: 'var(--font-mono, monospace)', fontSize: '0.82rem', wordBreak: 'break-all' }}
            >
              {data.name}
            </div>
            <div className="dc-signals-identity-row">
              <span className="dc-svc-source-badge sm-badge">{typeLabel}</span>
              {data.domainName ? (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: '0.74rem', color }}>
                  <span style={{
                    width: 8, height: 8, borderRadius: '50%',
                    background: color, flexShrink: 0, display: 'inline-block',
                  }} />
                  {data.domainName}
                </span>
              ) : (
                <span style={{ fontSize: '0.74rem', color: 'var(--text-muted, #64748b)' }}>Unassigned</span>
              )}
            </div>
          </div>

          {/* ── Metrics ───────────────────────────────────────────────── */}
          {hasMetrics && (
            <div className="dc-signals-sec">
              <span className="dc-signals-lbl">Metrics</span>
              <div className="dc-signals-signal-rows">
                {(data.paragraphCount ?? null) !== null && (
                  <div className="dc-signals-signal-row">
                    <span className="dc-signals-signal-key">Paragraphs</span>
                    <span className="dc-signals-signal-val">{data.paragraphCount}</span>
                  </div>
                )}
                {(data.connectionCount ?? null) !== null && (
                  <div className="dc-signals-signal-row">
                    <span className="dc-signals-signal-key">Connections</span>
                    <span className="dc-signals-signal-val">{data.connectionCount}</span>
                  </div>
                )}
                {(data.confidence ?? null) !== null && (
                  <div className="dc-signals-signal-row">
                    <span className="dc-signals-signal-key">Confidence</span>
                    <span className="dc-signals-signal-val">{Math.round(data.confidence * 100)}%</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── Hint when unassigned ──────────────────────────────────── */}
          {!data.domainName && (
            <div className="dc-signals-guide-hint" style={{ marginTop: 12 }}>
              This asset is not assigned to any domain. Switch to <strong>Domains</strong> to assign it.
            </div>
          )}

        </div>
      </aside>
    )
  }

  const renderSignals = () => {
    // ── Collapsed strip ────────────────────────────────────────────────────
    if (rightPanelCollapsed) {
      return (
        <aside className="dc-signals dc-signals--collapsed">
          <button
            className="dc-panel-tab dc-panel-tab--right"
            onClick={() => setRightPanelCollapsed(false)}
            title="Expand panel"
          >
            <HelpCircle size={16} />
          </button>
        </aside>
      )
    }

    // Program context panel — shown when user clicks a COBOL program in the tree.
    // Tree clicks take priority across ALL sub-tabs.
    if (selectedProgramCtx) {
      return renderProgramContext(selectedProgramCtx)
    }

    // ── Step 8: View-mode-aware routing for non-services sub-tabs ────────────
    if (decompositionViewMode === 'domains') {
      return renderDomainsGuidePanel()
    }
    if (decompositionViewMode === 'graph' || decompositionViewMode === 'list') {
      return crossViewSelection
        ? renderCrossViewAssetPanel(crossViewSelection.data)
        : renderGraphListNoSelectionPanel(decompositionViewMode)
    }
    // ── 'services' path — existing behaviour unchanged below ─────────────────

    if (!selectedSvc) {
      // Guide panel — shown when no service exists yet
      const hasSomeData = (sourceInventory?.programs?.length || 0) > 0
      return (
        <aside className="dc-signals">
          <div className="dc-signals-guide">
            <div className="dc-signals-guide-hdr">
              <Package size={16} className="dc-signals-guide-ico" />
              <span>Service Builder</span>
            </div>
            <p className="dc-signals-guide-text">
              Create a service using <strong>New Service</strong>, then add source assets from the tree.
            </p>
            <div className="dc-signals-guide-steps">
              <div className="dc-signals-guide-step">
                <span className="dc-signals-guide-num">1</span>
                <span>Click <strong>New Service</strong> to name a service boundary</span>
              </div>
              <div className="dc-signals-guide-step">
                <span className="dc-signals-guide-num">2</span>
                <span>Click or drag COBOL programs from the tree into it</span>
              </div>
              <div className="dc-signals-guide-step">
                <span className="dc-signals-guide-num">3</span>
                <span>Confirm each service, then click <strong>Lock Plan</strong> above</span>
              </div>
            </div>
            {hasSomeData && (
              <div className="dc-signals-guide-hint">
                <Code2 size={11} />
                {sourceInventory.programs.length} COBOL program{sourceInventory.programs.length !== 1 ? 's' : ''} available in the tree
              </div>
            )}
          </div>
        </aside>
      )
    }

    const score     = COHESION_SCORE[selectedSvc.cohesion]
    const effStatus = serviceStatuses[selectedSvc.id] || selectedSvc.status
    const alreadyApproved = effStatus === 'approved'

    // Boundary assessment for AI proposals — derived from cohesion
    const boundaryVerdict = !selectedSvc.isManual
      ? selectedSvc.cohesion === 'high'   ? { cls: 'good',    icon: 'check', text: 'Strong boundary' }
      : selectedSvc.cohesion === 'low'    ? { cls: 'warn',    icon: 'alert', text: 'Review before confirming' }
      :                                     { cls: 'neutral',  icon: 'info',  text: 'Reasonable boundary' }
      : null

    const memberCount    = selectedSvc.members?.length || selectedSvc.classes || 0
    const cobolCount     = selectedSvc.cobolOrigins?.length || 0
    const rationale      = !selectedSvc.isManual ? generateServiceRationale(selectedSvc) : []

    return (
      <aside className="dc-signals">
        <div className="dc-signals-header">
          <span className="dc-signals-title">Service details</span>
          <HelpPopover
            placement="bottom-left"
            title="Service details"
            body="This panel shows details about the service you selected. It explains what is inside the boundary, why the AI grouped these programs together, and what action to take next. Use it to decide whether to Confirm or adjust the grouping."
          />
        </div>

        <div className="dc-signals-body">

          {/* ── Identity ──────────────────────────────────────────────── */}
          <div className="dc-signals-identity">
            <div className="dc-signals-svc-name">{selectedSvc.name}</div>
            <div className="dc-signals-identity-row">
              <span className={`dc-status-chip ${effStatus}`}>{statusLabel(effStatus)}</span>
              <span className="dc-svc-source-badge sm-badge">{selectedSvc.isManual ? 'Manual' : 'AI'}</span>
              {boundaryVerdict && (
                <span className={`dc-signals-verdict ${boundaryVerdict.cls}`}>
                  {boundaryVerdict.icon === 'check' && <CheckCircle2 size={10} />}
                  {boundaryVerdict.icon === 'alert' && <AlertTriangle size={10} />}
                  {boundaryVerdict.icon === 'info'  && <Info size={10} />}
                  {boundaryVerdict.text}
                </span>
              )}
            </div>
            {selectedSvc.businessHint && (
              <div className="dc-signals-biz-capability">{selectedSvc.businessHint}</div>
            )}
          </div>

          {/* ── Why this service — decision rationale up front ────────── */}
          {rationale.length > 0 && (
            <div className="dc-signals-sec dc-signals-rationale-sec">
              <span className="dc-signals-lbl">Why this service</span>
              <div className="dc-signals-rationale-body">
                {rationale.map((line, i) => (
                  <p key={i} className="dc-signals-rationale-line">{line}</p>
                ))}
              </div>
            </div>
          )}

          {/* ── Inside this boundary ─────────────────────────────────── */}
          <div className="dc-signals-sec dc-signals-scope-sec">
            <span className="dc-signals-lbl">Inside this boundary</span>
            <div className="dc-signals-scope-stats">
              <span className="dc-signals-scope-stat">
                <strong>{memberCount}</strong> generated Java class{memberCount !== 1 ? 'es' : ''}
              </span>
              {cobolCount > 0 && cobolCount !== memberCount && (
                <span className="dc-signals-scope-stat">
                  from <strong>{cobolCount}</strong> COBOL source program{cobolCount !== 1 ? 's' : ''}
                </span>
              )}
              {selectedSvc.loc > 0 && (
                <span className="dc-signals-scope-stat">
                  {selectedSvc.loc >= 1000 ? `${(selectedSvc.loc/1000).toFixed(1)}k` : selectedSvc.loc} LOC total
                </span>
              )}
            </div>

            {/* Member list — compact */}
            {memberCount > 0 && (
              <div className="dc-signals-java-list dc-signals-java-list-compact">
                {(selectedSvc.members || []).map((m, i) => {
                  const mType = getMemberType(m)
                  return (
                    <div key={i} className="dc-signals-java-row">
                      <FileText size={10} className={`dc-signals-java-ico ${mType.toLowerCase()}`} />
                      <span className="dc-signals-java-name">{m.name}</span>
                      {m.loc > 0 && (
                        <span className="dc-signals-java-loc">
                          {m.loc >= 1000 ? `${(m.loc/1000).toFixed(1)}k` : m.loc}L
                        </span>
                      )}
                      {m.cobol && m.cobol !== m.name && m.cobol !== m.name.replace(/\.java$/i, '') && (
                        <span className="dc-signals-java-cobol" title={`From COBOL: ${m.cobol}`}>{m.cobol}</span>
                      )}
                    </div>
                  )
                })}
              </div>
            )}

            {/* COBOL sources when not 1:1 with members */}
            {cobolCount > 0 && cobolCount !== memberCount && (
              <div className="dc-signals-tags" style={{ marginTop: 6 }}>
                {selectedSvc.cobolOrigins.map(orig => (
                  <span key={orig} className="dc-signals-cobol-tag" title="Original COBOL program">{orig}</span>
                ))}
              </div>
            )}
          </div>

          {/* ── Outside this boundary ────────────────────────────────── */}
          {readiness.unassignedCobolCount > 0 && (
            <div className="dc-signals-sec dc-signals-outside-sec">
              <span className="dc-signals-lbl">Outside this boundary</span>
              <p className="dc-signals-outside-note">
                <strong>{readiness.unassignedCobolCount}</strong> source program{readiness.unassignedCobolCount !== 1 ? 's' : ''} are
                not grouped into any service — likely shared utilities, batch jobs, or
                infrastructure. These won't be included in generated services unless you add them.
              </p>
            </div>
          )}

          {/* ── Supporting signals ───────────────────────────────────── */}
          {(!selectedSvc.isManual && ((selectedSvc.couplingOut || 0) > 0 || selectedSvc.deps?.length > 0 || selectedSvc.domain)) && (
            <div className="dc-signals-sec">
              <span className="dc-signals-lbl">Supporting signals</span>
              <div className="dc-signals-signal-rows">
                {selectedSvc.domain && selectedSvc.domain !== selectedSvc.businessHint && (
                  <div className="dc-signals-signal-row">
                    <span className="dc-signals-signal-key">Domain</span>
                    <span className="dc-signals-signal-val">{selectedSvc.domain}</span>
                  </div>
                )}
                {(selectedSvc.couplingOut || 0) > 0 && (
                  <div className="dc-signals-signal-row">
                    <span className="dc-signals-signal-key">External deps</span>
                    <span className="dc-signals-signal-val">
                      {selectedSvc.couplingOut} outbound
                      {(selectedSvc.deps?.length || 0) > 0 && (
                        <span className="dc-signals-signal-dep-hint">
                          {' '}— {selectedSvc.deps.slice(0, 2).join(', ')}{selectedSvc.deps.length > 2 ? ` +${selectedSvc.deps.length - 2}` : ''}
                        </span>
                      )}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── Boundary Intelligence (Phase 1) — collapsed by default ── */}
          {(() => {
            const bi = computeBoundaryScore(selectedSvc, intelligenceMetrics, depGraphData)
            if (!bi.hasData) return null

            const statusLabel_bi = bi.overallStatus === 'risky'  ? 'Risky'
                                 : bi.overallStatus === 'review' ? 'Review'
                                 : bi.overallStatus === 'strong' ? 'Strong'
                                 : null

            return (
              <details className="dc-bi-details dc-signals-sec">
                <summary className="dc-bi-summary">
                  <span className="dc-signals-lbl">Advanced analysis</span>
                  {statusLabel_bi && (
                    <span className={`dc-bi-status dc-bi-status--${bi.overallStatus}`}>
                      {statusLabel_bi}
                    </span>
                  )}
                  <span className="dc-bi-summary-hint">cohesion · instability · coupling</span>
                </summary>
                <div className="dc-bi-sec-body">
                <div className="dc-bi-rows">

                  {/* Cohesion Score */}
                  {bi.cohesionScore !== null && (
                    <div className="dc-bi-row">
                      <div className="dc-bi-row-top">
                        <span className="dc-bi-label">Cohesion</span>
                        <span className={`dc-bi-value ${bi.cohesionScore >= 75 ? 'dc-bi-value--good' : bi.cohesionScore >= 45 ? 'dc-bi-value--warn' : 'dc-bi-value--risk'}`}>
                          {bi.cohesionScore}%
                        </span>
                      </div>
                      <div className="dc-bi-bar-track">
                        <div
                          className={`dc-bi-bar-fill ${bi.cohesionScore >= 75 ? 'good' : bi.cohesionScore >= 45 ? 'warn' : 'risk'}`}
                          style={{ width: `${bi.cohesionScore}%` }}
                        />
                      </div>
                      <p className="dc-bi-note">
                        LCOM4-based — higher means more internally cohesive source programs
                        {bi.cohesionMatched < bi.totalOrigins && bi.totalOrigins > 0
                          ? ` (${bi.cohesionMatched}/${bi.totalOrigins} programs matched)`
                          : ''}
                      </p>
                    </div>
                  )}

                  {/* Instability Score */}
                  {bi.instabilityScore !== null && (
                    <div className="dc-bi-row">
                      <div className="dc-bi-row-top">
                        <span className="dc-bi-label">Instability</span>
                        <span className={`dc-bi-value ${bi.instabilityScore <= 30 ? 'dc-bi-value--good' : bi.instabilityScore <= 60 ? 'dc-bi-value--warn' : 'dc-bi-value--risk'}`}>
                          {bi.instabilityScore}%
                        </span>
                      </div>
                      <div className="dc-bi-bar-track">
                        <div
                          className={`dc-bi-bar-fill ${bi.instabilityScore <= 30 ? 'good' : bi.instabilityScore <= 60 ? 'warn' : 'risk'}`}
                          style={{ width: `${bi.instabilityScore}%` }}
                        />
                      </div>
                      <p className="dc-bi-note">
                        Fan-out weighted by centrality — lower is more stable
                        {bi.instabilityMatched < bi.totalOrigins && bi.totalOrigins > 0
                          ? ` (${bi.instabilityMatched}/${bi.totalOrigins} programs matched)`
                          : ''}
                      </p>
                    </div>
                  )}

                  {/* Coupling Leak */}
                  {bi.couplingLeakPct !== null && (
                    <div className="dc-bi-row">
                      <div className="dc-bi-row-top">
                        <span className="dc-bi-label">Coupling Leak</span>
                        <span className={`dc-bi-value ${bi.couplingLeakPct <= 25 ? 'dc-bi-value--good' : bi.couplingLeakPct <= 50 ? 'dc-bi-value--warn' : 'dc-bi-value--risk'}`}>
                          {bi.couplingLeakPct}%
                        </span>
                      </div>
                      <div className="dc-bi-bar-track">
                        <div
                          className={`dc-bi-bar-fill ${bi.couplingLeakPct <= 25 ? 'good' : bi.couplingLeakPct <= 50 ? 'warn' : 'risk'}`}
                          style={{ width: `${bi.couplingLeakPct}%` }}
                        />
                      </div>
                      <p className="dc-bi-note">
                        {bi.externalEdges} of {bi.internalEdges + bi.externalEdges} outbound dependency edges cross the boundary
                        {bi.internalEdges + bi.externalEdges === 0
                          ? ' — no inter-class dependency edges detected'
                          : ' (Java class level, post-CR)'}
                      </p>
                    </div>
                  )}

                  {/* Split Candidate Count */}
                  {bi.totalOrigins > 0 && (
                    <div className="dc-bi-row dc-bi-row--split">
                      <div className="dc-bi-row-top">
                        <span className="dc-bi-label">Split candidates</span>
                        <span className={`dc-bi-value ${bi.splitCount === 0 ? 'dc-bi-value--good' : bi.splitCount === 1 ? 'dc-bi-value--warn' : 'dc-bi-value--risk'}`}>
                          {bi.splitCount} / {bi.totalOrigins}
                        </span>
                      </div>
                      {bi.splitCount > 0 ? (
                        <p className="dc-bi-note dc-bi-note--warn">
                          {bi.splitNames.join(', ')} {bi.splitCount === 1 ? 'has' : 'have'} multiple independent responsibility groups — consider decomposing before building
                        </p>
                      ) : (
                        <p className="dc-bi-note">No programs flagged as split candidates by Analyze</p>
                      )}
                    </div>
                  )}

                </div>

                {/* Data source attribution */}
                <p className="dc-bi-attribution">
                  Cohesion + Instability: Code Analysis Phase 1–4 ·
                  Coupling Leak: Dependency Mapper class graph ·
                  Display only — does not affect generation
                </p>
                </div>
              </details>
            )
          })()}

          {/* ── Finalize-readiness verdict ───────────────────────────── */}
          {!isApproved && (
            <div className={`dc-signals-readiness-note ${readiness.isReadyToFinalize ? 'ready' : 'blocked'}`}>
              {readiness.isReadyToFinalize
                ? <CheckCircle2 size={11} className="dc-signals-readiness-ico" />
                : <AlertCircle  size={11} className="dc-signals-readiness-ico" />
              }
              <span>
                {readiness.isReadyToFinalize
                  ? readiness.warningReasons.length === 0
                    ? 'Looking good — lock the plan when you\'re satisfied.'
                    : 'Ready to lock the plan. Optional warnings won\'t stop generation.'
                  : readiness.blockingReasons[0] || 'Not ready to lock yet — resolve the issue above.'
                }
              </span>
            </div>
          )}

          <div className="dc-signals-divider" />

          {/* ── Actions ──────────────────────────────────────────────── */}
          <div className="dc-signals-actions">
            {(!isApproved || isStaleFinalised) && (
              <button
                className={`dc-signals-act-btn primary${alreadyApproved ? ' done' : ''}${effStatus === 'modified' ? ' reconfirm' : ''}`}
                onClick={() => !alreadyApproved && handleApproveService(selectedSvc.id)}
                disabled={alreadyApproved}
                title={
                  alreadyApproved ? 'Already confirmed — click Unconfirm to revert'
                  : effStatus === 'modified' ? 'Previously confirmed — confirm again to lock your changes'
                  : 'Confirm this service — include it in the locked plan'
                }
              >
                <CheckCircle2 size={11} />
                {svcConfirmLabel(effStatus).replace('Service ', '')}
              </button>
            )}
            {alreadyApproved && (!isApproved || isStaleFinalised) && (
              <button
                className="dc-signals-act-btn"
                onClick={() => handleUnconfirmService(selectedSvc.id)}
                title="Revert this service to draft — removes it from the confirmed set"
              >
                Unconfirm
              </button>
            )}
            {selectedSvc.isManual && (
              <button
                className="dc-signals-act-btn"
                onClick={() => handleStartRename(selectedSvc)}
                title="Rename this service"
              >
                Rename
              </button>
            )}
            <button
              className="dc-signals-act-btn"
              onClick={() => setActiveTab('graph')}
              title="Inspect how this service's classes connect to other components — use to trace dependencies and refine boundaries"
            >
              <Network size={11} /> Inspect dependencies
            </button>
          </div>

        </div>
      </aside>
    )
  }

  // ── WORKBENCH (assembles three panels) ────────────────────────────────────

  // ── Step 4 — Center-pane placeholder for not-yet-implemented view modes ──
  // Domains (Step 5), Graph (Step 6), List (Step 7) all render this until their
  // real implementations land. Keeps the Services workbench fully functional.
  const renderDecompositionPlaceholder = (mode) => {
    const copy = {
      domains: {
        title: 'Domains view coming soon',
        body:  'Business-level slicing with drag-and-drop. The Services view below stays the live workspace.',
      },
      graph: {
        title: 'Graph view coming soon',
        body:  'Domain-coloured dependency graph with coupling highlights. Switch to Services to keep working.',
      },
      list: {
        title: 'List view coming soon',
        body:  'Sortable table of services and metrics. Switch to Services to keep working.',
      },
    }[mode] || { title: '', body: '' }

    return (
      <div
        className="dc-decomp-placeholder"
        id={`dc-decomp-panel-${mode}`}
        role="tabpanel"
      >
        <div className="dc-decomp-placeholder-inner">
          <div className="dc-decomp-placeholder-title">{copy.title}</div>
          <div className="dc-decomp-placeholder-body">{copy.body}</div>
        </div>
      </div>
    )
  }

  // Step 5 — Domains View renders the real DomainsBoardView.
  // Step 6 — Graph view renders the real DomainsGraphView.
  // Step 7 — List view renders the real DecompositionListView.
  // Services keeps the existing live workbench path.
  const renderDecompositionCenter = () => {
    if (decompositionViewMode === 'services') return renderServiceBuilder()
    // Domains tab — disabled for now (graph clustering handles paragraph grouping).
    // Re-enable in DefineDecompositionTabs.jsx when Domains needs to come back.
    // if (decompositionViewMode === 'domains') { ... }
    /*
    if (decompositionViewMode === 'domains') {
      if (!decompositionData) {
        if (dsProposals && dsProposals.length > 0) {
          return (
            <div style={{ flex: 1, overflow: 'auto', padding: '20px 24px' }}>
              <div style={{ marginBottom: 16, color: 'var(--text-secondary, #94a3b8)', fontSize: '0.83rem' }}>
                {dsProposals.length} domain{dsProposals.length !== 1 ? 's' : ''} identified via graph-powered clustering.
                Switch to <strong>Services</strong> to manage boundaries, confirm, and lock.
              </div>
              {dsProposals.map((p, i) => {
                const paragraphs = p.paragraphs || []
                const programs   = p.programs || []
                const shared     = p.shared_fields || []
                const pt         = p.pipeline_totals || {}
                return (
                  <div key={p.cluster_id || i} style={{
                    background: 'var(--surface-secondary, #1e293b)',
                    border: '1px solid var(--border-primary, #334155)',
                    borderRadius: 8,
                    padding: '14px 18px',
                    marginBottom: 10,
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                      <span style={{ fontWeight: 600, fontSize: '0.9rem', color: 'var(--text-primary, #e2e8f0)' }}>
                        {p.domain_name || `Domain ${p.cluster_id}`}
                      </span>
                      <span style={{ fontSize: '0.78rem', color: 'var(--text-secondary, #94a3b8)' }}>
                        {paragraphs.length} paragraph{paragraphs.length !== 1 ? 's' : ''}
                        {programs.length > 0 && ` · ${programs.join(', ')}`}
                      </span>
                    </div>
                    {p.rationale && (
                      <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary, #94a3b8)', marginBottom: 4 }}>
                        {p.rationale}
                      </div>
                    )}
                    {shared.length > 0 && (
                      <div style={{ fontSize: '0.78rem', color: 'var(--accent-blue, #60a5fa)' }}>
                        Shared fields: {shared.join(', ')}
                      </div>
                    )}
                    {pt.method_count > 0 && (
                      <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary, #94a3b8)', marginTop: 2 }}>
                        {pt.method_count} methods · {pt.entity_count || 0} entities · {pt.repository_count || 0} repos
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )
        }
        return (
          <div className="dc-decomp-placeholder">
            <div className="dc-decomp-placeholder-inner">
              <div className="dc-decomp-placeholder-title">No Domain Data</div>
              <div className="dc-decomp-placeholder-body">
                No domain proposals available yet. Run <strong>Define Services</strong> to generate
                graph-powered service boundary proposals, then return here to view domains.
              </div>
            </div>
          </div>
        )
      }
      return (
        <DomainsBoardView
          scoutAccountId={project?.account}
          applicationName={project?.name}
          userId={resolvedUserId}
          monolithIdentifierData={decompositionData}
          flushSaveRef={domainsBoardFlushRef}
        />
      )
    }
    */
    if (decompositionViewMode === 'graph') {
      return (
        <DomainsGraphView
          assetGraph={assetGraph}
          scoutAccountId={project?.account}
          applicationName={project?.name}
          onSelectionChange={handleCrossViewSelection}
          layoutRevisionFromParent={paletteLayoutRevision}
          selectedDomainId={selectedDomainId}
        />
      )
    }
    if (decompositionViewMode === 'list') {
      return (
        <DecompositionListView
          assetGraph={assetGraph}
          scoutAccountId={project?.account}
          applicationName={project?.name}
          userId={resolvedUserId}
          onSelectionChange={handleCrossViewSelection}
        />
      )
    }
    return renderDecompositionPlaceholder(decompositionViewMode)
  }

  // ── Step 12: domain tab strip ─────────────────────────────────────────────────
  // Rendered only when: (a) services sub-tab is active, (b) domain data loaded,
  // (c) at least one domain exists. When no domain contract exists, this renders
  // nothing and the existing Services experience is 100% unchanged.

  const renderDomainTabStrip = () => {
    if (!domainList.length) return null
    return (
      <div className="dc-domain-tabs" role="tablist" aria-label="Domain scope">
        <button
          role="tab"
          aria-selected={!selectedDomainId}
          className={`dc-domain-tab ${!selectedDomainId ? 'active' : ''}`}
          onClick={() => setSelectedDomainId(null)}
          title="Show services and programs across all domains"
        >
          All domains
          <span className="dc-domain-tab-count">{allCobolPrograms.length}</span>
        </button>
        {domainList.map(d => {
          const progCount = (programsForDomain[d.domain_id] || new Set()).size
          return (
            <button
              key={d.domain_id}
              role="tab"
              aria-selected={selectedDomainId === d.domain_id}
              className={`dc-domain-tab ${selectedDomainId === d.domain_id ? 'active' : ''}`}
              onClick={() => setSelectedDomainId(d.domain_id)}
              title={`Show programs and services scoped to ${d.name}`}
            >
              <span className="dc-domain-tab-name">{d.name}</span>
              <span className="dc-domain-tab-count">{progCount}</span>
            </button>
          )
        })}
      </div>
    )
  }

  const renderWorkbench = () => {
    // ── Step guide: compute current step ──────────────────────────────────────
    // Steps: 1 Review domains → 2 Promote/edit services → 3 Confirm services → 4 Finalize
    const sgTotalSvcs    = services.length
    const sgApproved     = services.filter(s => (serviceStatuses[s.id] || s.status) === 'approved').length
    const sgHasDomains   = domainLoadState === 'ok' && domainList.length > 0
    const sgAllConfirmed = sgTotalSvcs > 0 && sgApproved === sgTotalSvcs

    // Which step are we on? (1-indexed, 0 = done)
    const sgStep = (isApproved && !isStaleFinalised)
      ? 0  // fully done
      : sgAllConfirmed
        ? 4  // ready to finalize
        : sgTotalSvcs > 0
          ? 3  // confirm services
          : sgHasDomains
            ? 2  // promote domains to services
            : 1  // review domains first

    const sgSteps = [
      { n: 1, label: 'Review domains',     hint: 'See the Domains tab' },
      { n: 2, label: 'Promote to services', hint: 'Use as Service for each domain you want built' },
      { n: 3, label: 'Confirm services',    hint: 'Click Confirm on each service card' },
      { n: 4, label: 'Lock plan',            hint: 'Click Lock Plan above' },
    ]

    return (
    <div className="dc-workbench-wrapper">

      {/* ── Guided step strip ────────────────────────────────────────────── */}
      {sgStep !== 0 && (
        <div className="dc-step-guide">
          {sgSteps.map((s, i) => {
            const isDone   = s.n < sgStep
            const isActive = s.n === sgStep
            return (
              <span key={s.n} className="dc-step-guide-entry">
                <span
                  className={`dc-step-guide-step${isDone ? ' done' : isActive ? ' active' : ' pending'}`}
                  title={isActive ? s.hint : undefined}
                >
                  <span className="dc-step-guide-num">
                    {isDone ? <CheckCircle2 size={11} /> : s.n}
                  </span>
                  <span className="dc-step-guide-label">{s.label}</span>
                  {isActive && <span className="dc-step-guide-hint">{s.hint}</span>}
                </span>
                {i < sgSteps.length - 1 && (
                  <span className={`dc-step-guide-connector${isDone ? ' filled' : ''}`} />
                )}
              </span>
            )
          })}
        </div>
      )}
      {sgStep === 0 && (
        <div className="dc-step-guide dc-step-guide-complete">
          <CheckCircle2 size={13} className="dc-step-guide-done-ico" />
          <span>Define Services complete — proceed to Build Services from the menu when ready.</span>
        </div>
      )}

      <DefineDecompositionTabs
        value={decompositionViewMode}
        onChange={handleDecompositionViewModeChange}
      />
      {/* Step 12: domain tab strip — only in services mode, only when domain data exists */}
      {decompositionViewMode === 'services' && domainLoadState === 'ok' && renderDomainTabStrip()}
      <div className="dc-workbench-body">
        {renderInventoryTree()}
        {renderDecompositionCenter()}
        {!rightPanelCollapsed && (
          <button
            className="dc-signals-collapse-trigger"
            onClick={() => setRightPanelCollapsed(true)}
            title="Collapse panel"
          >
            <X size={12} />
          </button>
        )}
        {renderSignals()}
      </div>
    </div>
    )
  }

  // ── HANDOFF PLAN (Finalize → Review → Send) ───────────────────────────────

  const renderReport = () => {
    const contract = approvedContract

    // ── Pre-finalization: guide user to finish ──────────────────────────────
    if (!isApproved || !contract) {
      const liveConfirmed = services.filter(s => (serviceStatuses[s.id] || s.status) === 'approved')
      const liveDraft     = services.filter(s => (serviceStatuses[s.id] || s.status) !== 'approved')
      return (
        <div className="dc-handoff-view">
          <div className="dc-handoff-preflight">
            <Lock size={28} className="dc-handoff-preflight-ico" />
            <div className="dc-handoff-preflight-title">Plan Not Yet Locked</div>
            <div className="dc-handoff-preflight-sub">
              Confirm your services on the Workbench, then click{' '}
              <strong>Lock Plan</strong> to finalise and see exactly what will be sent to Build Services.
            </div>
            {services.length > 0 && (
              <div className="dc-handoff-preflight-preview">
                <div className="dc-handoff-preflight-preview-hdr">
                  Current services ({services.length})
                </div>
                {services.map(svc => {
                  const eff     = serviceStatuses[svc.id] || svc.status
                  const progCnt = svc.members?.length || svc.classes || 0
                  return (
                    <div key={svc.id} className="dc-handoff-preflight-row">
                      <span className={`dc-status-chip ${eff}`}>{statusLabel(eff)}</span>
                      <span className="dc-handoff-preflight-svc-name">{svc.name}</span>
                      <span className="dc-handoff-preflight-svc-count">
                        {progCnt} program{progCnt !== 1 ? 's' : ''}
                      </span>
                    </div>
                  )
                })}
              </div>
            )}
            {!readiness.isReadyToFinalize && readiness.blockingReasons.length > 0 && (
              <div className="dc-handoff-preflight-blocking">
                <AlertCircle size={12} />
                <span>{readiness.blockingReasons[0]}</span>
              </div>
            )}
            <div className="dc-handoff-preflight-actions">
              <button
                className="dc-action-btn secondary"
                onClick={() => setActiveTab('workbench')}
              >
                <Layers size={13} />
                Back to Workbench
              </button>
              <button
                className={`dc-action-btn primary${canFinalize ? '' : ' disabled'}`}
                disabled={!canFinalize || isApproving}
                onClick={canFinalize && !isApproving ? () => handleApprove(services, disconnected) : undefined}
              >
                {isApproving ? <Loader2 size={13} className="dc-spin" /> : <Lock size={13} />}
                {isApproving ? 'Locking…' : 'Lock Plan'}
              </button>
            </div>
          </div>
        </div>
      )
    }

    // ── Post-finalization: show the locked handoff contract ─────────────────
    // RC-UI-D: Journey HITL contracts are finalised at the whole-contract level
    // (contract.is_approved = true) but individual services may have status
    // 'proposed' because Journey HITL does not require per-service confirmation.
    // Treat both 'approved' and 'proposed' services as "confirmed" for Handoff
    // counts — only services explicitly in a rejected/inactive state are drafts.
    const confirmedSvcs  = contract.services.filter(s => s.status === 'approved' || s.status === 'proposed')
    const draftSvcs      = contract.services.filter(s => s.status !== 'approved' && s.status !== 'proposed')
    // RC-UI-HANDOFF: cobol_origins is stored as [] (empty array) when the contract
    // was built from a HITL-only session with no explicit cobol_origins populated.
    // Empty arrays are TRUTHY in JS, so `s.cobol_origins || fallback` silently
    // short-circuits to [] and allAssignedCobol stays empty → "0 Assigned Programs".
    // Fix: gate on length > 0 before trusting cobol_origins.
    const allAssignedCobol = new Set(
      confirmedSvcs.flatMap(s =>
        (s.cobol_origins?.length   > 0 ? s.cobol_origins   : null) ||
        (s.cobolOrigins?.length    > 0 ? s.cobolOrigins    : null) ||
        (s.members || []).map(m => m.cobol || m.name).filter(Boolean)
      )
    )
    const allPrograms     = sourceInventory?.programs || []
    const unassignedProgs = allPrograms.filter(p => !allAssignedCobol.has(p.name))
    const copybooks       = sourceInventory?.copybooks || []
    const jclItems        = sourceInventory?.jcl || []
    const totalAssigned   = allAssignedCobol.size
    const totalLoc        = confirmedSvcs.reduce((n, s) => n + (s.loc || 0), 0)

    const seededFrom  = contract.source?.seeded_from || 'manual_hitl'
    const seededLabel = seededFrom === 'monolith_identifier' ? 'Monolith Identifier'
      : seededFrom === 'manual_hitl' ? 'Manual (HITL)'
      : 'No upstream data'
    const dmLabel = contract.source?.dm_job_id ? ' + Dependency Mapper' : ''
    const approvedAt = contract.approved_at
      ? new Date(contract.approved_at).toLocaleString('en-US', {
          month: 'short', day: 'numeric', year: 'numeric',
          hour: '2-digit', minute: '2-digit',
        })
      : 'Unknown'

    return (
      <div className="dc-handoff-view">

        {/* ── Plan header ──────────────────────────────────────────────────── */}
        <div className="dc-handoff-header">
          <div className="dc-handoff-header-left">
            <div className="dc-handoff-title">
              {isStaleFinalised
                ? <AlertTriangle size={15} className="dc-handoff-title-ico stale" />
                : <CheckCircle2  size={15} className="dc-handoff-title-ico" />
              }
              Decomposition Handoff Plan
              {isStaleFinalised && (
                <span className="dc-handoff-stale-badge">Stale</span>
              )}
            </div>
            <div className="dc-handoff-meta">
              <span>ID: <code className="dc-handoff-id">{contract.decomposition_id}</code></span>
              <span className="dc-handoff-meta-sep">·</span>
              <span>Finalized {approvedAt}</span>
              <span className="dc-handoff-meta-sep">·</span>
              <span>Source: {seededLabel}{dmLabel}</span>
            </div>
          </div>
          {isStaleFinalised && (
            <div className="dc-handoff-header-actions">
              <button
                className={`dc-action-btn primary stale`}
                disabled={!canFinalize || isApproving}
                onClick={canFinalize && !isApproving ? () => handleApprove(services, disconnected) : undefined}
              >
                {isApproving ? <Loader2 size={13} className="dc-spin" /> : <Lock size={13} />}
                {isApproving ? 'Locking…' : 'Re-lock Plan'}
              </button>
            </div>
          )}
        </div>

        {isStaleFinalised && (
          <div className="dc-handoff-stale-note">
            <AlertTriangle size={12} />
            This plan is out of date — services were changed after locking. Re-lock the plan to update it before continuing to Build Services.
          </div>
        )}

        {/* ── Generation scope summary ─────────────────────────────────────── */}
        <div className="dc-handoff-scope-row">
          <div className="dc-handoff-scope-card included">
            <div className="dc-handoff-scope-num">{confirmedSvcs.length}</div>
            <div className="dc-handoff-scope-lbl">Confirmed Services</div>
          </div>
          <div className="dc-handoff-scope-card included">
            <div className="dc-handoff-scope-num">{totalAssigned}</div>
            <div className="dc-handoff-scope-lbl">Source Programs</div>
            {totalLoc > 0 && (
              <div className="dc-handoff-scope-sub">{totalLoc.toLocaleString()} LOC</div>
            )}
          </div>
          {unassignedProgs.length > 0 && (
            <div className="dc-handoff-scope-card excluded">
              <div className="dc-handoff-scope-num">{unassignedProgs.length}</div>
              <div className="dc-handoff-scope-lbl">Unassigned</div>
            </div>
          )}
          {draftSvcs.length > 0 && (
            <div className="dc-handoff-scope-card excluded">
              <div className="dc-handoff-scope-num">{draftSvcs.length}</div>
              <div className="dc-handoff-scope-lbl">Draft Services</div>
            </div>
          )}
        </div>

        {/* ── Confirmed Target Services ─────────────────────────────────────── */}
        <div className="dc-handoff-section">
          <div className="dc-handoff-section-hdr included">
            <CheckCircle2 size={13} />
            <span>Confirmed Services ({confirmedSvcs.length})</span>
          </div>
          {confirmedSvcs.length === 0 ? (
            <div className="dc-handoff-section-empty">
              No confirmed services — go to Workbench, confirm at least one service, then re-lock the plan.
            </div>
          ) : confirmedSvcs.map(svc => {
            const origins = svc.cobol_origins
              || (svc.members || []).map(m => m.cobol || m.name)
              || []
            const memberMap = Object.fromEntries(
              (svc.members || []).map(m => [m.cobol || m.name, m])
            )
            return (
              <div key={svc.id} className="dc-handoff-svc-card">
                <div className="dc-handoff-svc-hdr">
                  <Box size={12} className="dc-handoff-svc-ico" />
                  <span className="dc-handoff-svc-name">{svc.name}</span>
                  {svc.domain && <span className="dc-handoff-svc-domain">{svc.domain}</span>}
                  <span className="dc-status-chip approved">Confirmed</span>
                  <span className="dc-handoff-svc-stat">
                    {origins.length} program{origins.length !== 1 ? 's' : ''}
                  </span>
                  {svc.loc > 0 && (
                    <span className="dc-handoff-svc-stat">{svc.loc.toLocaleString()} LOC</span>
                  )}
                </div>
                {origins.length > 0 && (
                  <div className="dc-handoff-svc-members">
                    {origins.map((prog, idx) => {
                      const m = memberMap[prog] || {}
                      return (
                        <span key={idx} className="dc-handoff-prog-chip">
                          <Code2 size={9} />
                          <span>{prog}</span>
                          {m.loc > 0 && (
                            <span className="dc-handoff-prog-loc">{m.loc.toLocaleString()}</span>
                          )}
                        </span>
                      )
                    })}
                  </div>
                )}
              </div>
            )
          })}
        </div>

        {/* ── Draft / Unconfirmed services ──────────────────────────────────── */}
        {draftSvcs.length > 0 && (
          <div className="dc-handoff-section">
            <div className="dc-handoff-section-hdr draft">
              <AlertTriangle size={13} />
              <span>Draft / Unconfirmed Services ({draftSvcs.length})</span>
              <span className="dc-handoff-section-tag excluded">excluded</span>
            </div>
            {draftSvcs.map(svc => (
              <div key={svc.id} className="dc-handoff-svc-card draft">
                <div className="dc-handoff-svc-hdr">
                  <Box size={12} className="dc-handoff-svc-ico muted" />
                  <span className="dc-handoff-svc-name muted">{svc.name}</span>
                  <span className="dc-status-chip proposed">Draft</span>
                </div>
                <div className="dc-handoff-svc-note">
                  Go to Workbench → Confirm this service → Re-finalize to include it.
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── Unassigned programs ───────────────────────────────────────────── */}
        {unassignedProgs.length > 0 && (
          <div className="dc-handoff-section">
            <div className="dc-handoff-section-hdr unassigned">
              <Circle size={13} />
              <span>Unassigned Programs ({unassignedProgs.length})</span>
            </div>
            <div className="dc-handoff-chip-list">
              {unassignedProgs.map(p => (
                <span key={p.name} className="dc-handoff-prog-chip muted">
                  <Code2 size={9} />
                  <span>{p.displayName || p.name}</span>
                  {p.loc > 0 && <span className="dc-handoff-prog-loc">{p.loc.toLocaleString()}</span>}
                </span>
              ))}
            </div>
            <div className="dc-handoff-section-note">
              These programs are not included in any service and will not be generated. Assign them on the Workbench to include them, or leave them out.
            </div>
          </div>
        )}

        {/* ── Shared / common assets ────────────────────────────────────────── */}
        {copybooks.length > 0 && (
          <div className="dc-handoff-section">
            <div className="dc-handoff-section-hdr shared">
              <Share2 size={13} />
              <span>Shared / Common Assets</span>
              <span className="dc-handoff-section-tag neutral">
                {copybooks.length} copybook{copybooks.length !== 1 ? 's' : ''} — shared library
              </span>
            </div>
            <div className="dc-handoff-chip-list">
              {copybooks.map(c => (
                <span key={c.name} className="dc-handoff-prog-chip shared">
                  <FileText size={9} />
                  <span>{c.displayName || c.name}</span>
                </span>
              ))}
            </div>
            <div className="dc-handoff-section-note">
              Copybooks are shared structures available to all generated services — not exclusively
              owned by any single service.
            </div>
          </div>
        )}

        {/* ── JCL ──────────────────────────────────────────────────────────── */}
        {jclItems.length > 0 && (
          <div className="dc-handoff-section">
            <div className="dc-handoff-section-hdr neutral">
              <Cpu size={13} />
              <span>JCL Jobs ({jclItems.length})</span>
              <span className="dc-handoff-section-tag neutral">optional — not assigned</span>
            </div>
            <div className="dc-handoff-chip-list">
              {jclItems.map(j => (
                <span key={j.name} className="dc-handoff-prog-chip muted">
                  <FileText size={9} />
                  <span>{j.displayName || j.name}</span>
                </span>
              ))}
            </div>
            <div className="dc-handoff-section-note">
              JCL job definitions are optional and are not required for Service Generation to proceed.
            </div>
          </div>
        )}

        {/* ── Advisory warnings ────────────────────────────────────────────── */}
        {readiness.warningReasons.length > 0 && (
          <div className="dc-handoff-section">
            <div className="dc-handoff-section-hdr warning">
              <AlertTriangle size={13} />
              <span>Advisory Notes ({readiness.warningReasons.length})</span>
            </div>
            <ul className="dc-handoff-warn-list">
              {readiness.warningReasons.map((r, i) => (
                <li key={i}>{r}</li>
              ))}
            </ul>
          </div>
        )}

        {/* ── What happens next ─────────────────────────────────────────────── */}

        {/* ── Bottom action row ────────────────────────────────────────────── */}
        <div className="dc-handoff-bottom-actions">
          <span className="dc-handoff-bottom-spacer" />
          {isStaleFinalised ? (
            <button
              className={`dc-action-btn primary stale`}
              disabled={!canFinalize || isApproving}
              onClick={canFinalize && !isApproving ? () => handleApprove(services, disconnected) : undefined}
            >
              {isApproving ? <Loader2 size={13} className="dc-spin" /> : <Lock size={13} />}
              {isApproving ? 'Locking…' : 'Re-lock Plan'}
            </button>
          ) : (
            <button
              className="dc-action-btn primary dc-action-btn-svcgen dc-action-btn-svcgen--lg"
              title="Navigate to Build Services — also accessible from the main menu"
              onClick={() => onGoToServiceGeneration?.({ approvedJobId })}
            >
              <ArrowRight size={13} />
              Go to Build Services
            </button>
          )}
        </div>

      </div>
    )
  }

  // ── GRAPH VIEW ─────────────────────────────────────────────────────────────

  // Compute connected slice from assetGraph relationships
  const depGraphConnected = (() => {
    if (!depGraphSelected || !assetGraph?.relationships) return new Set()
    const connected = new Set([depGraphSelected])
    assetGraph.relationships.forEach(r => {
      if (r.from === depGraphSelected) connected.add(r.to)
      if (r.to   === depGraphSelected) connected.add(r.from)
    })
    return connected
  })()

  const depGraphSelectedPrograms = (() => {
    if (!assetGraph?.assets) return []
    return assetGraph.assets
      .filter(a => depGraphConnected.has(a.id) && a.type === 'COBOL_PROGRAM')
      .map(a => a.name)
  })()

  // Build ReactFlow graph whenever assetGraph data arrives
  useEffect(() => {
    if (!assetGraph?.assets?.length) { setRfNodes([]); setRfEdges([]); return }
    const assetIds = new Set(assetGraph.assets.map(a => a.id))

    const rfNodeList = assetGraph.assets.map(a => ({
      id: a.id,
      type: 'depAsset',
      position: { x: 0, y: 0 },
      data: {
        label: a.name,
        assetType: a.type,
        highlighted: false,
        isSelected: false,
        assignedTo: null,
      },
    }))

    const rfEdgeList = (assetGraph.relationships || [])
      .filter(r => assetIds.has(r.from) && assetIds.has(r.to))
      .map(r => ({
        id: `${r.from}||${r.to}||${r.type}`,
        source: r.from,
        target: r.to,
        type: 'default',
        data: { color: DEP_EDGE_COLORS[r.type] || '#8b92a5', relType: r.type },
        style: { stroke: DEP_EDGE_COLORS[r.type] || '#8b92a5', strokeWidth: 1.5, opacity: 1 },
        markerEnd: { type: MarkerType.ArrowClosed, width: 8, height: 8, color: DEP_EDGE_COLORS[r.type] || '#8b92a5' },
      }))

    // Dagre layout — left-to-right hierarchy
    const g = new dagre.graphlib.Graph()
    g.setDefaultEdgeLabel(() => ({}))
    g.setGraph({ rankdir: 'LR', ranksep: 100, nodesep: 60, marginx: 60, marginy: 60 })
    rfNodeList.forEach(n => g.setNode(n.id, { width: 120, height: 50 }))
    rfEdgeList.forEach(e => g.setEdge(e.source, e.target))
    dagre.layout(g)

    const layouted = rfNodeList.map((n, i) => {
      const pos = g.node(n.id)
      const safeX = pos && isFinite(pos.x) ? pos.x - 60 : (i % 6) * 160
      const safeY = pos && isFinite(pos.y) ? pos.y - 25 : Math.floor(i / 6) * 80
      return { ...n, position: { x: safeX, y: safeY } }
    })

    setRfNodes(layouted)
    setRfEdges(rfEdgeList)
    setDepGraphSelected(null)
    setGraphServiceName('')
    setGraphAddToServiceId('')
  }, [assetGraph])

  // ReactFlow node click — highlight slice, dim everything else
  const onRFNodeClick = useCallback((event, rfNode) => {
    const nodeId = rfNode.id
    const isDeselecting = depGraphSelected === nodeId

    if (isDeselecting) {
      setDepGraphSelected(null)
      setGraphServiceName('')
      setGraphAddToServiceId('')
      setRfNodes(nds => nds.map(n => ({ ...n, data: { ...n.data, highlighted: false, isSelected: false }, style: { opacity: 1 } })))
      setRfEdges(eds => eds.map(e => ({ ...e, style: { stroke: e.data?.color || '#8b92a5', strokeWidth: 1.5, opacity: 1 } })))
      return
    }

    setDepGraphSelected(nodeId)
    setGraphServiceName('')
    setGraphAddToServiceId('')

    const connectedNodeIds = new Set([nodeId])
    const connectedEdgeIds = new Set()
    ;(assetGraph?.relationships || []).forEach(r => {
      if (r.from === nodeId) { connectedNodeIds.add(r.to);   connectedEdgeIds.add(`${r.from}||${r.to}||${r.type}`) }
      if (r.to   === nodeId) { connectedNodeIds.add(r.from); connectedEdgeIds.add(`${r.from}||${r.to}||${r.type}`) }
    })

    setRfNodes(nds => nds.map(n => ({
      ...n,
      data: { ...n.data, highlighted: connectedNodeIds.has(n.id), isSelected: n.id === nodeId },
      style: { opacity: connectedNodeIds.has(n.id) ? 1 : 0.06 },
    })))
    setRfEdges(eds => eds.map(e => {
      const isConn = connectedEdgeIds.has(e.id)
      return { ...e, style: { stroke: isConn ? (e.data?.color || '#60a5fa') : 'rgba(139,146,165,0.15)', strokeWidth: isConn ? 2.5 : 1, opacity: isConn ? 1 : 0.2 } }
    }))
  }, [depGraphSelected, assetGraph, setRfNodes, setRfEdges])

  // ReactFlow pane click — reset everything
  const onRFPaneClick = useCallback(() => {
    if (!depGraphSelected) return
    setDepGraphSelected(null)
    setGraphServiceName('')
    setGraphAddToServiceId('')
    setRfNodes(nds => nds.map(n => ({ ...n, data: { ...n.data, highlighted: false, isSelected: false }, style: { opacity: 1 } })))
    setRfEdges(eds => eds.map(e => ({ ...e, style: { stroke: e.data?.color || '#8b92a5', strokeWidth: 1.5, opacity: 1 } })))
  }, [depGraphSelected, setRfNodes, setRfEdges])

  // ── Knowledge Graph — fetch from Neo4j when tab activates ─────────────
  useEffect(() => {
    if (graphView !== 'knowledge' || neoGraph) return
    const acct = project?.account
    const app  = project?.name
    if (!acct || !app) return

    const API_BASE = getAPIBaseUrl()
    const token = localStorage.getItem('token')
    const headers = token ? { Authorization: `Bearer ${token}` } : {}

    setNeoGraphLoading(true)
    fetch(`${API_BASE}/define-services/graph?account_id=${encodeURIComponent(acct)}&app_id=${encodeURIComponent(app)}`, { headers })
      .then(r => r.ok ? r.json() : Promise.reject(new Error(`Graph fetch failed: ${r.status}`)))
      .then(data => { setNeoGraph(data); setNeoGraphLoading(false) })
      .catch(err => { console.error('[KG] fetch error:', err); setNeoGraphLoading(false) })
  }, [graphView, neoGraph, project])

  // Build KG ReactFlow nodes/edges whenever neoGraph data arrives
  useEffect(() => {
    if (!neoGraph?.nodes?.length) { setKgNodes([]); setKgEdges([]); return }

    // Unique node id = label:name (e.g. "Program:CUSTUPD")
    const nodeId = (n) => `${n.label}:${n.name}`

    const rfNodeList = neoGraph.nodes.map(n => ({
      id: nodeId(n),
      type: 'kgNode',
      position: { x: 0, y: 0 },
      data: {
        label: n.name,
        nodeType: n.label,
        program: n.program || null,
        lineCount: n.line_count || null,
        statementCount: n.statement_count || null,
        paragraphCount: n.paragraph_count || null,
        highlighted: false,
        isSelected: false,
      },
    }))

    const nodeIdSet = new Set(rfNodeList.map(n => n.id))
    const rfEdgeList = neoGraph.edges
      .map((e, i) => {
        const src = `${e.from_label}:${e.from_name}`
        const tgt = `${e.to_label}:${e.to_name}`
        if (!nodeIdSet.has(src) || !nodeIdSet.has(tgt)) return null
        const edgeColor = KG_EDGE_COLORS[e.relationship] || '#8b92a5'
        return {
          id: `kg-${i}-${src}-${tgt}-${e.relationship}`,
          source: src,
          target: tgt,
          type: 'default',
          label: KG_EDGE_LABELS[e.relationship] || e.relationship,
          labelStyle: { fontSize: 9, fill: edgeColor },
          data: { color: edgeColor, relType: e.relationship },
          style: { stroke: edgeColor, strokeWidth: 1.5, opacity: 1 },
          markerEnd: { type: MarkerType.ArrowClosed, width: 8, height: 8, color: edgeColor },
        }
      })
      .filter(Boolean)

    // Dagre layout — top-to-bottom hierarchy (Programs at top, Paragraphs below, etc.)
    const g = new dagre.graphlib.Graph()
    g.setDefaultEdgeLabel(() => ({}))
    g.setGraph({ rankdir: 'TB', ranksep: 80, nodesep: 40, marginx: 40, marginy: 40 })
    rfNodeList.forEach(n => g.setNode(n.id, { width: 130, height: 50 }))
    rfEdgeList.forEach(e => g.setEdge(e.source, e.target))
    dagre.layout(g)

    const layouted = rfNodeList.map((n, i) => {
      const pos = g.node(n.id)
      const safeX = pos && isFinite(pos.x) ? pos.x - 65 : (i % 6) * 160
      const safeY = pos && isFinite(pos.y) ? pos.y - 25 : Math.floor(i / 6) * 80
      return { ...n, position: { x: safeX, y: safeY } }
    })

    setKgNodes(layouted)
    setKgEdges(rfEdgeList)
    setKgSelectedNode(null)
  }, [neoGraph])

  // KG node click — highlight connected nodes
  const onKGNodeClick = useCallback((event, rfNode) => {
    const nodeId = rfNode.id
    const isDeselecting = kgSelectedNode === nodeId

    if (isDeselecting) {
      setKgSelectedNode(null)
      setKgNodes(nds => nds.map(n => ({ ...n, data: { ...n.data, highlighted: false, isSelected: false }, style: { opacity: 1 } })))
      setKgEdges(eds => eds.map(e => ({ ...e, style: { stroke: e.data?.color || '#8b92a5', strokeWidth: 1.5, opacity: 1 }, labelStyle: { fontSize: 9, fill: e.data?.color || '#8b92a5' } })))
      return
    }

    setKgSelectedNode(nodeId)

    const connectedNodeIds = new Set([nodeId])
    const connectedEdgeIds = new Set()
    neoGraph.edges.forEach((e, i) => {
      const src = `${e.from_label}:${e.from_name}`
      const tgt = `${e.to_label}:${e.to_name}`
      if (src === nodeId) { connectedNodeIds.add(tgt); connectedEdgeIds.add(`kg-${i}-${src}-${tgt}-${e.relationship}`) }
      if (tgt === nodeId) { connectedNodeIds.add(src); connectedEdgeIds.add(`kg-${i}-${src}-${tgt}-${e.relationship}`) }
    })

    setKgNodes(nds => nds.map(n => ({
      ...n,
      data: { ...n.data, highlighted: connectedNodeIds.has(n.id), isSelected: n.id === nodeId },
      style: { opacity: connectedNodeIds.has(n.id) ? 1 : 0.06 },
    })))
    setKgEdges(eds => eds.map(e => {
      const isConn = connectedEdgeIds.has(e.id)
      return { ...e, style: { stroke: isConn ? (e.data?.color || '#60a5fa') : 'rgba(139,146,165,0.15)', strokeWidth: isConn ? 2.5 : 1, opacity: isConn ? 1 : 0.2 }, labelStyle: { fontSize: 9, fill: isConn ? (e.data?.color || '#60a5fa') : 'rgba(139,146,165,0.15)' } }
    }))
  }, [kgSelectedNode, neoGraph, setKgNodes, setKgEdges])

  // KG pane click — reset
  const onKGPaneClick = useCallback(() => {
    if (!kgSelectedNode) return
    setKgSelectedNode(null)
    setKgNodes(nds => nds.map(n => ({ ...n, data: { ...n.data, highlighted: false, isSelected: false }, style: { opacity: 1 } })))
    setKgEdges(eds => eds.map(e => ({ ...e, style: { stroke: e.data?.color || '#8b92a5', strokeWidth: 1.5, opacity: 1 }, labelStyle: { fontSize: 9, fill: e.data?.color || '#8b92a5' } })))
  }, [kgSelectedNode, setKgNodes, setKgEdges])

  // KG connected edges/nodes for inspector
  const kgConnected = useMemo(() => {
    if (!kgSelectedNode || !neoGraph?.edges) return { nodes: new Set(), edges: [] }
    const connected = new Set([kgSelectedNode])
    const edges = []
    neoGraph.edges.forEach(e => {
      const src = `${e.from_label}:${e.from_name}`
      const tgt = `${e.to_label}:${e.to_name}`
      if (src === kgSelectedNode) { connected.add(tgt); edges.push(e) }
      if (tgt === kgSelectedNode) { connected.add(src); edges.push(e) }
    })
    return { nodes: connected, edges }
  }, [kgSelectedNode, neoGraph])

  // Handler: create a NEW Target Service from the highlighted selection
  const handleCreateServiceFromGraph = () => {
    const name = graphServiceName.trim()
    if (depGraphSelectedPrograms.length === 0 || !name) return
    const newId = `svc-graph-${Date.now()}`

    const newSvc = {
      id: newId,
      name,
      domain: name,
      classes: depGraphSelectedPrograms.length,
      loc: 0,
      cohesion: 'medium',
      couplingOut: 0,
      businessHint: name,
      status: 'proposed',
      cobolOrigins: [...depGraphSelectedPrograms],
      members: depGraphSelectedPrograms.map(p => ({
        name: p, cobol: p, loc: 0, extDeps: 0,
      })),
      whySignals: {
        dataShared: 'Created from graph selection',
        internalDensity: `${depGraphSelectedPrograms.length} programs selected`,
        circular: '0 circular dependencies detected',
        aiConfidence: 0,
      },
      deps: [],
    }

    setManualServices(prev => [...prev, newSvc])
    setSelectedServiceId(newId)
    setDepGraphSelected(null)
    setGraphServiceName('')
    setGraphAddToServiceId('')
    setActiveTab('workbench')
  }

  // Handler: add selected programs to an EXISTING user-created service
  const handleAddToExistingService = () => {
    if (depGraphSelectedPrograms.length === 0 || !graphAddToServiceId) return
    setManualServices(prev => prev.map(svc => {
      if (svc.id !== graphAddToServiceId) return svc
      const existingOrigins = new Set(svc.cobolOrigins || [])
      const newProgs = depGraphSelectedPrograms.filter(p => !existingOrigins.has(p))
      if (newProgs.length === 0) return svc
      return {
        ...svc,
        cobolOrigins: [...(svc.cobolOrigins || []), ...newProgs],
        members: [
          ...(svc.members || []),
          ...newProgs.map(p => ({ name: p, cobol: p, loc: 0, extDeps: 0 })),
        ],
        classes: (svc.classes || 0) + newProgs.length,
      }
    }))
    setSelectedServiceId(graphAddToServiceId)
    setDepGraphSelected(null)
    setGraphServiceName('')
    setGraphAddToServiceId('')
    setActiveTab('workbench')
  }

  const renderGraph = () => {
    const allSvcs = services

    // ── SERVICE VIEW ──────────────────────────────────────────────────────────
    const renderServiceView = () => {
      if (allSvcs.length === 0) {
        return (
          <div className="dc-graph-empty">
            <Network size={28} />
            <span>No services defined yet.</span>
            <span className="dc-graph-empty-sub">
              Create services in the Workbench tab, then return here to view the dependency map.
            </span>
          </div>
        )
      }

      const NODE_W = 138, NODE_H = 52, GAP_X = 172, GAP_Y = 96, PAD = 22
      const cols = Math.max(1, Math.min(3, Math.ceil(Math.sqrt(allSvcs.length))))
      const graphNodes = allSvcs.map((svc, i) => {
        const col = i % cols, row = Math.floor(i / cols)
        const effStatus = serviceStatuses[svc.id] || svc.status
        const cls = effStatus === 'approved' ? 'green' : effStatus === 'modified' ? 'amber' : 'blue'
        const label = svc.name.replace(/ManagementService/g, 'MgmtSvc').replace(/ProcessingService/g, 'ProcSvc').replace(/Service$/, 'Svc').substring(0, 20)
        return { id: svc.id, label, x: PAD + col * GAP_X, y: PAD + row * (NODE_H + GAP_Y), w: NODE_W, h: NODE_H, cls, status: effStatus }
      })
      const nodeMap = {}
      graphNodes.forEach(n => { nodeMap[n.id] = { cx: n.x + n.w / 2, cy: n.y + n.h / 2 } })
      const nameToId = {}
      allSvcs.forEach(s => { nameToId[s.name] = s.id })
      const graphEdges = [], seen = new Set()
      allSvcs.forEach(svc => {
        ;(svc.deps || []).forEach(dep => {
          const targetId = nameToId[dep] || dep
          if (graphNodes.find(n => n.id === targetId) && targetId !== svc.id) {
            const key = [svc.id, targetId].sort().join('~~')
            if (!seen.has(key)) { seen.add(key); graphEdges.push({ from: svc.id, to: targetId, strength: 'medium' }) }
          }
        })
      })
      const maxX = Math.max(...graphNodes.map(n => n.x + n.w)) + PAD
      const maxY = Math.max(...graphNodes.map(n => n.y + n.h)) + PAD
      const viewW = Math.max(maxX, 400), viewH = Math.max(maxY, 200)
      const strokeMap = {
        strong: { stroke: 'rgba(96,165,250,0.55)', strokeWidth: 2 },
        medium: { stroke: 'rgba(139,146,165,0.45)', strokeWidth: 1.5 },
        weak: { stroke: 'rgba(139,146,165,0.27)', strokeWidth: 1 },
      }

      return (
        <>
          <div className="dc-graph-canvas-wrap">
            <div className="dc-graph-canvas">
              <svg className="dc-graph-svg" viewBox={`0 0 ${viewW} ${viewH}`} xmlns="http://www.w3.org/2000/svg">
                <defs><marker id="dc-arrow" markerWidth="7" markerHeight="5" refX="6" refY="2.5" orient="auto"><polygon points="0 0, 7 2.5, 0 5" fill="rgba(139,146,165,0.45)" /></marker></defs>
                {graphEdges.map((edge, i) => {
                  const from = nodeMap[edge.from], to = nodeMap[edge.to]
                  if (!from || !to) return null
                  const s = strokeMap[edge.strength] || strokeMap.medium
                  return <line key={i} x1={from.cx} y1={from.cy} x2={to.cx} y2={to.cy} stroke={s.stroke} strokeWidth={s.strokeWidth} markerEnd="url(#dc-arrow)" />
                })}
              </svg>
              {graphNodes.map(node => (
                <div key={node.id} className={['dc-graph-node', `dc-gnode-${node.cls}`, hoveredNode === node.id ? 'hovered' : ''].filter(Boolean).join(' ')} style={{ left: node.x, top: node.y, width: node.w, height: node.h }} onMouseEnter={() => setHoveredNode(node.id)} onMouseLeave={() => setHoveredNode(null)} onClick={() => { setSelectedServiceId(node.id); setActiveTab('workbench') }} title="Click to open in Workbench">
                  <span className="dc-gnode-label">{node.label}</span>
                  <span className={`dc-gnode-dot ${node.status}`} />
                </div>
              ))}
            </div>
            <aside className="dc-graph-legend">
              <div className="dc-legend-title">Legend</div>
              <div className="dc-legend-item"><span className="dc-legend-dot blue" />Draft</div>
              <div className="dc-legend-item"><span className="dc-legend-dot green" />Confirmed</div>
              <div className="dc-legend-item"><span className="dc-legend-dot amber" />Modified</div>
              <div className="dc-legend-sep" />
              {graphEdges.length > 0 ? <><div className="dc-legend-item"><span className="dc-legend-edge medium"/>Dependency</div><div className="dc-legend-item"><span className="dc-legend-edge weak"/>Weak dep</div></> : <div className="dc-legend-item muted">No edges detected</div>}
            </aside>
          </div>
          {graphEdges.length === 0 && <div className="dc-graph-note"><Info size={12} /> No inter-service dependencies detected.</div>}
        </>
      )
    }

    // ── DEPENDENCY / RELATIONSHIP VIEW — ReactFlow ────────────────────────────
    const renderDepView = () => {
      if (!rfNodes.length) {
        return (
          <div className="dc-graph-empty">
            <Network size={28} />
            <span>No relationship data available.</span>
            <span className="dc-graph-empty-sub">
              Run Code Analysis to generate asset relationships, then return here to discover connected groups.
            </span>
          </div>
        )
      }

      const hasSelection   = depGraphSelected !== null
      const selectedAsset  = hasSelection ? assetGraph?.assets?.find(a => a.id === depGraphSelected) : null
      const selectedColor  = selectedAsset ? (DEP_TYPE_COLORS[selectedAsset.type] || '#8b92a5') : '#8b92a5'
      const selectedTypeLabel = selectedAsset ? (DEP_TYPE_LABELS[selectedAsset.type] || selectedAsset.type) : ''
      const sliceAssets    = hasSelection ? (assetGraph?.assets || []).filter(a => depGraphConnected.has(a.id)) : []
      const selectedRels   = hasSelection
        ? (assetGraph?.relationships || []).filter(r => depGraphConnected.has(r.from) && depGraphConnected.has(r.to))
        : []
      const edgeTypeLabels = {
        CALLS: 'Calls', COPIES: 'Copies', READS: 'Reads', WRITES: 'Writes',
        FILE_IO: 'File I/O', SQL_SELECT: 'SQL Read', SQL_INSERT: 'SQL Write',
        SQL_UPDATE: 'SQL Update', SQL_DELETE: 'SQL Delete',
        EXECUTES: 'Executes', DD_REFERENCES: 'DD Ref',
      }

      return (
        <div className="dc-depview">
          {/* ReactFlow canvas */}
          <div className="dc-depview-canvas-area">
            <ReactFlow
              nodes={rfNodes}
              edges={rfEdges}
              onNodesChange={onRfNodesChange}
              onEdgesChange={onRfEdgesChange}
              onNodeClick={onRFNodeClick}
              onPaneClick={onRFPaneClick}
              nodeTypes={DEP_NODE_TYPES}
              fitView
              fitViewOptions={{ padding: 0.22 }}
              proOptions={{ hideAttribution: true }}
              minZoom={0.25}
              maxZoom={2.5}
              deleteKeyCode={null}
            >
              <Background color="rgba(139,146,165,0.07)" gap={22} size={1} />
              <Controls showInteractive={false} position="bottom-left" style={{ bottom: 48 }} />
            </ReactFlow>

            {/* Floating hint / slice badge */}
            {!hasSelection ? (
              <div className="dc-depview-hint">
                <Search size={11} />
                Click any node to discover its connected slice
              </div>
            ) : (
              <div className="dc-depview-slice-badge">
                <span className="dc-slice-badge-count">{depGraphConnected.size}</span>
                <span className="dc-slice-badge-label">nodes in slice</span>
                {depGraphSelectedPrograms.length > 0 && (
                  <span className="dc-slice-badge-buildable">
                    · {depGraphSelectedPrograms.length} buildable
                  </span>
                )}
                {(depGraphConnected.size - depGraphSelectedPrograms.length) > 0 && (
                  <span className="dc-slice-badge-support">
                    · {depGraphConnected.size - depGraphSelectedPrograms.length} supporting
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Right-side inspector */}
          <div className={`dc-depview-inspector ${hasSelection ? 'active' : ''}`}>
            {hasSelection && selectedAsset ? (
              <>
                <div className="dc-inspector-header">
                  <span className="dc-inspector-stripe" style={{ background: selectedColor }} />
                  <div>
                    <div className="dc-inspector-name">{selectedAsset.name}</div>
                    <div className="dc-inspector-type" style={{ color: selectedColor }}>{selectedTypeLabel}</div>
                  </div>
                </div>

                <div className="dc-inspector-stats">
                  <div className="dc-inspector-stat">
                    <span className="dc-inspector-stat-val">{depGraphConnected.size}</span>
                    <span className="dc-inspector-stat-label">In Slice</span>
                  </div>
                  <div className="dc-inspector-stat dc-stat-buildable">
                    <span className="dc-inspector-stat-val dc-stat-val-buildable">{depGraphSelectedPrograms.length}</span>
                    <span className="dc-inspector-stat-label">Buildable</span>
                  </div>
                  <div className="dc-inspector-stat">
                    <span className="dc-inspector-stat-val">{selectedRels.length}</span>
                    <span className="dc-inspector-stat-label">Edges</span>
                  </div>
                </div>

                {/* ── Buildable Programs — these go into the service ─────── */}
                <div className="dc-inspector-section dc-inspector-section-buildable">
                  <div className="dc-inspector-section-hdr-row">
                    <div className="dc-inspector-section-title">Buildable Programs</div>
                    {depGraphSelectedPrograms.length > 0 && (
                      <span className="dc-inspector-buildable-badge">{depGraphSelectedPrograms.length}</span>
                    )}
                  </div>
                  {depGraphSelectedPrograms.length > 0 ? (
                    <>
                      <p className="dc-inspector-section-desc">COBOL programs included when you create a service from this slice.</p>
                      <div className="dc-inspector-nodes">
                        {depGraphSelectedPrograms.slice(0, 8).map(prog => (
                          <span key={prog.id || prog.name || prog} className="dc-inspector-node-chip dc-chip-buildable">
                            {prog.name || prog.id || prog}
                          </span>
                        ))}
                        {depGraphSelectedPrograms.length > 8 && (
                          <span className="dc-inspector-node-chip dc-chip-more">+{depGraphSelectedPrograms.length - 8}</span>
                        )}
                      </div>
                    </>
                  ) : (
                    <p className="dc-inspector-section-desc dc-inspector-section-desc-muted">No COBOL programs in this slice — select a COBOL program node to find buildable members.</p>
                  )}
                </div>

                {/* ── Supporting Assets — context, not included in service ── */}
                {sliceAssets.filter(a => a.type !== 'COBOL_PROGRAM').length > 0 && (
                  <div className="dc-inspector-section">
                    <div className="dc-inspector-section-hdr-row">
                      <div className="dc-inspector-section-title">Supporting Assets</div>
                      <span className="dc-inspector-support-badge">{sliceAssets.filter(a => a.type !== 'COBOL_PROGRAM').length}</span>
                    </div>
                    <p className="dc-inspector-section-desc">Connected context — shared/referenced assets, not added to service boundaries.</p>
                    {(() => {
                      const groups = {}
                      sliceAssets.filter(a => a.type !== 'COBOL_PROGRAM').forEach(a => {
                        const tl = DEP_TYPE_LABELS[a.type] || a.type
                        const col = DEP_TYPE_COLORS[a.type] || '#8b92a5'
                        if (!groups[tl]) groups[tl] = { color: col, nodes: [] }
                        groups[tl].nodes.push(a)
                      })
                      return Object.entries(groups).map(([typeLabel, { color, nodes: typeNodes }]) => (
                        <div key={typeLabel} className="dc-inspector-type-group">
                          <div className="dc-inspector-type-group-hdr">
                            <span className="dc-chip-dot" style={{ background: color }} />
                            <span className="dc-inspector-type-group-lbl" style={{ color }}>{typeLabel}</span>
                            <span className="dc-inspector-type-group-ct">{typeNodes.length}</span>
                          </div>
                          <div className="dc-inspector-nodes">
                            {typeNodes.slice(0, 5).map(a => (
                              <span key={a.id} className="dc-inspector-node-chip" style={{ borderColor: `${color}44`, opacity: 0.8 }}>
                                {a.name}
                              </span>
                            ))}
                            {typeNodes.length > 5 && (
                              <span className="dc-inspector-node-chip dc-chip-more">+{typeNodes.length - 5}</span>
                            )}
                          </div>
                        </div>
                      ))
                    })()}
                  </div>
                )}

                {selectedRels.length > 0 && (
                  <div className="dc-inspector-section">
                    <div className="dc-inspector-section-title">Relationships</div>
                    <div className="dc-inspector-edges">
                      {selectedRels.slice(0, 9).map((r, i) => {
                        const fromName = assetGraph.assets.find(a => a.id === r.from)?.name || '?'
                        const toName   = assetGraph.assets.find(a => a.id === r.to)?.name   || '?'
                        const edgeColor = DEP_EDGE_COLORS[r.type] || '#8b92a5'
                        return (
                          <div key={i} className="dc-inspector-edge-row">
                            <span className="dc-inspector-edge-from">{fromName}</span>
                            <span className="dc-inspector-edge-arrow" style={{ color: edgeColor }}>→</span>
                            <span className="dc-inspector-edge-to">{toName}</span>
                            <span className="dc-inspector-edge-type" style={{ color: edgeColor }}>{edgeTypeLabels[r.type] || r.type}</span>
                          </div>
                        )
                      })}
                      {selectedRels.length > 9 && <div className="dc-inspector-edge-more">+{selectedRels.length - 9} more</div>}
                    </div>
                  </div>
                )}

                <div className="dc-inspector-actions">
                  <div className="dc-inspector-create-block">
                    <input
                      className="dc-inspector-name-input"
                      type="text"
                      placeholder="Service name…"
                      value={graphServiceName}
                      onChange={e => setGraphServiceName(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && handleCreateServiceFromGraph()}
                    />
                    <button
                      className="dc-inspector-cta"
                      onClick={handleCreateServiceFromGraph}
                      disabled={depGraphSelectedPrograms.length === 0 || !graphServiceName.trim()}
                    >
                      <Plus size={14} />
                      Create Service
                      <span className="dc-inspector-cta-count">{depGraphSelectedPrograms.length}</span>
                    </button>
                  </div>

                  {manualServices.length > 0 && (
                    <>
                      <div className="dc-inspector-divider"><span>or add to existing</span></div>
                      <div className="dc-inspector-add-block">
                        <select
                          className="dc-inspector-svc-select"
                          value={graphAddToServiceId}
                          onChange={e => setGraphAddToServiceId(e.target.value)}
                        >
                          <option value="">Select service…</option>
                          {manualServices.map(s => (
                            <option key={s.id} value={s.id}>{s.name}</option>
                          ))}
                        </select>
                        <button
                          className="dc-inspector-add-btn"
                          onClick={handleAddToExistingService}
                          disabled={!graphAddToServiceId || depGraphSelectedPrograms.length === 0}
                        >
                          Add
                        </button>
                      </div>
                    </>
                  )}

                  <button
                    className="dc-inspector-clear"
                    onClick={() => {
                      setDepGraphSelected(null); setGraphServiceName(''); setGraphAddToServiceId('')
                      setRfNodes(nds => nds.map(n => ({ ...n, data: { ...n.data, highlighted: false, isSelected: false }, style: { opacity: 1 } })))
                      setRfEdges(eds => eds.map(e => ({ ...e, style: { stroke: e.data?.color || '#8b92a5', strokeWidth: 1.5, opacity: 1 } })))
                    }}
                  >
                    Clear Selection
                  </button>
                </div>
              </>
            ) : (
              <div className="dc-inspector-empty">
                <Network size={24} />
                <span>Select a node to define a slice</span>
                <span className="dc-inspector-empty-sub">Click any node. Connected assets highlight as your slice. Name it and create a service from the selection.</span>
              </div>
            )}
          </div>
        </div>
      )
    }

    // ── KNOWLEDGE GRAPH VIEW — Neo4j graph data ──────────────────────────────
    const renderKnowledgeGraph = () => {
      if (neoGraphLoading) {
        return (
          <div className="dc-graph-empty">
            <Loader2 size={28} className="dc-spin" />
            <span>Loading knowledge graph from Neo4j...</span>
          </div>
        )
      }

      if (!kgNodes.length) {
        return (
          <div className="dc-graph-empty">
            <Database size={28} />
            <span>No graph data available.</span>
            <span className="dc-graph-empty-sub">
              Run Code Analysis to populate the Neo4j knowledge graph, then return here to explore relationships.
            </span>
          </div>
        )
      }

      const hasSelection = kgSelectedNode !== null
      const selectedNodeData = hasSelection ? neoGraph?.nodes?.find(n => `${n.label}:${n.name}` === kgSelectedNode) : null
      const selectedColor = selectedNodeData ? (KG_TYPE_COLORS[selectedNodeData.label] || '#8b92a5') : '#8b92a5'

      return (
        <div className="dc-depview">
          <div className="dc-depview-canvas-area">
            <ReactFlow
              nodes={kgNodes}
              edges={kgEdges}
              onNodesChange={onKgNodesChange}
              onEdgesChange={onKgEdgesChange}
              onNodeClick={onKGNodeClick}
              onPaneClick={onKGPaneClick}
              nodeTypes={KG_NODE_TYPES}
              fitView
              fitViewOptions={{ padding: 0.22 }}
              proOptions={{ hideAttribution: true }}
              minZoom={0.15}
              maxZoom={2.5}
              deleteKeyCode={null}
            >
              <Background color="rgba(139,146,165,0.07)" gap={22} size={1} />
              <Controls showInteractive={false} position="bottom-left" style={{ bottom: 48 }} />
            </ReactFlow>

            {!hasSelection ? (
              <div className="dc-depview-hint">
                <Search size={11} />
                Click any node to inspect its relationships
              </div>
            ) : (
              <div className="dc-depview-slice-badge">
                <span className="dc-slice-badge-count">{kgConnected.nodes.size}</span>
                <span className="dc-slice-badge-label">connected nodes</span>
                <span className="dc-slice-badge-support">
                  · {kgConnected.edges.length} edges
                </span>
              </div>
            )}
          </div>

          {/* Right-side inspector */}
          <div className={`dc-depview-inspector ${hasSelection ? 'active' : ''}`}>
            {hasSelection && selectedNodeData ? (
              <>
                <div className="dc-inspector-header">
                  <span className="dc-inspector-stripe" style={{ background: selectedColor }} />
                  <div>
                    <div className="dc-inspector-name">{selectedNodeData.name}</div>
                    <div className="dc-inspector-type" style={{ color: selectedColor }}>{KG_TYPE_LABELS[selectedNodeData.label] || selectedNodeData.label}</div>
                  </div>
                </div>

                <div className="dc-inspector-stats">
                  <div className="dc-inspector-stat">
                    <span className="dc-inspector-stat-val">{kgConnected.nodes.size}</span>
                    <span className="dc-inspector-stat-label">Connected</span>
                  </div>
                  <div className="dc-inspector-stat">
                    <span className="dc-inspector-stat-val">{kgConnected.edges.length}</span>
                    <span className="dc-inspector-stat-label">Edges</span>
                  </div>
                  {selectedNodeData.line_count && (
                    <div className="dc-inspector-stat">
                      <span className="dc-inspector-stat-val">{selectedNodeData.line_count}</span>
                      <span className="dc-inspector-stat-label">Lines</span>
                    </div>
                  )}
                  {selectedNodeData.statement_count && (
                    <div className="dc-inspector-stat">
                      <span className="dc-inspector-stat-val">{selectedNodeData.statement_count}</span>
                      <span className="dc-inspector-stat-label">Stmts</span>
                    </div>
                  )}
                </div>

                {selectedNodeData.program && (
                  <div className="dc-inspector-section">
                    <div className="dc-inspector-section-title">Parent Program</div>
                    <span className="dc-inspector-node-chip" style={{ borderColor: `${KG_TYPE_COLORS.Program}44` }}>
                      {selectedNodeData.program}
                    </span>
                  </div>
                )}

                {/* Connected nodes grouped by type */}
                {(() => {
                  const groups = {}
                  neoGraph.nodes.filter(n => kgConnected.nodes.has(`${n.label}:${n.name}`) && `${n.label}:${n.name}` !== kgSelectedNode).forEach(n => {
                    const tl = KG_TYPE_LABELS[n.label] || n.label
                    const col = KG_TYPE_COLORS[n.label] || '#8b92a5'
                    if (!groups[tl]) groups[tl] = { color: col, nodes: [] }
                    groups[tl].nodes.push(n)
                  })
                  return Object.keys(groups).length > 0 && (
                    <div className="dc-inspector-section">
                      <div className="dc-inspector-section-title">Connected Nodes</div>
                      {Object.entries(groups).map(([typeLabel, { color, nodes: typeNodes }]) => (
                        <div key={typeLabel} className="dc-inspector-type-group">
                          <div className="dc-inspector-type-group-hdr">
                            <span className="dc-chip-dot" style={{ background: color }} />
                            <span className="dc-inspector-type-group-lbl" style={{ color }}>{typeLabel}</span>
                            <span className="dc-inspector-type-group-ct">{typeNodes.length}</span>
                          </div>
                          <div className="dc-inspector-nodes">
                            {typeNodes.slice(0, 8).map(n => (
                              <span key={`${n.label}:${n.name}`} className="dc-inspector-node-chip" style={{ borderColor: `${color}44` }}>
                                {n.name}
                              </span>
                            ))}
                            {typeNodes.length > 8 && (
                              <span className="dc-inspector-node-chip dc-chip-more">+{typeNodes.length - 8}</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )
                })()}

                {/* Relationships */}
                {kgConnected.edges.length > 0 && (
                  <div className="dc-inspector-section">
                    <div className="dc-inspector-section-title">Relationships</div>
                    <div className="dc-inspector-edges">
                      {kgConnected.edges.slice(0, 12).map((e, i) => {
                        const edgeColor = KG_EDGE_COLORS[e.relationship] || '#8b92a5'
                        return (
                          <div key={i} className="dc-inspector-edge-row">
                            <span className="dc-inspector-edge-from">{e.from_name}</span>
                            <span className="dc-inspector-edge-arrow" style={{ color: edgeColor }}>→</span>
                            <span className="dc-inspector-edge-to">{e.to_name}</span>
                            <span className="dc-inspector-edge-type" style={{ color: edgeColor }}>{KG_EDGE_LABELS[e.relationship] || e.relationship}</span>
                          </div>
                        )
                      })}
                      {kgConnected.edges.length > 12 && <div className="dc-inspector-edge-more">+{kgConnected.edges.length - 12} more</div>}
                    </div>
                  </div>
                )}

                <button
                  className="dc-inspector-clear"
                  onClick={() => {
                    setKgSelectedNode(null)
                    setKgNodes(nds => nds.map(n => ({ ...n, data: { ...n.data, highlighted: false, isSelected: false }, style: { opacity: 1 } })))
                    setKgEdges(eds => eds.map(e => ({ ...e, style: { stroke: e.data?.color || '#8b92a5', strokeWidth: 1.5, opacity: 1 }, labelStyle: { fontSize: 9, fill: e.data?.color || '#8b92a5' } })))
                  }}
                >
                  Clear Selection
                </button>
              </>
            ) : (
              <div className="dc-inspector-empty">
                <Database size={24} />
                <span>Explore the knowledge graph</span>
                <span className="dc-inspector-empty-sub">Click any node to inspect its type, connections, and metadata from Neo4j.</span>
              </div>
            )}
          </div>
        </div>
      )
    }

    return (
      <div className="dc-graph-view">
        <div className="dc-graph-toolbar">
          <div className="dc-graph-toolbar-left">
            <button className={`dc-graph-tool-btn ${graphView === 'services' ? 'active' : ''}`} onClick={() => setGraphView('services')}>Services View</button>
            <button className={`dc-graph-tool-btn ${graphView === 'dependencies' ? 'active' : ''}`} onClick={() => setGraphView('dependencies')}>Dependency View</button>
            <button className={`dc-graph-tool-btn ${graphView === 'knowledge' ? 'active' : ''}`} onClick={() => setGraphView('knowledge')}>
              <Database size={12} /> Knowledge Graph
            </button>
          </div>
          <div className="dc-graph-toolbar-right">
            {isApproved && !isStaleFinalised && (
              <span className="dc-graph-finalized-chip">
                <CheckCircle2 size={11} />
                Plan locked
              </span>
            )}
            {graphView === 'dependencies' && depGraphSelected && (
              <button className="dc-graph-tool-btn" onClick={() => {
                setDepGraphSelected(null); setGraphServiceName(''); setGraphAddToServiceId('')
                setRfNodes(nds => nds.map(n => ({ ...n, data: { ...n.data, highlighted: false, isSelected: false }, style: { opacity: 1 } })))
                setRfEdges(eds => eds.map(e => ({ ...e, style: { stroke: e.data?.color || '#8b92a5', strokeWidth: 1.5, opacity: 1 } })))
              }}>
                <X size={12} /> Clear Selection
              </button>
            )}
            {graphView === 'knowledge' && kgSelectedNode && (
              <button className="dc-graph-tool-btn" onClick={() => {
                setKgSelectedNode(null)
                setKgNodes(nds => nds.map(n => ({ ...n, data: { ...n.data, highlighted: false, isSelected: false }, style: { opacity: 1 } })))
                setKgEdges(eds => eds.map(e => ({ ...e, style: { stroke: e.data?.color || '#8b92a5', strokeWidth: 1.5, opacity: 1 }, labelStyle: { fontSize: 9, fill: e.data?.color || '#8b92a5' } })))
              }}>
                <X size={12} /> Clear Selection
              </button>
            )}
            <button className="dc-graph-tool-btn" onClick={() => {
              if (graphView === 'knowledge') {
                setKgSelectedNode(null)
                setKgNodes(nds => nds.map(n => ({ ...n, data: { ...n.data, highlighted: false, isSelected: false }, style: { opacity: 1 } })))
                setKgEdges(eds => eds.map(e => ({ ...e, style: { stroke: e.data?.color || '#8b92a5', strokeWidth: 1.5, opacity: 1 }, labelStyle: { fontSize: 9, fill: e.data?.color || '#8b92a5' } })))
              } else {
                setDepGraphSelected(null); setGraphServiceName(''); setGraphAddToServiceId('')
                setRfNodes(nds => nds.map(n => ({ ...n, data: { ...n.data, highlighted: false, isSelected: false }, style: { opacity: 1 } })))
                setRfEdges(eds => eds.map(e => ({ ...e, style: { stroke: e.data?.color || '#8b92a5', strokeWidth: 1.5, opacity: 1 } })))
              }
            }}>
              <RefreshCw size={12} /> Reset
            </button>
          </div>
        </div>
        {graphView === 'services' ? renderServiceView() : graphView === 'knowledge' ? renderKnowledgeGraph() : renderDepView()}
      </div>
    )
  }

  // ── AUTOMATION FLOW VIEW — commented out, does nothing yet ─────────────────
  //
  // const renderAutomationFlow = () => ( ... )
  // Full function preserved in git history. Re-enable when Automation Flow has real functionality.

  // ── RENDER ─────────────────────────────────────────────────────────────────

  // ── State: loading ────────────────────────────────────────────────────────
  if (loadPhase === 'loading') {
    return (
      <div className="dc-view">
        {renderHeader()}
        <div className="dc-center-state">
          <Loader2 size={22} className="dc-center-spinner" />
          <span>Loading decomposition data…</span>
        </div>
      </div>
    )
  }

  // ── State: proposals-loading — MI/DM/AN jobs are queued or running ──────────
  if (loadPhase === 'proposals-loading') {
    // Illustrative source chips — representative of the typical COBOL→Java pipeline.
    // Not real project data — the loading screen shows before data is available.
    const _splashSources = [
      { label: 'CUSTMGMT.cbl',         type: 'cobol', delay: 0    },
      { label: 'BILLING.cbl',           type: 'cobol', delay: 0.08 },
      { label: 'COMMS.cpy',             type: 'copy',  delay: 0.16 },
      { label: 'CustomerService.java',  type: 'java',  delay: 0.24 },
      { label: 'REPORT.cbl',            type: 'cobol', delay: 0.32 },
    ]
    return (
      <div className="dc-view">
        {renderHeader()}
        <div className="dc-splash-wrap">
          <div className="dc-splash-scene" role="status" aria-label="Preparing service suggestions">

            {/* ── Left: source assets ─────────────────────────────── */}
            <div className="dc-splash-col">
              <span className="dc-splash-col-label">Source Assets</span>
              <div className="dc-splash-chips">
                {_splashSources.map(({ label, type, delay }) => (
                  <div
                    key={label}
                    className={`dc-splash-chip dc-splash-chip--${type}`}
                    style={{ animationDelay: `${delay}s` }}
                  >
                    {label}
                  </div>
                ))}
              </div>
            </div>

            {/* ── Connector → ─────────────────────────────────────── */}
            <div className="dc-splash-connector" aria-hidden="true">
              <div className="dc-splash-connector-track">
                <div className="dc-splash-connector-bead" />
              </div>
            </div>

            {/* ── Centre: analysis hub ────────────────────────────── */}
            <div className="dc-splash-col dc-splash-col--hub">
              <div className="dc-splash-orb">
                <div className="dc-splash-orb-ring" />
                <div className="dc-splash-orb-core">
                  {/* Network node icon — source → hub → services */}
                  <svg width="38" height="38" viewBox="0 0 26 26" fill="none" aria-hidden="true">
                    <circle cx="13" cy="13" r="3" fill="currentColor" />
                    <circle cx="5"  cy="5"  r="2" fill="currentColor" opacity="0.6" />
                    <circle cx="21" cy="5"  r="2" fill="currentColor" opacity="0.6" />
                    <circle cx="5"  cy="21" r="2" fill="currentColor" opacity="0.6" />
                    <circle cx="21" cy="21" r="2" fill="currentColor" opacity="0.6" />
                    <line x1="13" y1="13" x2="5"  y2="5"  stroke="currentColor" strokeWidth="1.2" opacity="0.45" />
                    <line x1="13" y1="13" x2="21" y2="5"  stroke="currentColor" strokeWidth="1.2" opacity="0.45" />
                    <line x1="13" y1="13" x2="5"  y2="21" stroke="currentColor" strokeWidth="1.2" opacity="0.45" />
                    <line x1="13" y1="13" x2="21" y2="21" stroke="currentColor" strokeWidth="1.2" opacity="0.45" />
                  </svg>
                </div>
              </div>
              <span className="dc-splash-col-label dc-splash-col-label--active">Analysing</span>
            </div>

            {/* ── Connector → ─────────────────────────────────────── */}
            <div className="dc-splash-connector" aria-hidden="true">
              <div className="dc-splash-connector-track">
                <div className="dc-splash-connector-bead dc-splash-connector-bead--b" />
              </div>
            </div>

            {/* ── Right: service suggestion cards ─────────────────── */}
            <div className="dc-splash-col">
              <span className="dc-splash-col-label">Suggested Services</span>
              <div className="dc-splash-svc-cards">
                {[0.1, 0.28, 0.46].map((delay, i) => (
                  <div key={i} className="dc-splash-svc-card" style={{ animationDelay: `${delay}s` }}>
                    <div className="dc-splash-svc-card-shimmer" />
                  </div>
                ))}
              </div>
            </div>
          </div>

          <p className="dc-splash-status">This page updates automatically.</p>
        </div>
      </div>
    )
  }

  // ── State: no-ca — nothing has run for this project ───────────────────────
  if (isNoCa) {
    return (
      <div className="dc-view">
        {renderHeader()}
        <div className="dc-center-state">
          <AlertCircle size={22} className="dc-center-warn" />
          <strong>Code Analysis hasn't run yet for {projectName}</strong>
          <p>
            Define Services needs Java generation to be complete before service boundaries
            can be proposed. Run <strong>Ingest → Code Analysis</strong> first.
          </p>
          <button
            className="dc-action-btn secondary"
            style={{ marginTop: 8 }}
            onClick={() => onGoToAnalyze?.()}
          >
            Go to Analyze →
          </button>
        </div>
      </div>
    )
  }

  // ── State: error ──────────────────────────────────────────────────────────
  if (loadPhase === 'error') {
    return (
      <div className="dc-view">
        {renderHeader()}
        <div className="dc-center-state">
          <AlertTriangle size={22} className="dc-center-warn" />
          <strong>Failed to load decomposition data</strong>
          <p className="dc-center-error-detail">{loadError}</p>
          <p>Check API connectivity and try again.</p>
        </div>
      </div>
    )
  }

  // ── States: no-mi | loaded — stage opens in both cases ────────────────────
  // no-mi: CA has run but MI hasn't yet — workbench shows guidance, no proposals
  // loaded: MI has run — workbench shows real proposals
  return (
    <div className="dc-view">
      {renderHeader()}
      {renderStageHeader()}
      {isNoMi && (
        <div className="dc-info-banner">
          <Info size={11} />
          <span>
            {dsProposals
              ? <>Graph-powered proposals loaded — {dsProposals.length} service {dsProposals.length === 1 ? 'boundary' : 'boundaries'} identified. Review and confirm below.</>
              : dsGenerating
                ? <>Analyzing application structure — clustering programs by shared data fields...</>
                : upstreamJobs['code_analysis']
                  ? <>Graph data available — generating service boundary proposals...</>
                  : <>Create services manually below. Run <strong>Code Analysis</strong> first for graph-powered proposals.</>
            }
          </span>
          {upstreamJobs['code_analysis'] && dsProposals && (
            <button
              className="dc-banner-action-btn"
              onClick={handleGenerateGraphProposals}
              disabled={dsGenerating}
            >
              {dsGenerating
                ? <><Loader2 size={12} className="dc-spin" /> Generating…</>
                : <><Sparkles size={12} /> Re-generate</>
              }
            </button>
          )}
        </div>
      )}
      {isReal && !isApproved && (
        <div className="dc-real-banner">
          <CheckCircle2 size={12} />
          {dsProposals
            ? <>Graph-powered service proposals loaded ({dsProposals.length} clusters). Review, confirm, then lock the plan.</>
            : <>AI service proposals loaded from Monolith Identifier{dmJobId ? ' + Dependency Mapper' : ''}. Review the suggested groups, confirm the ones you want, then lock the plan.</>
          }
        </div>
      )}
      {isApproved && !isStaleFinalised && (
        <div className="dc-approved-banner">
          <CheckCircle2 size={12} />
          <span>
            Plan locked — {readiness.confirmedCount} confirmed service{readiness.confirmedCount !== 1 ? 's' : ''}. Continue to Build Services from the menu when ready.
          </span>
          <button className="dc-banner-link" onClick={() => setActiveTab('report')}>
            Review Plan →
          </button>
        </div>
      )}
      {isStaleFinalised && (
        <div className="dc-stale-banner">
          <AlertTriangle size={12} />
          {readiness.isReadyToFinalize
            ? <>Services changed since last lock — re-confirm any edited services, then click&nbsp;<strong>Re-lock Plan</strong> to update.</>
            : <>Services changed and re-locking is now blocked: {readiness.blockingReasons[0]}. Resolve the issue, then re-lock.</>
          }
        </div>
      )}
      <div className="dc-content">
        {activeTab === 'workbench'  && renderWorkbench()}
        {activeTab === 'graph'      && renderGraph()}
        {activeTab === 'report'     && renderReport()}
        {/* {activeTab === 'automation' && renderAutomationFlow()} */}{/* commented out — Automation Flow tab disabled */}
      </div>

      {/* ── Custom context menu ────────────────────────────────────────────── */}
      {contextMenu && (
        <div
          className="dc-ctx-menu"
          style={{ top: contextMenu.y, left: contextMenu.x }}
          onClick={e => e.stopPropagation()}
        >
          <button className="dc-ctx-menu-item" onClick={handleContextMenuView}>
            <Eye size={12} />
            View file
          </button>
          {contextMenu.item?.type !== 'Data' && contextMenu.item?.type !== 'copybook' && (() => {
            const ctxItem = contextMenu.item
            const ctxKey  = `${ctxItem.type}:${ctxItem.name}`
            const ctxFile = {
              name:  ctxItem.displayName || ctxItem.name,
              path:  ctxItem.path || findItemPath(ctxItem.name, ctxItem.type),
              group: ctxItem.type,
            }
            const isSelected = treeSelection.has(ctxKey)
            const multiselLabel = !treeSelectionMode
              ? 'Multi-select'
              : isSelected
                ? 'Remove from multi-select'
                : 'Add to multi-select'
            const handleMultisel = () => {
              setContextMenu(null)
              if (!treeSelectionMode) {
                setTreeSelection(new Set([ctxKey]))
                setTreeSelectionMode(true)
              } else if (isSelected) {
                setTreeSelection(prev => { const n = new Set(prev); n.delete(ctxKey); return n })
              } else {
                setTreeSelection(prev => { const n = new Set(prev); n.add(ctxKey); return n })
              }
            }
            // Bulk selection files for when the context menu also fires bulk AI directly
            const bulkFiles = treeSelectionMode && treeSelection.size > 0 ? selectionToFiles() : null
            return (
              <>
                <div className="dc-ctx-menu-divider" />
                {/* Single-file AI — always scoped to the clicked file only */}
                <div className="dc-ctx-menu-section-hdr">
                  AI — {ctxItem.displayName || ctxItem.name}
                </div>
                <button className="dc-ctx-menu-item" onClick={() => { setContextMenu(null); setDcAiModal({ action: 'custom-prompt',     file: ctxFile }) }}>
                  <Code2     size={12} />Custom Prompt
                </button>
                <button className="dc-ctx-menu-item" onClick={() => { setContextMenu(null); setDcAiModal({ action: 'explain',           file: ctxFile }) }}>
                  <BookOpen  size={12} />Explain This Code
                </button>
                <button className="dc-ctx-menu-item" onClick={() => { setContextMenu(null); setDcAiModal({ action: 'business-use-case', file: ctxFile }) }}>
                  <BarChart2 size={12} />Business Use Case
                </button>
                <button className="dc-ctx-menu-item" onClick={() => { setContextMenu(null); setDcAiModal({ action: 'java-quick-view',   file: ctxFile }) }}>
                  <Sparkles  size={12} />Generate Java Quick View
                </button>
                {/* Selected-set AI — only shown when a multi-select is active */}
                {bulkFiles && (
                  <>
                    <div className="dc-ctx-menu-divider" />
                    <div className="dc-ctx-menu-section-hdr">
                      AI — Selected ({treeSelection.size})
                    </div>
                    <button className="dc-ctx-menu-item" onClick={() => { setContextMenu(null); setDcBulkAiModal({ action: 'custom-prompt',     files: bulkFiles }) }}>
                      <Code2     size={12} />Custom Prompt
                    </button>
                    <button className="dc-ctx-menu-item" onClick={() => { setContextMenu(null); setDcBulkAiModal({ action: 'explain',           files: bulkFiles }) }}>
                      <BookOpen  size={12} />Explain This Group
                    </button>
                    <button className="dc-ctx-menu-item" onClick={() => { setContextMenu(null); setDcBulkAiModal({ action: 'business-use-case', files: bulkFiles }) }}>
                      <BarChart2 size={12} />Business Use Case
                    </button>
                    <button className="dc-ctx-menu-item" onClick={() => { setContextMenu(null); setDcBulkAiModal({ action: 'java-quick-view',   files: bulkFiles }) }}>
                      <Sparkles  size={12} />Generate Java Quick View
                    </button>
                  </>
                )}
                <div className="dc-ctx-menu-divider" />
                <button className="dc-ctx-menu-item" onClick={handleMultisel}>
                  {multiselLabel}
                </button>
                {treeSelectionMode && treeSelection.size > 0 && (
                  <button className="dc-ctx-menu-item" onClick={() => {
                    const finalSet = new Set(treeSelection)
                    finalSet.add(ctxKey)
                    setContextMenu(null)
                    const selectedItems = [...finalSet].map(k => {
                      const ci = k.indexOf(':')
                      return { type: k.slice(0, ci), name: k.slice(ci + 1) }
                    })
                    const members = selectedItems.map(m => {
                      const pd = (sourceInventory?.programs || []).find(p => p.name === m.name)
                      return { name: m.name, cobol: m.type === 'COBOL' ? m.name : '', loc: pd?.loc || 0, extDeps: 0 }
                    })
                    const cobolOrigins = selectedItems.filter(i => i.type === 'COBOL').map(i => i.name)
                    const id          = `svc-sel-${Date.now()}`
                    const defaultName = `New Service ${manualServices.length + 1}`
                    const newSvc = {
                      id, name: defaultName,
                      domain: 'Unclassified', classes: members.length,
                      loc: members.reduce((sum, m) => sum + (m.loc || 0), 0),
                      cohesion: 'medium', couplingOut: 0, businessHint: '',
                      status: 'proposed', cobolOrigins, members,
                      whySignals: { dataShared: 'Manual grouping', internalDensity: 'User-defined', circular: 'N/A', aiConfidence: null },
                      deps: [], isManual: true,
                    }
                    setManualServices(prev => [...prev, newSvc])
                    setSelectedServiceId(id)
                    setTreeSelection(new Set())
                    setTreeSelectionMode(false)
                    setRenamingSvcId(id)
                    setRenameValue(defaultName)
                    if (isApproved) setIsStaleFinalised(true)
                  }}>
                    <Layers size={12} />
                    Create Service from Selection
                  </button>
                )}
              </>
            )
          })()}
        </div>
      )}

      {/* ── Single-file AI action modal ────────────────────────────────────── */}
      {dcAiModal && (
        <InventoryAIModal
          action={dcAiModal.action}
          file={dcAiModal.file}
          catalogIngestJobId={upstreamJobs['ingest']}
          project={project}
          onClose={() => setDcAiModal(null)}
        />
      )}

      {/* ── Multi-select AI action dropdown ────────────────────────────────── */}
      {dcBulkAiMenu && (
        <div className="dc-bulk-ai-backdrop" onClick={() => setDcBulkAiMenu(null)}>
          <div className="dc-bulk-ai-menu" style={{ top: dcBulkAiMenu.top, left: dcBulkAiMenu.left }}
            onClick={e => e.stopPropagation()}>
            <div className="dc-bulk-ai-hdr">
              {treeSelection.size} asset{treeSelection.size !== 1 ? 's' : ''} selected
            </div>
            {[
              { id: 'custom-prompt',     label: 'Custom Prompt',           Icon: Code2     },
              { id: 'explain',           label: 'Explain This Group',      Icon: BookOpen  },
              { id: 'business-use-case', label: 'Business Use Case',       Icon: BarChart2 },
              { id: 'java-quick-view',   label: 'Generate Java Quick View',Icon: Sparkles  },
            ].map(({ id, label, Icon }) => (
              <button key={id} className="dc-bulk-ai-item" onClick={() => {
                setDcBulkAiMenu(null)
                setDcBulkAiModal({ action: id, files: selectionToFiles() })
              }}>
                <Icon size={13} /><span>{label}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ── Multi-select AI action modal ───────────────────────────────────── */}
      {dcBulkAiModal && (
        <BulkInventoryAIModal
          action={dcBulkAiModal.action}
          files={dcBulkAiModal.files}
          catalogIngestJobId={upstreamJobs['ingest']}
          project={project}
          onClose={() => setDcBulkAiModal(null)}
        />
      )}

      {/* ── Cmd+K Command Palette — Step 10 ───────────────────────────────── */}
      <DecomposeCommandPalette
        isOpen={paletteOpen}
        onClose={() => setPaletteOpen(false)}
        commands={paletteCommands}
        assetNames={allCobolPrograms}
        onFindProgram={(name) => {
          setBrowserSearch(name)
          if (decompositionViewMode !== 'services') handleDecompositionViewModeChange('services')
        }}
      />

      {/* ── File viewer modal ──────────────────────────────────────────────── */}
      {fileViewer && (
        <div className="dc-fv-overlay" onClick={() => setFileViewer(null)}>
          <div className="dc-fv-modal" onClick={e => e.stopPropagation()}>
            <div className="dc-fv-header">
              <div className="dc-fv-title">
                <FileText size={14} />
                <span>{fileViewer.name}</span>
                {fileViewer.type && (
                  <span className={`dc-tree-type-tag ${fileViewer.type === 'COBOL' ? 'cobol' : fileViewer.type === 'copybook' ? 'copybook' : 'jcl'}`}>
                    {fileViewer.type === 'COBOL' ? 'COBOL' : fileViewer.type === 'copybook' ? 'CPY' : 'JCL'}
                  </span>
                )}
              </div>
              <button className="dc-fv-close" onClick={() => setFileViewer(null)} title="Close (Esc)">✕</button>
            </div>
            <div className="dc-fv-body">
              {fileViewer.loading && (
                <div className="dc-fv-state">Loading…</div>
              )}
              {!fileViewer.loading && fileViewer.error && (
                <div className="dc-fv-state error">{fileViewer.error}</div>
              )}
              {!fileViewer.loading && !fileViewer.error && fileViewer.content !== null && (
                <pre className="dc-fv-pre">{fileViewer.content}</pre>
              )}
            </div>
            <div className="dc-fv-footer">
              {fileViewer.path && <span className="dc-fv-path">{fileViewer.path}</span>}
              <button className="dc-fv-footer-btn" onClick={() => setFileViewer(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default DecomposeCodeView
